import type {
    ApiError,
    PossibleNoShowListDto,
    PossibleNoDepartureListDto,
    NoWmsListDto,
    AppointmentStatusCode,
    CalendarDayDto,
    CalendarRangeDto,
    LookupsDto,
    AppointmentDto,
    AppointmentEventDto,
    DockBoardDto,
    BoardConfigDto,
    CheckInConfigDto,
    CheckInDto,
    VoidedCheckInDto,
    DetentionConfigDto,
    SuggestLoadDto,
    LicenceScanResultDto,
    CheckInIdentityDto,
    ReleaseCheckDto,
    ReleaseOverrideDto,
    DocumentConfigDto, SignPreviewDto, SignResultDto,
    DocumentDto,
    TemplateCheckDto,
    DoorTextDto,
    DoorTextSendResultDto,
    CheckInTextSendResultDto,
    DoorTextConfigDto,
    DoorTextConfigResponseDto,
    ReminderScheduleResponseDto,
    ReminderPreviewDto,
} from './types';

/**
 * API base URL. In dev, Vite proxies /api/* to the production Function App
 * (see vite.config.ts), so this stays as '/api' for both dev and prod.
 * Override via VITE_API_BASE if pointing at a different backend.
 */
const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

export class ApiCallError extends Error {
    constructor(public details: ApiError) {
        super(details.error || `Request failed with status ${details.status}`);
        this.name = 'ApiCallError';
    }
}

/**
 * Inner request helper. All other API functions go through this.
 */
async function request<T>(
    path: string,
    token: string,
    init: RequestInit = {},
): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    // ⚠️ NEVER SET Content-Type FOR FormData. `fetch` sets
    // `multipart/form-data; boundary=…` itself, and the boundary is the only way
    // the server can find where each part starts. Setting the header by hand
    // omits it, and the request arrives as an unparseable body — a 400 that
    // looks like the file was rejected rather than never read.
    if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }

    const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

    // Try to parse JSON body even on error — server returns structured errors
    let body: unknown = null;
    const contentType = res.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
        body = await res.json().catch(() => null);
    } else if (!res.ok) {
        body = { error: await res.text().catch(() => res.statusText) };
    }

    if (!res.ok) {
        const errBody = (body && typeof body === 'object') ? (body as Record<string, unknown>) : {};
        throw new ApiCallError({
            status: res.status,
            error: typeof errBody.error === 'string' ? errBody.error : `HTTP ${res.status}`,
            issues: errBody.issues,
            conflicts: errBody.conflicts as ApiError['conflicts'],
            // Carried through so a caller can tell two 409s apart — see the
            // note on ApiError.code.
            code: typeof errBody.code === 'string' ? errBody.code : undefined,
            /* ⛔⛤ AND THIS ONE HAD TO BE ADDED IN BOTH CONSTRUCTORS. There are two
               identical error-building blocks in this file, and a field listed in
               only one of them is dropped on whichever path the caller happens to
               use - silently, because the type says optional.
                 ** A DTO IS A FILTER, NOT A DESCRIPTION. AN UNNAMED FIELD IS
                    DELETED. ** (third time this week) */
            shape: typeof errBody.shape === 'string' ? errBody.shape : undefined,
        });
    }

    return body as T;
}

/**
 * GET /api/calendar?facilityCode=...&date=YYYY-MM-DD
 */
export function getCalendar(
    token: string,
    facilityCode: string,
    date: string,
): Promise<CalendarDayDto> {
    const params = new URLSearchParams({ facilityCode, date });
    return request<CalendarDayDto>(`/calendar?${params}`, token);
}

/**
 * GET /api/calendar/range?facilityCode=...&start=YYYY-MM-DD&end=YYYY-MM-DD[&status=CODE,CODE]
 * Appointments overlapping [start, end]. Optional statusCodes filters by status.
 */
export function getCalendarRange(
    token: string,
    facilityCode: string,
    start: string,
    end: string,
    statusCodes?: AppointmentStatusCode[],
): Promise<CalendarRangeDto> {
    const params = new URLSearchParams({ facilityCode, start, end });
    if (statusCodes && statusCodes.length > 0) params.set('status', statusCodes.join(','));
    return request<CalendarRangeDto>(`/calendar/range?${params}`, token);
}

/**
 * GET /api/lookups — facilities, carriers, clients for dropdowns.
 */
export function getLookups(token: string): Promise<LookupsDto> {
    return request<LookupsDto>('/lookups', token);
}

/**
 * GET /api/health — anonymous, no token needed. Used for connection
 * diagnostics from the UI.
 */
export async function getHealth(): Promise<{ status: string; timestamp: string }> {
    const res = await fetch(`${API_BASE}/health`);
    if (!res.ok) throw new Error(`Health check failed: HTTP ${res.status}`);
    return res.json();
}

/**
 * Body for POST /api/appointments. Mirrors the zod CreateAppointmentRequest
 * on the server, plus a `requiredCapabilityIds` array.
 */
export interface CreateAppointmentBody {
    facilityCode: string;
    dockDoorId: number | null;
    direction: 'INBOUND' | 'OUTBOUND' | 'BLOCK';
    carrierCode?: string;
    carrierName?: string | null;
    clientCode?: string;
    clientName?: string | null;
    documentType?: string;
    documentNumber?: string | null;
    poNumber?: string | null;
    referenceNumber?: string | null;
    /** #130. Bookable; `trailerNumber` deliberately is not — see AppointmentDto. */
    containerNumber?: string | null;
    palletCount?: number | null;
    scheduledStartUtc: string;
    scheduledEndUtc: string;
    comments?: string | null;
    requiredCapabilityIds?: number[];
    consignees?: {
        consigneeCode: string;
        consigneeName?: string | null;
        shipmentNumber?: string | null;
        isPrimary?: boolean;
    }[];
    overrideWarnings?: boolean;
}

/**
 * POST /api/appointments — create a new appointment.
 * Server returns 201 with the created appointment on success, 409 with
 * { conflicts: [...] } on conflict (surfaced via ApiCallError.details.conflicts).
 */
export function createAppointment(
    token: string,
    body: CreateAppointmentBody,
): Promise<AppointmentDto> {
    return request<AppointmentDto>('/appointments', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export interface BlockBatchBody {
    facilityCode: string;
    dockDoorIds: number[];
    scheduledStartUtc: string;
    scheduledEndUtc: string;
    comments?: string | null;
}
export interface BlockBatchResult {
    created: { dockDoorId: number; appointmentId: number }[];
    skipped: { dockDoorId: number; reason: string }[];
}

/**
 * POST /api/appointments/block-batch — create a maintenance block on several
 * doors at once. Each door is independent; occupied doors are skipped & reported.
 */
export function createBlockBatch(
    token: string,
    body: BlockBatchBody,
): Promise<BlockBatchResult> {
    return request<BlockBatchResult>('/appointments/block-batch', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// ---- When the system may text a driver by itself (#150, migration 152) -----

export interface AutomatedTextConfigDto {
    enableDoorAssignedText: boolean;
    /** ⭐ Only text a man who was actually promised a text. */
    doorTextOnlyIfPromised: boolean;
    enableReminderText: boolean;
    reminderHoursBefore: number;
    /** Minutes since facility-local midnight. Both null = no quiet hours. */
    quietStartMinutes: number | null;
    quietEndMinutes: number | null;
    maxAutoTextsPerAppointment: number;
}

export interface AutomatedTextResponse {
    facilityCode: string;
    /** ⛔ null = NOTHING is automated here. Not "use the defaults". */
    config: AutomatedTextConfigDto | null;
    shippedDefaults: AutomatedTextConfigDto;
    /** ⭐ So switching it on is not an act of faith. Null if unavailable. */
    recent: { sent: number; failed: number; since: string } | null;
    refusalLabels: Record<string, string>;
    /** ⛔ False = migration 152 not applied. Nothing saves, nothing sends. */
    migrationApplied: boolean;
}

/** GET /api/mgmt/automated-text-config?facilityCode= */
export function getAutomatedTextConfig(token: string, facilityCode: string): Promise<AutomatedTextResponse> {
    return request<AutomatedTextResponse>(
        `/mgmt/automated-text-config?facilityCode=${encodeURIComponent(facilityCode)}`, token);
}

/**
 * POST /api/mgmt/automated-text-config
 *
 * ⭐ The response's `armed[]` says in words what will now happen — switching this
 * on starts putting messages on real phones, and "Saved." does not convey that.
 */
export function saveAutomatedTextConfig(
    token: string, body: { facilityCode: string } & AutomatedTextConfigDto,
): Promise<{ saved: boolean; armed: string[]; message: string }> {
    return request('/mgmt/automated-text-config', token, {
        method: 'POST', body: JSON.stringify(body),
    });
}

/** DELETE /api/mgmt/automated-text-config — back to nothing automated. */
export function clearAutomatedTextConfig(
    token: string, facilityCode: string,
): Promise<{ deleted: boolean; message: string }> {
    return request('/mgmt/automated-text-config', token, {
        method: 'DELETE', body: JSON.stringify({ facilityCode }),
    });
}

// ---- Which fields a booking must carry (#131, migration 151) ---------------

export type BookingScope = 'Scheduler' | 'Carrier' | 'Client';

export interface FieldPolicyRowDto {
    policyId: number;
    scope: BookingScope;
    direction: 'INBOUND' | 'OUTBOUND' | 'BOTH';
    requirePoNumber: boolean;
    requireReferenceNumber: boolean;
    requireContainerNumber: boolean;
    requireTrailerNumber: boolean;
    warnWhenNoReference: boolean;
}

export interface FieldPolicyResponse {
    facilityCode: string;
    rows: FieldPolicyRowDto[];
    /** ⭐ So the screen can say "(defaults)" instead of rendering four unchecked
     *  boxes that look like a decision somebody made. */
    shippedDefaults: Omit<FieldPolicyRowDto, 'policyId' | 'scope' | 'direction'>;
    scopes: BookingScope[];
    /** ⛔ `bookable: false` means NO booking form collects it, so requiring it
     *  would refuse every appointment. The screen must disable those. */
    fields: { key: string; label: string; bookable: boolean }[];
    bookableFields: string[];
    unsatisfiable: { policyId: number; scope: BookingScope; direction: string; field: string }[];
    /** ⛔ False = migration 151 is not applied. Nothing saves, nothing is enforced. */
    migrationApplied: boolean;
}

/** GET /api/mgmt/appointment-field-policy?facilityCode= */
export function getFieldPolicy(token: string, facilityCode: string): Promise<FieldPolicyResponse> {
    return request<FieldPolicyResponse>(
        `/mgmt/appointment-field-policy?facilityCode=${encodeURIComponent(facilityCode)}`, token);
}

/** POST /api/mgmt/appointment-field-policy — upsert one scope+direction. */
export function saveFieldPolicy(
    token: string,
    body: { facilityCode: string; scope: BookingScope; direction: string } & Omit<FieldPolicyRowDto, 'policyId' | 'scope' | 'direction'>,
): Promise<{ saved: boolean }> {
    return request<{ saved: boolean }>('/mgmt/appointment-field-policy', token, {
        method: 'POST', body: JSON.stringify(body),
    });
}

/**
 * DELETE /api/mgmt/appointment-field-policy — back to the shipped defaults.
 *
 * ⭐ A real delete, not a disable: "no row" already means "shipped defaults", so a
 * deactivated row would be a second way of saying the same thing.
 */
export function resetFieldPolicy(
    token: string,
    body: { facilityCode: string; scope: BookingScope; direction: string },
): Promise<{ deleted: boolean; message: string }> {
    return request<{ deleted: boolean; message: string }>('/mgmt/appointment-field-policy', token, {
        method: 'DELETE', body: JSON.stringify(body),
    });
}

// ---- An arrival's own history (#98 / #140) ----------------------------------

export interface CheckInEventDto {
    eventType: string;
    fieldName: string | null;
    oldValue: string | null;
    newValue: string | null;
    notes: string | null;
    actorName: string | null;
    createdUtc: string;
}

/**
 * GET /api/mgmt/checkins/{id}/history
 *
 * ⭐ THE ENDPOINT HAS EXISTED SINCE #91 AND NOTHING CALLED IT. Every edit, void,
 * pallet variance, license scan and match has been written to
 * `dock.DriverCheckInEvent` where no scheduler could see it.
 *
 * ⛔ A DIFFERENT LOG FROM THE APPOINTMENT'S. `dock.AppointmentAudit` is what
 * happened to the BOOKING; this is what happened to the ARRIVAL. They overlap
 * (a match is written to both) and they are not the same question.
 */
/**
 * ---- ⭐⭐⭐ DECISION 215 — ONE PARTY'S OWN RECORD --------------------------
 *
 * > Pete, 2026-09-22: *"the appointment is the main item in this whole program,
 * > connecting the drivers, client, carrier, consignee and paperwork together.
 * > All the other ones besides appointment should show their individual comms,
 * > history, etc. so you get a complete picture and a sub picture."*
 *
 * ⛔ THE HUB HALF IS NOT HERE AND NEEDS NOTHING. `dock.AppointmentAudit` already
 * records the driver, the door, the paperwork and the texts against the load, so
 * `getAppointmentEvents` was already the complete picture — the rail was showing
 * 8 of 16 because of a struck LIMIT. This call is only the SUB-picture.
 *
 * ⚠️ `code` IS A QUERY PARAMETER, `kind` IS A PATH SEGMENT, and the route says why:
 * a party code is arbitrary WMS text that can contain a slash.
 */
export type PartyHistoryKind = 'client' | 'carrier' | 'consignee';

export interface PartyHistoryRow {
    appointmentId: string;
    appointmentNumber: string | null;
    /** ⭐ THE SITE THE LOAD WAS AT — this list crosses facilities, so a row that
     *  does not say where it happened is a row a scheduler cannot place. */
    facilityCode: string;
    eventUtc: string;
    eventType: string;
    actorType: string | null;
    actorId: string | null;
    notes: string | null;
}

export interface PartyHistoryResult {
    rows: PartyHistoryRow[];
    /** ⛔ TRUE WHEN THE 200-ROW CAP BIT. There is no "load more" behind it, so the
     *  panel must SAY SO — the same obligation the struck `+ N earlier` line was
     *  invented for, and the reason it was invented is still valid. */
    truncated: boolean;
    /**
     * ⛔⛔ `false` IS THE REFUSAL CASE, not an empty history: the caller may not
     * see the facility that was asked for, so the query never ran. ⚠️ Rendering it
     * as "this client has no history" would report a permissions problem as a fact
     * about the client.
     *   ** AN EMPTY LIST IS ONLY HONEST IF IT SAYS WHY IT IS EMPTY. **
     */
    permitted: boolean;
}

/**
 * ⭐ TWO HALVES OF ONE FRAME, BOTH REQUIRED — the SITE and the DAY.
 *
 * > Pete, 2026-09-22: *"Why would we show anything other than what is there for
 * > the facility they are in/selected?"* … *"We want to show all but in the
 * > context of the day on the calendar … I don't think we should be showing
 * > yesterdays items, be confusing."*
 *
 * ⚠️ THE DAY PICKS THE LOADS, NOT THE EVENTS. A load scheduled today was created
 * last week; you get the whole story of today's loads, not today's clicks.
 */
export function getPartyHistory(
    token: string, kind: PartyHistoryKind, facilityCode: string, date: string,
    code: string, limit?: number,
): Promise<PartyHistoryResult> {
    const params = new URLSearchParams({ facilityCode, date, code });
    if (limit != null) params.set('limit', String(limit));
    return request<PartyHistoryResult>(`/party/${kind}/history?${params}`, token);
}

export function getCheckInHistory(token: string, checkInId: string): Promise<{ events: CheckInEventDto[] }> {
    return request<{ events: CheckInEventDto[] }>(
        `/mgmt/checkins/${encodeURIComponent(checkInId)}/history`, token);
}

// ---- The driver's paperwork vs the booking (#132) ---------------------------

export interface PaperworkConflictDto {
    field: string;
    appointmentValue: string;
    driverValue: string;
    detail: string;
}

export interface PaperworkStatusDto {
    /** ISO, or null when nothing is outstanding. */
    conflictUtc: string | null;
    lastEvent: {
        eventUtc: string;
        eventType: 'PaperworkConflict' | 'PaperworkBackfilled' | string;
        actorId: string | null;
        detail: {
            checkInId?: string;
            appointmentBefore?: Record<string, string | null>;
            driverPaperwork?: Record<string, string | null>;
            filled?: Record<string, string>;
            conflicts?: PaperworkConflictDto[];
            agreements?: string[];
            summary?: string;
            trailerColumnPresent?: boolean;
        } | null;
    } | null;
    /** ⛔ False = migration 151 is not applied; there is no flag to read. */
    migrationApplied: boolean;
}

/**
 * GET /api/mgmt/appointments/{id}/paperwork
 *
 * ⛔ A SEPARATE CALL, deliberately not folded into the calendar. `PaperworkConflictUtc`
 * only exists after migration 151, and the calendar is the one endpoint that must
 * never depend on an unapplied migration (#127).
 */
export function getAppointmentPaperwork(token: string, appointmentId: number | string): Promise<PaperworkStatusDto> {
    return request<PaperworkStatusDto>(
        `/mgmt/appointments/${encodeURIComponent(String(appointmentId))}/paperwork`, token);
}

/** POST .../paperwork/reviewed — clears the FLAG. ⛔ Never the record. */
export function markPaperworkReviewed(
    token: string, appointmentId: number | string,
): Promise<{ cleared: boolean; message: string }> {
    return request<{ cleared: boolean; message: string }>(
        `/mgmt/appointments/${encodeURIComponent(String(appointmentId))}/paperwork/reviewed`,
        token, { method: 'POST' });
}

// ---- Mass-create day-only appointments (#66) -------------------------------

export interface MassCreateBody {
    facilityCode: string;
    /** ⛔ The FACILITY'S local date, yyyy-mm-dd — not an instant. */
    localDate: string;
    direction: 'INBOUND' | 'OUTBOUND';
    clientCode: string;
    clientName?: string | null;
    carrierCode: string;
    carrierName?: string | null;
    count: number;
    documentType?: string | null;
    comments?: string | null;
    /**
     * ⛔ ONLY EVER SET FROM A CLICK. The server refuses a batch that would cross
     * the facility's daily cap with a 409 carrying `code: 'daily_limit_reached'`;
     * this flag says a named person read that sentence and chose to proceed
     * anyway. Defaulting it to true would silently disable the cap for everyone.
     */
    acknowledgeDailyLimit?: boolean;
}

export interface MassCreateResult {
    batchId: string;
    created: number;
    appointmentIds: string[];
    windowStartUtc: string;
    windowEndUtc: string;
    /** Worth saying out loud, never worth refusing over: holidays, unset hours. */
    warnings: string[];
}

/**
 * ⚠️ REPLACED CancelBatchResult (#141). The old shape was { cancelled, skipped } —
 * a batch undo that could only cancel. It now removes what never happened and
 * cancels only what did, so it reports the SAME THREE NUMBERS as an upload
 * back-out. ⛔ One shape, because a scheduler should not have to learn two ways of
 * being told the same thing.
 */
export type BatchBackoutResult = UploadBackoutResult & { batchId: string };

/**
 * POST /api/mgmt/appointments/mass-create
 *
 * "20 trucks today, no times." Creates `count` appointments with no door, no
 * time promised and no PO/BOL/reference numbers, all sharing one batch id.
 *
 * ⚠️ 409 with `details.code === 'daily_limit_reached'` means the facility's daily
 * cap would be crossed. `details.error` is the sentence to show; re-submit with
 * `acknowledgeDailyLimit: true` only after the scheduler has seen it.
 */
export function massCreateAppointments(
    token: string,
    body: MassCreateBody,
): Promise<MassCreateResult> {
    return request<MassCreateResult>('/mgmt/appointments/mass-create', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/**
 * POST /api/mgmt/appointments/mass-create/{batchId}/backout
 *
 * ⭐ Removes the appointments nothing ever happened to, cancels the ones that have
 * history, and leaves alone anything that has arrived or that somebody already
 * decided about. Same judge, same executor and same result shape as backOutUpload.
 *
 * ⚠️ THE PATH CHANGED FROM /cancel, so this needs the matching backend. Deployed
 * against the old backend it 404s — which is the visible failure, and better than
 * the same URL quietly meaning something else.
 */
export function backOutMassCreateBatch(
    token: string, batchId: string, facilityCode: string, localDate: string,
): Promise<BatchBackoutResult> {
    return request<BatchBackoutResult>(
        `/mgmt/appointments/mass-create/${encodeURIComponent(batchId)}/backout`,
        token,
        { method: 'POST', body: JSON.stringify({ facilityCode, localDate }) },
    );
}

/**
 * GET /api/appointments/validate-po?facilityCode=...&poNumber=...
 * Returns the WMS-side info about a PO, including whether it exists.
 */
export interface PoLookupResult {
    exists: boolean;
    poNumber?: string;
    clientCode?: string;
    carrierCode?: string;
    expectedLineCount?: number;
    expectedArrivalUtc?: string;
    rawError?: string;
}

export function validatePo(
    token: string,
    facilityCode: string,
    poNumber: string,
): Promise<PoLookupResult> {
    const params = new URLSearchParams({ facilityCode, poNumber });
    return request<PoLookupResult>(`/appointments/validate-po?${params}`, token);
}

// ============================================================================
// ADD to web/src/api/client.ts — paste this block right AFTER the validatePo
// function (after its closing brace, before the "Helper for endpoints that
// return 204" comment). It reuses the same request<T>() helper.
// ============================================================================

/** One row from the PO picker search — normalized across all WMS systems. */
export interface PoSummary {
    poNumber: string;
    wmsSource: string;            // 'Deposco' | 'Camelot' | 'Extensiv'
    orderType: string;            // 'Purchase Order' | 'Receipt' | ...
    facilityCode: string | null;  // canonical facility (mapped); null if unknown
    rawFacility: string | null;   // the WMS's own facility value (e.g. 'MAIN')
    clientCode: string | null;    // BusinessUnit
    /**
     * The WMS's OTHER number. A Camelot receipt has two: poNumber is the
     * receipt (12605) and this is the PO # off the paperwork
     * ('PO TEST PETE-SARAH'). Deposco leaves it null.
     */
    customerOrderNumber: string | null;
    /** Camelot receipt: the screen's Rcv From Reference / Rcv From Code.
     *  On a shipment the same source fields hold the consignee's. */
    customerReference: string | null;
    customerCode: string | null;
    /** Carrier-side numbers (Pipeline migration 030). */
    containerNumber: string | null;
    bolNumber: string | null;
    proNumber: string | null;
    customer: string | null;
    supplierName: string | null;  // TradingPartner
    status: string | null;
    statusBucket: string | null;
    orderTotal: number | null;
    placedDate: string | null;
    createdDate: string | null;
    isCurrentFacility: boolean;   // true if facilityCode === the searched facility
    /**
     * ---- ⭐⭐ DECISION 214 — THE DIRECTION VERDICT, COMPUTED SERVER-SIDE ------
     *
     * ⛔ `orderDirection` IS `null` FOR A TYPE THIS APP HAS NO RULE FOR — a
     * Camelot `Adjustment`, today, and whatever Camelot invents next. ⚠️ It does
     * NOT mean "neither"; it means "unclassified", and those rows are now
     * RETURNED AND LABELLED rather than silently dropped, which is the whole of
     * decision 214.
     *
     * ⛔⛔ `directionMismatch` IS A TRI-STATE AND MUST BE READ AS ONE.
     *   true  — this document runs the other way (or has no direction at all)
     *   false — it matches the direction being booked
     *   null  — no direction was sent, so there is nothing to mismatch
     * ⚠️ `if (r.directionMismatch)` is correct; `if (!r.directionMismatch)` is
     * NOT the complement of it, because `null` is falsy and means "unknown".
     *   ** A TRI-STATE READ AS A BOOLEAN IS A WARNING THAT GOES QUIET ON EXACTLY
     *      THE ROWS THAT NEEDED IT. **
     */
    orderDirection: 'INBOUND' | 'OUTBOUND' | null;
    directionMismatch: boolean | null;
}

export interface PoDetailLine {
    lineNumber: string | null;
    itemNumber: string | null;
    category?: string | null;
    orderedQty: number | null;
    /**
     * ⛔⛔ `ShippedQuantity` FROM THE WMS — ⛔⤴ ~~`receivedQty`~~, renamed 2026-09-27.
     * Pete, on an outbound sales order reading `Received 100`: *"thats an outbound!!!!
     * Confused on what that means."*
     * ⚠️ THERE IS NO RECEIVED QUANTITY TO HAVE. `dbo.OrderLinesCache` carries no such
     * column; receipts live in `dbo.ReceiptLinesCache` (with `ReceivingLocation` and
     * `ReceivedDate`), which no route reads yet.
     *   ** A FIELD NAMED FOR WHAT SOMEBODY HOPED IT HELD IS A LIE THE UI REPEATS. **
     */
    shippedQty: number | null;
    uom: string | null;
    unitPrice: number | null;
    lineTotal?: number | null;
    lineStatus: string | null;
    receivedDate?: string | null;
}

export interface PoDetailResult {
    header: {
        poNumber: string;
        wmsSource: string;
        orderType: string;
        facilityCode: string | null;
        clientCode: string | null;
        customer: string | null;
        supplierName: string | null;
        status: string | null;
        orderTotal: number | null;
        placedDate: string | null;
        createdDate: string | null;
    };
    lines: PoDetailLine[];
    /** ⭐ F80: 'timeout' when the header was read but the lines did not arrive in
     *  time (the database was busy). ⛔ Then `lines` is [] and means NOTHING —
     *  never render it as "no lines". Absent from an older backend. */
    linesError?: 'timeout' | null;
}

/**
 * ---- ⭐⭐ THE WMS ORDERS RAIL PANEL (#333) -------------------------------
 *
 * ⚠️ THE BOUNDS ARE PASSED IN, NOT A SCOPE NAME. §1Z.1 — the calendar already
 * computes day/week/month bounds (including the six-week grid the month view
 * actually draws), so the panel sends those. ⛔ A third copy of that arithmetic
 * is what `tools/search-scope-guard.cjs` exists to prevent.
 *
 * ⛔ `sources` — UNDEFINED IS NO FILTER, AN EMPTY ARRAY KEEPS NOTHING (§1R.10).
 * Do not "helpfully" drop an empty array; unticking every box must show nothing,
 * or the filter appears not to work.
 */
export interface OrdersPanelRowDto {
    orderNumber: string;
    orderType: string | null;
    wmsSource: string | null;
    direction: 'INBOUND' | 'OUTBOUND' | null;
    businessUnit: string | null;
    customer: string | null;
    status: string | null;
    statusBucket: string | null;
    /**
     * ⭐⭐ WHY A `Complete` OUTBOUND SHOWS UNDER **Open** (#367). True when this is
     * an outbound order whose `ShippingStatus` is not one of the shipped codes —
     * Deposco calls it Complete and it has not left the building, so it is real open
     * work. The panel draws a `not shipped` chip so a correct filter stops reading
     * as a broken one.
     * ⚠️ `null` for inbound, and `null` when the code table could not be read.
     * ⛔ ABSENT IS NOT `false` — never render "shipped" from a null.
     */
    notShipped: boolean | null;
    /** ⭐ F60 — outbound shipping state in words (Deposco Shipping Status; Camelot from its status). Optional for an older backend. */
    shippingStatus?: string | null;
    effectiveDate: string | null;
    plannedShipDate: string | null;
    plannedArrivalDate: string | null;
    containerNumber: string | null;
    bolNumber: string | null;
    proNumber: string | null;
    /** ⭐ 2026-09-28 — the WMS Ship Via, the order's nearest thing to a carrier (the Match screen). Optional for an older backend. */
    shipVia?: string | null;
    customerOrderNumber: string | null;
    customerReference: string | null;
    /** ⛔ A COUNT, because one order can take several trucks. */
    bookedCount: number;
    /**
     * ⭐ 2026-09-27, open item 35: the four match fields the server has always
     * computed and this type never declared. They say WHICH COLUMNS MET, never
     * what they mean — `matchedOn` used to carry a label ('PO number') that is
     * wrong for TRAFFIX, which puts its container in `CustomerOrderNumber`.
     * ⚠️ All four are null together for a link-only booking: a deliberate
     * attachment is a real match with no field to name.
     */
    bookedOn: {
        appointmentId: string;
        appointmentNumber: string | null;
        scheduledStartUtc: string | null;
        /** ⭐ 'DD23' — the door. Null when unassigned, or when the door belongs
         *  to another facility (the query bounds it rather than trusting it). */
        dockDoorCode: string | null;
        /**
         * ⛔⛔ `'TimeSlot'` | `'DayOnly'` — AND THE CARD MUST ASK BEFORE PRINTING A
         * TIME. A day-only booking's `scheduledStartUtc` is the facility's OPENING
         * time, stored wide so capacity and daily-limit queries can see it.
         * `appointmentTimeLabel` in `lib/utils.ts` is the one place that decides
         * what goes where a start time would go; this is its input.
         *   ** A STORED WINDOW IS NOT A PROMISE SOMEBODY MADE. **
         */
        schedulingMode: string | null;
        matchedApptField: string | null;
        matchedWmsColumn: string | null;
        matchedApptValue: string | null;
        matchedWmsValue: string | null;
    }[];
    alreadyBooked: boolean;
    /** ⛔ Booked INSIDE the range on screen. NOT the inverse of `alreadyBooked`. */
    bookedInRangeCount: number;
    facility: string | null;
    /**
     * ⭐ The site's DISPLAY NAME. ⛔ The row must never render `facility` alone:
     * a bare code beside the status read as `Printed 200` and parsed as a
     * QUANTITY, which is why 200+ of Denver's orders looked like Brighton's.
     */
    facilityName: string | null;
    elsewhere: boolean;
    /** ⭐ Reassigned here by a dock.WmsOrderFacilityOverride rule (migration 176). */
    byOverride: boolean;
    /**
     * ⭐ 'OPEN' | 'DONE' | 'CANCELLED' per dock.WmsOrderStatusGroup, or `null`.
     *
     * ⛔ `null` MEANS NOBODY HAS CLASSIFIED THIS STATUS — and the row is on
     * screen BECAUSE of that, never in spite of it. The panel labels it so an
     * unrecognised WMS status shows up as a gap in a table somebody can fix,
     * rather than as a truck that silently never appeared on the board.
     */
    statusGroup: string | null;
    /** ⚠️ Dated only by the sync timestamp — the WMS gave it no planned date. */
    dateIsFallback: boolean;
    rowUpdatedAt: string | null;
}

export interface OrdersPanelDto {
    rows: OrdersPanelRowDto[];
    capped: boolean;
    fromDate: string;
    toDate: string;
    /** ⭐ §1Z.2 — rendered on EVERY result, empty or full. */
    scopeLabel: string;
    /** ⚠️ null means NOT ASKED, which is not zero. */
    outsideScope: number | null;
    /**
     * ⭐ SPEC §6 — how many appointments in the visible range have at least one
     * matching order. ⛔ APPOINTMENTS, NOT ORDERS: one carrying three counts 1,
     * so this and the rendered row count answer DIFFERENT questions and each
     * needs its own label. ⚠️ null means not answered, which is not zero.
     */
    matchedAppointmentsInRange: number | null;
    sourcesInRange: string[];
    searchedAllDates: boolean;
    refusedReason: string | null;
    minSearchLength: number;
    /**
     * ---- ⭐⭐ WHICH INVISIBLE DEFAULTS THE SEARCH DROPPED (§1Z.3) ----------
     *
     * > Pete, 2026-09-09: *"on the search textbox, we need to make sure it
     * > doesn't limit the criteria the user can't see - so needs to be open to a
     * > certain extent"*
     *
     * ⛔ EMPTY ON A DATED LIST. On a search it names each default that fell away
     * ("this site only", "open orders only") so the panel can say so. ⚠️ A list
     * that silently changed what it filters by is the same lie as one that
     * silently filters — the boxes on screen would no longer describe the rows.
     */
    droppedDefaults: string[];
    /**
     * ⭐ THE (i) CONTENT, READ FROM THE SAME TABLE THAT DRIVES THE FILTER.
     * ⛔ Never hard-code this list in a component: the legend and the filter
     * would be two sources of truth and only one of them is enforced, so the
     * moment Camelot answers about `Posted` the paragraph starts lying silently.
     * ⚠️ `null` means the lookup FAILED, which is not "nothing is classified".
     */
    statusLegend: Array<{ wmsSource: string; currentStatus: string; groupCode: string }> | null;
    /** ⭐ Statuses on this screen with no row in the table. These rows are SHOWN. */
    unclassifiedSeen: Array<{ wmsSource: string; currentStatus: string }>;
    /**
     * ⭐⭐ WHAT THE PANEL PULLED (#366). `whereSql` is the string that RAN, not a
     * reconstruction — a display-only copy is the two-sources-of-truth failure
     * that renders perfectly while describing a query nobody executed.
     * ⭐ `provenance.changeBy` answers "config or deploy?" per rule.
     */
    criteria: {
        whereSql: string;
        directions: Array<{ direction: 'INBOUND' | 'OUTBOUND'; orderTypes: string[]; dateSql: string }>;
        statusFilter: string[] | null;
        facilityScope: string;
        searchColumns: Array<{ column: string; label: string; separatorInsensitive: boolean }>;
        provenance: Array<{ rule: string; livesIn: string; changeBy: 'UPDATE' | 'DEPLOY' }>;
        /**
         * ⭐ An outbound that has not shipped is shown whatever its CurrentStatus (#367).
         * ⚠️ `shippingWidened: false` WITH `shippingCodesRead: false` means the lookup
         * FAILED and the panel is over-hiding as it did before — a different thing from
         * the rule simply not applying, and the (i) says which.
         */
        shippingWidened: boolean;
        shippingCodesRead: boolean;
    };
}

export function getPanelOrders(
    token: string,
    args: {
        facilityCode: string;
        from: string;
        to: string;
        scopeLabel?: string;
        /** ⛔ undefined = no filter; [] = both unticked, shows nothing (§1R.10). */
        directions?: ('INBOUND' | 'OUTBOUND')[];
        sources?: string[];
        /** "Never Booked" — no appointment on ANY date. */
        unbookedOnly?: boolean;
        /** "Booked <range>" — has one INSIDE from..to. ⛔ Not the inverse of the above. */
        bookedInRange?: boolean;
        /** ⛔ Default false. Without it the panel shows ONLY this facility. */
        includeOtherSites?: boolean;
        /**
         * ---- ⛔⛔ THREE STATES, AND THE FIRST ONE IS WHY THIS IS NOT A BOOLEAN
         *
         *   · `undefined` — the scheduler has not touched the status boxes. The
         *                   server applies its default on a dated list and
         *                   applies NOTHING during a search (§1Z.3).
         *   · `null`      — every status, chosen explicitly.
         *   · `[]`        — every box unticked; shows nothing (§1R.10).
         *
         * ⚠️ SENDING THE DEFAULT EXPLICITLY WOULD BREAK §1Z.3. If the panel
         * always spelt out `OPEN`, the server could no longer tell a default from
         * a choice, and a typed search would stay silently narrowed to open
         * orders — reporting "nothing found" for a container whose order is
         * marked Posted.
         */
        statusGroups?: string[] | null;
        q?: string | null;
    },
): Promise<OrdersPanelDto> {
    const p = new URLSearchParams({
        facilityCode: args.facilityCode, from: args.from, to: args.to,
    });
    if (args.scopeLabel) p.set('scopeLabel', args.scopeLabel);
    if (args.directions !== undefined) p.set('directions', args.directions.join(','));
    // ⛔ PRESENT-BUT-EMPTY IS A REAL STATE. `sources=` means "nothing ticked".
    if (args.sources !== undefined) p.set('sources', args.sources.join(','));
    if (args.unbookedOnly) p.set('unbookedOnly', 'true');
    if (args.bookedInRange) p.set('bookedInRange', 'true');
    if (args.includeOtherSites) p.set('includeOtherSites', 'true');
    /**
     * ⛔ ABSENT, 'ALL', AND EMPTY ARE THREE DIFFERENT REQUESTS. `?statusGroups=`
     * means "unticked everything, show nothing"; 'ALL' means "every status".
     * ⚠️ Spelling ALL as an empty string would make one of those two silently
     * become the other — one shows every row and the other shows none.
     */
    if (args.statusGroups === null) p.set('statusGroups', 'ALL');
    else if (args.statusGroups !== undefined) p.set('statusGroups', args.statusGroups.join(','));
    if (args.q) p.set('q', args.q);
    return request(`/mgmt/orders?${p}`, token);
}

/** Prefix-search cached POs across all WMS. facilityCode sorts (not filters). */
export function poSearch(
    token: string,
    q: string,
    facilityCode: string,
    /** Narrows to the documents a truck of this direction can be booked against.
     *  Omitted = search both (the form may not have a direction yet). */
    direction?: 'INBOUND' | 'OUTBOUND',
    /** YYYY-MM-DD. Either this or 3+ characters of `q` is enough to search. */
    date?: string,
    /**
     * ⭐⭐ A DATE RANGE (2026-09-22). Pete: *"we search dates 2 weeks back and 2
     * weeks forward to narrow the results"* -- because this box is used mid-
     * booking and `dbo.OrdersCache` holds 366,889 sales orders.
     *
     * ⛔ AN EXACT MATCH IGNORES IT ENTIRELY, server-side: *"But still hit anything
     * on an exact match."* The caller does not decide that and must not try -- the
     * same `isWholeIdentifier` gate the calendar search uses makes the call.
     *   ** IF A CALLER CAN NARROW AN EXACT LOOKUP, THE EXACT LOOKUP IS NOT A RULE,
     *      IT IS A SUGGESTION. **
     */
    from?: string,
    to?: string,
): Promise<PoSearchResult> {
    const params = new URLSearchParams({ q });
    if (facilityCode) params.set('facilityCode', facilityCode);
    if (direction) params.set('direction', direction);
    if (date) params.set('date', date);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    /**
     * ⛔⛔ RETURNS THE ENVELOPE NOW, NOT JUST `results` (#363). It used to unwrap
     * to the array, which threw `otherDirection` away at the one call site that
     * needs it — and the empty state then reported ABSENCE for a document that is
     * sitting in the cache under the opposite type.
     *
     * ⚠️ A required shape change, not an optional field, so the compiler walks
     * every caller. A defaulted one lets a stale call site look updated.
     */
    return request<PoSearchResult>(`/appointments/po-search?${params}`, token);
}

export interface PoSearchResult {
    results: PoSummary[];
    /**
     * ---- ⛔⛤ ~~otherDirection~~ — ALWAYS `null` SINCE DECISION 214 -----------
     *
     * It reported *"it exists as an outbound document"* to explain an absence the
     * order-type filter had created. ⚠️ THE FILTER IS GONE, so the row it used to
     * describe is now IN `results`, labelled. The server still sends the key as a
     * literal `null` for one release so a bundle cached from this morning does not
     * read `undefined`; the field goes after the frontend is live and confirmed.
     * ⛔ Do not write new code against it. It cannot become non-null.
     */
    otherDirection: { direction: string; count: number } | null;
    /**
     * ⭐⭐ WHICH DATE RULE ACTUALLY RAN (2026-09-22).
     *
     *   'exact' — the typed text is a whole identifier, so NO date filter applied.
     *   'range' — the ±2-week default (or whatever the caller sent).
     *   'day'   — a single date was picked.
     *   'none'  — no date filter at all.
     *
     * ⛔ THE SCREEN MUST SAY THIS. An empty list that does not name where it
     * looked is the sentence a scheduler mistakes for "this does not exist".
     *   ** AN EMPTY LIST IS ONLY HONEST IF IT SAYS WHERE IT LOOKED. **
     * ⚠️ Optional, because an OLD backend in the minutes after a deploy sends
     * neither it nor the range — the screen must degrade to saying nothing rather
     * than to asserting 'none'.
     */
    dateScope?: 'exact' | 'range' | 'day' | 'none';
    from?: string | null;
    to?: string | null;
}

/** View-only header + line items for one PO. wmsSource + orderType pick the source. */
export function poDetail(
    token: string,
    poNumber: string,
    wmsSource: string,
    orderType: string,
    /** ⛔ REQUIRED SINCE #342 — it is what scopes the read to your company. */
    facilityCode: string,
): Promise<PoDetailResult> {
    /* ⭐ F80: partial=1 says "I can show a header whose lines timed out". Without
       it the backend still fails the whole request, so an older client never
       mistakes a timeout for an order with no lines. */
    const params = new URLSearchParams({ poNumber, wmsSource, facilityCode, partial: '1' });
    if (orderType) params.set('orderType', orderType);
    return request<PoDetailResult>(`/appointments/po-detail?${params}`, token);
}




/**
 * Helper for endpoints that return 204 No Content — fetch + token, no body.
 * Used by all the status-change endpoints (arrive/depart/cancel) and
 * reschedule, which return 204 on success.
 */
async function requestNoBody(
    path: string,
    token: string,
    init: RequestInit = {},
): Promise<void> {
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    // ⚠️ NEVER SET Content-Type FOR FormData. `fetch` sets
    // `multipart/form-data; boundary=…` itself, and the boundary is the only way
    // the server can find where each part starts. Setting the header by hand
    // omits it, and the request arrives as an unparseable body — a 400 that
    // looks like the file was rejected rather than never read.
    if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
    if (!res.ok) {
        let errBody: Record<string, unknown> = {};
        try {
            errBody = await res.json();
        } catch {
            // ignore
        }
        throw new ApiCallError({
            status: res.status,
            error: typeof errBody.error === 'string' ? errBody.error : `HTTP ${res.status}`,
            issues: errBody.issues,
            conflicts: errBody.conflicts as ApiError['conflicts'],
            // Carried through so a caller can tell two 409s apart — see the
            // note on ApiError.code.
            code: typeof errBody.code === 'string' ? errBody.code : undefined,
            /* ⛔⛤ AND THIS ONE HAD TO BE ADDED IN BOTH CONSTRUCTORS. There are two
               identical error-building blocks in this file, and a field listed in
               only one of them is dropped on whichever path the caller happens to
               use - silently, because the type says optional.
                 ** A DTO IS A FILTER, NOT A DESCRIPTION. AN UNNAMED FIELD IS
                    DELETED. ** (third time this week) */
            shape: typeof errBody.shape === 'string' ? errBody.shape : undefined,
        });
    }
}

/** POST /api/appointments/{id}/arrive */
export function markArrived(
    token: string,
    appointmentId: number,
    dockDoorId?: number,
): Promise<void> {
    return requestNoBody(`/appointments/${appointmentId}/arrive`, token, {
        method: 'POST',
        body: dockDoorId !== undefined ? JSON.stringify({ dockDoorId }) : undefined,
    });
}

/**
 * POST /api/appointments/{id}/depart
 *
 * ⛔ AN OUTBOUND LOAD MAY COME BACK 409 `OutboundReleaseBlocked` (#97). That is
 * not a failure — it means the server found warnings a named human has to
 * acknowledge before the truck leaves. The caller opens OutboundReleaseDialog,
 * which re-reads the check from the server, and calls this again with `override`.
 *
 * ⚠️ `override` IS NOT TRUSTED BY THE SERVER as a statement of what was wrong.
 * The departure path re-gathers and re-judges; the override only says which
 * codes the scheduler was shown and agreed to, and why.
 */
export function markDeparted(
    token: string, appointmentId: number, override?: ReleaseOverrideDto,
): Promise<void> {
    return requestNoBody(`/appointments/${appointmentId}/depart`, token, {
        method: 'POST',
        body: override ? JSON.stringify(override) : undefined,
    });
}

/**
 * GET /api/mgmt/appointments/{id}/release-check — may this outbound load leave?
 *
 * ⭐ READ-ONLY AND ADVISORY. The gate that matters is the departure itself; this
 * exists so the problem can be shown while the driver is still at the desk and a
 * licence can still be scanned.
 */
export function getReleaseCheck(token: string, appointmentId: number): Promise<ReleaseCheckDto> {
    return request(`/mgmt/appointments/${appointmentId}/release-check`, token);
}

/** PATCH /api/mgmt/capacity/{capacityId}/active — turn a limit band on/off. */
export function setCapacityActive(token: string, capacityId: number, isActive: boolean): Promise<void> {
    return requestNoBody(`/mgmt/capacity/${capacityId}/active`, token, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
    });
}

/** PATCH /api/mgmt/capacity/rule/{capacityId}/active — make the WHOLE rule (all its weekdays) active/inactive. */
export function setCapacityRuleActive(token: string, capacityId: number, isActive: boolean): Promise<void> {
    return requestNoBody(`/mgmt/capacity/rule/${capacityId}/active`, token, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
    });
}

/** DELETE /api/mgmt/capacity/rule/{capacityId} — delete the WHOLE rule (all its weekdays). */
export function deleteCapacityRule(token: string, capacityId: number): Promise<void> {
    return requestNoBody(`/mgmt/capacity/rule/${capacityId}`, token, { method: 'DELETE' });
}

/** PATCH /api/mgmt/capacity/rule/{capacityId}/times — move/resize the WHOLE rule to a new window. */
export function setCapacityRuleTimes(token: string, capacityId: number, startMinutes: number, endMinutes: number): Promise<void> {
    return requestNoBody(`/mgmt/capacity/rule/${capacityId}/times`, token, {
        method: 'PATCH',
        body: JSON.stringify({ startMinutes, endMinutes }),
    });
}

/** PATCH /api/mgmt/dedications/rule/{dedicationId}/active — make the WHOLE dedication rule active/inactive. */
export function setDedicationRuleActive(token: string, dedicationId: number, isActive: boolean): Promise<void> {
    return requestNoBody(`/mgmt/dedications/rule/${dedicationId}/active`, token, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
    });
}

/** DELETE /api/mgmt/dedications/rule/{dedicationId} — delete the WHOLE dedication rule. */
export function deleteDedicationRule(token: string, dedicationId: number): Promise<void> {
    return requestNoBody(`/mgmt/dedications/rule/${dedicationId}`, token, { method: 'DELETE' });
}

/** PATCH /api/mgmt/dedications/rule/{dedicationId}/times — move/resize the WHOLE dedication rule. */
export function setDedicationRuleTimes(token: string, dedicationId: number, startMinutes: number, endMinutes: number): Promise<void> {
    return requestNoBody(`/mgmt/dedications/rule/${dedicationId}/times`, token, {
        method: 'PATCH',
        body: JSON.stringify({ startMinutes, endMinutes }),
    });
}

/** POST /api/appointments/{id}/cancel */
export function cancelAppointment(
    token: string,
    appointmentId: number,
    reason?: string,
): Promise<void> {
    return requestNoBody(`/appointments/${appointmentId}/cancel`, token, {
        method: 'POST',
        body: reason ? JSON.stringify({ reason }) : undefined,
    });
}

/**
 * POST /api/appointments/{id}/no-show — REQUESTED/APPROVED → NOSHOW.
 *
 * Scheduler-gated. The truck never arrived. Server guards that the appointment
 * is currently REQUESTED or APPROVED (409 otherwise). Does NOT email the
 * carrier — notification is an opt-in follow-up via notifyAppointment('noshow').
 */
export function markNoShow(token: string, appointmentId: number): Promise<void> {
    return requestNoBody(`/appointments/${appointmentId}/no-show`, token, { method: 'POST' });
}

/**
 * POST /api/appointments/{id}/approve — REQUESTED → APPROVED.
 *
 * Scheduler-gated. Server transitions the appointment to APPROVED. It no longer
 * emails the carrier automatically — notification is now opt-in via
 * notifyAppointment('approved'), consistent with reschedule/cancel/no-show.
 * Server guards that the appointment is currently REQUESTED (InvalidTransition
 * via ApiCallError otherwise).
 */
export function approveAppointment(token: string, appointmentId: number): Promise<void> {
    return requestNoBody(`/appointments/${appointmentId}/approve`, token, { method: 'POST' });
}

/**
 * POST /api/appointments/{id}/reject — REQUESTED → CANCELLED, with reason.
 *
 * Scheduler-gated. A non-empty `reason` is REQUIRED (server returns 400 if
 * missing). Server transitions the appointment to CANCELLED (there is no
 * dedicated REJECTED status — the audit carries `rejected: true` + the reason)
 * and emails the carrier a decline notice including the reason. Same REQUESTED
 * guard as approve.
 */
export function rejectAppointment(
    token: string,
    appointmentId: number,
    reason: string,
): Promise<void> {
    return requestNoBody(`/appointments/${appointmentId}/reject`, token, {
        method: 'POST',
        body: JSON.stringify({ reason }),
    });
}

/**
 * PATCH /api/appointments/{id}/schedule — reschedule (and/or change door)
 *
 * Pass dockDoorId to change the door (or null to send back to the unassigned
 * queue), or omit it to keep the current door. start/end are required.
 *
 * Server returns 200 with the SAVED span on success, 409 with { conflicts } on
 * conflict.
 */
export interface RescheduleBody {
    dockDoorId?: number | null;
    scheduledStartUtc: string;
    scheduledEndUtc: string;
    reason?: string;
    overrideWarnings?: boolean;
}

/**
 * ⭐ WHAT THE SERVER ACTUALLY WROTE (#264).
 *
 * Pete, 2026-08-26: *"then after that, the appointment time is expanded from 30
 * minutes to 1 hour."* The chip is drawn from the day's single `slotMinutes`,
 * which `getCalendarDay` takes from the INBOUND window; the server promotes a
 * day-only truck using the rule for ITS OWN direction. On an outbound load those
 * two numbers differ, and the difference is the jump he sees.
 *
 * ⛔ NULL MEANS "THE SERVER DID NOT SAY", NOT "NOTHING CHANGED". A backend that
 * has not been deployed yet still answers 204, and the screen must behave exactly
 * as it does today in that case — correct itself on the reload rather than
 * treating a missing body as an error and rolling the drag back.
 */
export interface RescheduleResult {
    doorChanged: boolean;
    scheduledStartUtc: string;
    scheduledEndUtc: string;
    schedulingMode: 'TimeSlot';
}

export async function rescheduleAppointment(
    token: string,
    appointmentId: number,
    body: RescheduleBody,
): Promise<RescheduleResult | null> {
    const headers = new Headers();
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('Content-Type', 'application/json');
    const res = await fetch(`${API_BASE}/appointments/${appointmentId}/schedule`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        let errBody: Record<string, unknown> = {};
        try { errBody = await res.json(); } catch { /* ignore */ }
        throw new ApiCallError({
            status: res.status,
            error: typeof errBody.error === 'string' ? errBody.error : `HTTP ${res.status}`,
            conflicts: errBody.conflicts as ApiError['conflicts'],
            issues: errBody.issues,
            code: typeof errBody.code === 'string' ? errBody.code : undefined,
        });
    }
    // ⚠️ 204 FROM AN OLDER BACKEND, OR ANY BODY WE CANNOT READ, IS NOT A FAILURE.
    if (res.status === 204) return null;
    try {
        const data = await res.json() as Partial<RescheduleResult>;
        if (typeof data?.scheduledStartUtc !== 'string'
            || typeof data?.scheduledEndUtc !== 'string') return null;
        return {
            doorChanged: data.doorChanged === true,
            scheduledStartUtc: data.scheduledStartUtc,
            scheduledEndUtc: data.scheduledEndUtc,
            schedulingMode: 'TimeSlot',
        };
    } catch {
        return null;
    }
}

/**
 * POST /api/appointments/{id}/notify-reschedule — email the carrier the new
 * window after a scheduler reschedule. Scheduler-gated. Returns whether an
 * email was actually sent (`notified: false` if the appointment isn't
 * carrier-requested or has no carrier email on file). Call this only when the
 * scheduler opts to notify; the reschedule itself no longer emails.
 */
export function notifyReschedule(
    token: string,
    appointmentId: number,
): Promise<{ ok: boolean; notified: boolean }> {
    return request<{ ok: boolean; notified: boolean }>(
        `/appointments/${appointmentId}/notify-reschedule`,
        token,
        { method: 'POST' },
    );
}

/** Carrier-facing events that can trigger an opt-in notification email. */
/**
 * ⭐ `'updated'` ADDED 2026-09-18 (decision 186) — a details-only edit, i.e. one
 * that changed the carrier, PO, client or pallet count WITHOUT moving the
 * appointment. ⛔ MUST STAY IN STEP WITH `NotifyEvent` in
 * `src/functions/appointmentStatus.ts`; the server rejects an unknown event with
 * a 400 naming the whole allowlist, which is the only thing keeping these two
 * copies honest.
 *
 *   ** TWO LISTS THAT MUST AGREE FOREVER, AND THE SERVER IS THE ONE THAT DECIDES.
 *      THE 400 IS THE TEST. **
 */
export type NotifyEvent = 'created' | 'rescheduled' | 'approved' | 'cancelled' | 'noshow' | 'arrived' | 'departed' | 'updated';

/** Who a notify call is for. Omit for both (the server's default). */
export type NotifyTarget = 'carrier' | 'drivers';

export interface NotifyResultDto {
    ok: boolean;
    /** true if ANY recipient was notified — carrier or driver. */
    notified: boolean;
    carrierNotified?: boolean;
    driverNotified?: boolean;
    driverNotifyFailed?: boolean;
    /** Drivers actually texted. */
    driversTexted?: number;
    /** Drivers who have replied STOP. NOT a failure — the consent gate working. */
    driversOptedOut?: number;
    /** Drivers this event could have texted (0 when the event doesn't text
     *  drivers at all — approved / rescheduled / cancelled are the only three). */
    driversEligible?: number;
    smsEnabled?: boolean;
}

/**
 * POST /api/appointments/{id}/notify — opt-in notification for a status/time
 * event. Scheduler-gated. Composes the right message server-side for the given
 * event from the appointment's current details, emails the carrier and/or texts
 * the appointment's drivers, and returns what actually happened per audience.
 * Returns `notified: false` (not an error) when there's nobody to notify.
 *
 * ⚠️ `targets` OMITTED MEANS BOTH, server-side. Pass it explicitly whenever the
 * scheduler made a choice — the three-button prompt does — so a driver text
 * isn't sent by a caller that only meant to email the carrier.
 *
 * Driver texts go through Pipeline's engine, which owns the SMS consent gate, so
 * a driver who replied STOP comes back `driversOptedOut`, not a failure.
 */
export function notifyAppointment(
    token: string,
    appointmentId: number,
    event: NotifyEvent,
    targets?: NotifyTarget[],
): Promise<NotifyResultDto> {
    return request<NotifyResultDto>(
        `/appointments/${appointmentId}/notify`,
        token,
        { method: 'POST', body: JSON.stringify(targets ? { event, targets } : { event }) },
    );
}

/**
 * GET /api/appointments/{id}/events — full chronological event timeline
 * (created, status changes, reschedules, notifications sent). Scheduler-gated.
 */
export function getAppointmentEvents(
    token: string,
    appointmentId: number,
): Promise<AppointmentEventDto[]> {
    return request<{ events: AppointmentEventDto[] }>(
        `/appointments/${appointmentId}/events`,
        token,
        { method: 'GET' },
    ).then((r) => r.events);
}

/**
 * POST /api/appointments/{id}/edit — change content fields (carrier, client,
 * PO/consignees, direction, capabilities, comments) without changing times
 * or door. Server returns the updated appointment on success.
 */
export interface EditAppointmentBody {
    direction: 'INBOUND' | 'OUTBOUND' | 'BLOCK';
    carrierCode: string;
    carrierName?: string | null;
    clientCode: string;
    clientName?: string | null;
    documentType: string;
    documentNumber?: string | null;
    poNumber?: string | null;
    referenceNumber?: string | null;
    /** #130. Bookable; `trailerNumber` deliberately is not — see AppointmentDto. */
    containerNumber?: string | null;
    palletCount?: number | null;
    comments?: string | null;
    requiredCapabilityIds?: number[];
    consignees?: {
        consigneeCode: string;
        consigneeName?: string | null;
        shipmentNumber?: string | null;
        isPrimary?: boolean;
    }[];
}

export function editAppointment(
    token: string,
    appointmentId: number,
    body: EditAppointmentBody,
): Promise<AppointmentDto> {
    return request<AppointmentDto>(`/appointments/${appointmentId}/edit-details`, token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// ============================================================================
// /api/me
// ============================================================================

export interface MeResponse {
    email: string;
    displayName: string;
    isScheduler: boolean;
    isAdmin: boolean;
    facilityCodes: string[];
    defaultFacilityCode: string | null;
    /** F802 (Decision 467): the company in use, the person's DOCK companies (the picker) and, per company, which apps are open (the header tabs). */
    companyId?: number | null;
    companies?: Array<{ companyId: number; companyCode: string; companyName: string }>;
    suite?: null | { isSuperAdmin: boolean; companies: Array<{ companyId: number; companyCode: string; name: string; apps: Array<{ appCode: string; open: boolean }> }> };
}

export function getMe(token: string): Promise<MeResponse> {
    return request<MeResponse>('/me', token);
}

/**
 * F823 sign-in history: "this person is using the Dock (or Labor) now". The Dock passes it to the
 * Pipeline, which keeps one row per session; the answer is not used and a failure is ignored.
 */
export function markSeen(token: string, app: 'DOCK' | 'LABOR'): Promise<{ recorded: boolean }> {
    return request<{ recorded: boolean }>('/me/seen', token, { method: 'POST', body: JSON.stringify({ app }) });
}

/** PUT /api/me/default-facility?code=... — set (or clear with no code) the caller's default facility. */
export function setMyDefaultFacility(token: string, facilityCode: string | null): Promise<void> {
    const qs = facilityCode ? `?code=${encodeURIComponent(facilityCode)}` : '';
    return requestNoBody(`/me/default-facility${qs}`, token, { method: 'PUT' });
}

// ============================================================================
// Admin CRUD
// ============================================================================

// ---- Facility ----

export interface FacilityRow {
    FacilityCode: string;
    DisplayName: string;
    Timezone: string;
    Address: string | null;
    PipelineActive: boolean;
    WmsType: string;
    WmsConfigJson: string | null;
    DockActive: boolean;
    CreatedUtc: string;
    ModifiedUtc: string;
}

export interface UpsertFacilityBody {
    facilityCode: string;
    wmsType: 'Deposco' | 'Camelot' | 'Extensiv';
    wmsConfigJson?: string | null;
    isActive?: boolean;
}

export async function adminListFacilities(token: string): Promise<FacilityRow[]> {
    const res = await request<{ facilities: FacilityRow[] }>('/mgmt/facilities', token);
    return res.facilities;
}

export async function adminCreateFacility(token: string, body: UpsertFacilityBody): Promise<string> {
    const res = await request<{ facilityCode: string }>('/mgmt/facilities/create', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    return res.facilityCode;
}

export function adminUpdateFacility(token: string, code: string, body: UpsertFacilityBody): Promise<void> {
    return requestNoBody(`/mgmt/facilities/${code}/update`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

export function adminSoftDeleteFacility(token: string, code: string): Promise<void> {
    return requestNoBody(`/mgmt/facilities/${code}/disable`, token, { method: 'DELETE' });
}

export function adminRestoreFacility(token: string, code: string): Promise<void> {
    return requestNoBody(`/mgmt/facilities/${code}/restore`, token, { method: 'POST' });
}

// ---- DockDoor ----

export interface DoorAdminRow {
    DockDoorId: number;
    Code: string;
    Name: string;
    DoorGroup: string | null;
    DisplayOrder: number;
    MaxConcurrent: number;
    AllowedDirection: 'INBOUND' | 'OUTBOUND' | 'BOTH';
    IsActive: boolean;
    CapabilityIds: number[];
}

export interface UpsertDoorBody {
    facilityCode: string;
    code: string;
    name: string;
    doorGroup?: string | null;
    displayOrder: number;
    maxConcurrent: number;
    allowedDirection: 'INBOUND' | 'OUTBOUND' | 'BOTH';
    capabilityIds: number[];
    isActive?: boolean;
}

export async function adminListDoors(token: string, facilityCode: string): Promise<DoorAdminRow[]> {
    const params = new URLSearchParams({ facilityCode });
    const res = await request<{ doors: DoorAdminRow[] }>(`/mgmt/doors?${params}`, token);
    return res.doors;
}

export async function adminCreateDoor(token: string, body: UpsertDoorBody): Promise<number> {
    const res = await request<{ dockDoorId: number }>('/mgmt/doors/create', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    return res.dockDoorId;
}

export function adminUpdateDoor(token: string, id: number, body: UpsertDoorBody): Promise<void> {
    return requestNoBody(`/mgmt/doors/${id}/update`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

export function adminSoftDeleteDoor(token: string, id: number): Promise<void> {
    return requestNoBody(`/mgmt/doors/${id}/disable`, token, { method: 'DELETE' });
}

export function adminRestoreDoor(token: string, id: number): Promise<void> {
    return requestNoBody(`/mgmt/doors/${id}/restore`, token, { method: 'POST' });
}

// ---- Capability ----

export interface CapabilityAdminRow {
    CapabilityId: number;
    Code: string;
    Name: string;
    Description: string | null;
}

export interface UpsertCapabilityBody {
    code: string;
    name: string;
    description?: string | null;
}

export async function adminListCapabilities(token: string): Promise<CapabilityAdminRow[]> {
    const res = await request<{ capabilities: CapabilityAdminRow[] }>('/mgmt/capabilities', token);
    return res.capabilities;
}

export async function adminCreateCapability(token: string, body: UpsertCapabilityBody): Promise<number> {
    const res = await request<{ capabilityId: number }>('/mgmt/capabilities/create', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    return res.capabilityId;
}

export function adminUpdateCapability(token: string, id: number, body: UpsertCapabilityBody): Promise<void> {
    return requestNoBody(`/mgmt/capabilities/${id}/update`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

export function adminDeleteCapability(token: string, id: number): Promise<void> {
    return requestNoBody(`/mgmt/capabilities/${id}/delete`, token, { method: 'DELETE' });
}

// ---- Carrier ----

export interface CarrierAdminRow {
    CarrierId: number;
    CarrierCode: string;
    CarrierName: string;
    ContactEmail: string | null;
    ContactPhone: string | null;
    Notes: string | null;
    IsActive: boolean;
    CreatedUtc: string;
    ModifiedUtc: string;
}

export interface UpsertCarrierBody {
    carrierCode: string;
    carrierName: string;
    contactEmail?: string | null;
    contactPhone?: string | null;
    notes?: string | null;
}

export async function adminListCarriers(token: string): Promise<CarrierAdminRow[]> {
    const res = await request<{ carriers: CarrierAdminRow[] }>('/mgmt/carriers', token);
    return res.carriers;
}

export async function adminCreateCarrier(token: string, body: UpsertCarrierBody): Promise<number> {
    const res = await request<{ carrierId: number }>('/mgmt/carriers/create', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    return res.carrierId;
}

export function adminUpdateCarrier(token: string, id: number, body: UpsertCarrierBody): Promise<void> {
    return requestNoBody(`/mgmt/carriers/${id}/update`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

export function adminSoftDeleteCarrier(token: string, id: number): Promise<void> {
    return requestNoBody(`/mgmt/carriers/${id}/disable`, token, { method: 'DELETE' });
}

export function adminRestoreCarrier(token: string, id: number): Promise<void> {
    return requestNoBody(`/mgmt/carriers/${id}/restore`, token, { method: 'POST' });
}
// ============================================================================
// /api/users — admin user management (NEW)
// ============================================================================

export type AppRole = 'scheduler' | 'admin';

export interface AppUser {
    id: string;
    displayName: string;
    email: string;
    roles: AppRole[];
}

export interface DirectorySearchResult {
    id: string;
    displayName: string;
    email: string;
    existingRoles: AppRole[];
}

export interface ListUsersResponse {
    users: AppUser[];
}

export interface SearchUsersResponse {
    results: DirectorySearchResult[];
}

export interface AvailablePipelineFacility {
    FacilityCode: string;
    DisplayName: string;
    Timezone: string;
}

export async function adminListAvailableFacilities(token: string): Promise<AvailablePipelineFacility[]> {
    const res = await request<{ facilities: AvailablePipelineFacility[] }>('/mgmt/facilities/available', token);
    return res.facilities;
}

export function listUsers(token: string): Promise<ListUsersResponse> {
    return request<ListUsersResponse>('/users', token);
}

export function searchUsers(token: string, query: string): Promise<SearchUsersResponse> {
    const q = encodeURIComponent(query);
    return request<SearchUsersResponse>(`/users/search?q=${q}`, token);
}

/**
 * ---- ⛔⛤ SUPERSEDED 2026-09-21 (decision 207). §0.3 — STRUCK WHERE IT LIVES --
 *
 * ⛔⛤ ~~addUserRole(token, userId, role)  — POST, meaning ADD~~
 * ⛔⛤ ~~removeUserRole(token, userId, role) — DELETE /users/{id}/role/{role}~~
 *
 * ⛔⛔ THOSE WROTE ENTRA GROUPS. Company 1 flipped to `RoleTable` and the gate
 * stopped reading groups, so the screen kept working and granted nothing.
 *
 * ⚠️ AND THE *SHAPES* WERE WRONG TOO, not just the store behind them.
 * `UX_UserCompanyRole_Company_Subject_App` allows ONE DOCK row per person:
 *   - "add" became **set** — granting Admin to a Scheduler is an UPDATE.
 *   - DELETE lost `{role}` — removing "the admin role" and "the scheduler role"
 *     were never two operations against a single row.
 *   ** A PARAMETER THE STORE CANNOT HONOUR IS A PROMISE THE HANDLER HAS TO
 *      BREAK QUIETLY. **
 */

/**
 * Set this person's DOCK role. Creates, promotes, demotes or reactivates —
 * whichever the one row needs.
 *
 * ⚠️ `email` AND `displayName` ARE NOT COSMETIC. Tokens on this tenant carry no
 * email claim — `/api/me` returns `email: ""`, measured — so
 * `UserCompanyRole.Email`/`DisplayName` are the ONLY place a readable name for
 * this person will ever exist. ⛔ Omit them and every future grant reads
 * "(no name)" permanently, diffable against Entra by object id alone.
 * ⭐ The admin has the Graph search result on screen at the moment they click.
 *    This is the one moment those values are free.
 */
export async function setUserRole(
    token: string,
    userId: string,
    role: AppRole,
    who?: { email?: string; displayName?: string },
): Promise<void> {
    await request<void>(`/users/${encodeURIComponent(userId)}/role/${role}`, token, {
        method: 'POST',
        body: JSON.stringify({ email: who?.email ?? null, displayName: who?.displayName ?? null }),
        headers: { 'Content-Type': 'application/json' },
    });
}

/**
 * Remove this person's DOCK access. ⛔ No role segment — there is one row.
 * ⚠️ The server refuses self-removal with a 400 naming the reason: three people
 * hold rank 100, and with LegacyGroups deleted there is no group membership to
 * fall back on. *The last door out of a room must not be lockable from inside.*
 */
export async function removeUserAccess(token: string, userId: string): Promise<void> {
    await request<void>(`/users/${encodeURIComponent(userId)}`, token, {
        method: 'DELETE',
    });
}
// ============================================================================
// /api/mgmt/carrier-contacts — Phase 4 admin: manage carrier signups
// ============================================================================

export interface CarrierContactRow {
    carrierContactId: number;
    carrierContactGuid: string;
    /** Null for pending self-signup contacts not yet linked to a Carrier. */
    carrierId: number | null;
    carrierCode: string | null;
    carrierName: string | null;
    email: string;
    displayName: string | null;
    /** Dispatcher's own phone (SMS target for the reminder). Null until set
     *  via updateCarrierContactPhone — not captured at self-signup. */
    phone: string | null;
    isApproved: boolean;
    isActive: boolean;
    createdUtc: string;        // ISO
    approvedUtc: string | null;
    approvedByUserId: string | null;
    lastLoginUtc: string | null;
    modifiedUtc: string;
    /** ⭐ F212 (migration 244): Portal Users are carriers or clients. A client user is one
     *  client code of the scheduler's own company. */
    clientCompanyId?: number | null;
    clientCode?: string | null;
    clientName?: string | null;
    kind?: 'Carrier' | 'Client' | null;
}

export interface ListContactsResponse {
    contacts: CarrierContactRow[];
}

export interface SingleContactResponse {
    contact: CarrierContactRow;
    /** ⭐ F221: invite / approve emailed the person a welcome with a sign-in link */
    emailSent?: boolean;
}

/** ⭐ F212: exactly one of carrierId (a carrier user) or clientCode (a client user). */
export interface CreateContactBody {
    email: string;
    displayName?: string | null;
    carrierId?: number;
    clientCode?: string;
}

export interface ApproveContactBody {
    carrierId?: number;
    clientCode?: string;
}

export function listPendingCarrierContacts(token: string): Promise<ListContactsResponse> {
    return request<ListContactsResponse>('/mgmt/carrier-contacts/pending', token);
}

export function listApprovedCarrierContacts(token: string): Promise<ListContactsResponse> {
    return request<ListContactsResponse>('/mgmt/carrier-contacts/approved', token);
}

/** ⭐ F215: disabled portal users (carriers, pending, and this company's clients), to turn back on. */
export function listDisabledCarrierContacts(token: string): Promise<ListContactsResponse> {
    return request<ListContactsResponse>('/mgmt/carrier-contacts/disabled', token);
}

export function createCarrierContact(token: string, body: CreateContactBody): Promise<SingleContactResponse> {
    return request<SingleContactResponse>('/mgmt/carrier-contacts', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export function approveCarrierContact(token: string, id: number, body: ApproveContactBody): Promise<SingleContactResponse> {
    return request<SingleContactResponse>(`/mgmt/carrier-contacts/${id}/approve`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

/** Edit-in-place — phone only, for now. See mgmtUpdateCarrierContact. */
export function updateCarrierContactPhone(token: string, id: number, phone: string | null): Promise<void> {
    return requestNoBody(`/mgmt/carrier-contacts/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ phone }),
    });
}

export async function disableCarrierContact(token: string, id: number): Promise<void> {
    await request<void>(`/mgmt/carrier-contacts/${id}`, token, { method: 'DELETE' });
}

/** ⭐ F221: email an approved portal user a fresh welcome + sign-in link. */
export function sendCarrierContactLink(token: string, id: number): Promise<{ emailSent: boolean }> {
    return request<{ emailSent: boolean }>(`/mgmt/carrier-contacts/${id}/send-link`, token, { method: 'POST' });
}

export function reactivateCarrierContact(token: string, id: number): Promise<SingleContactResponse> {
    return request<SingleContactResponse>(`/mgmt/carrier-contacts/${id}/reactivate`, token, {
        method: 'POST',
    });
}
// ============================================================================
// /api/carrier-auth — carrier portal magic-link authentication
// ============================================================================
//
// Called from the public /carrier/login and /carrier/verify pages. These
// endpoints are NOT authenticated (they're how a carrier authenticates in
// the first place) — they don't use the request() helper that injects a
// Bearer token.

const API_BASE_FOR_PUBLIC = '/api';

export interface CarrierAuthRequestLinkResponse {
    ok: boolean;
    message: string;
}

export interface CarrierAuthVerifyTokenResponse {
    sessionToken: string;
    expiresAt: string;            // ISO
    displayName: string | null;
    carrierCode: string;
    email: string;
    /** ⭐ F212: which portal the link belongs to */
    kind?: 'Carrier' | 'Client' | null;
    clientCode?: string | null;
    clientName?: string | null;
    /** ⭐ F215: every account this email signs in to, each with its own session (the first is this one) */
    accounts?: PortalAccountDto[];
}

export interface PortalAccountDto {
    sessionToken: string;
    expiresAt: string;
    displayName: string | null;
    carrierCode: string;
    email: string;
    kind: 'Carrier' | 'Client';
    clientCode: string | null;
    clientName: string | null;
}

/**
 * Request a magic sign-in link to be emailed. Always returns 200 with a
 * generic message regardless of whether the email exists in the system
 * (backend deliberately doesn't leak account existence).
 */
export async function carrierAuthRequestLink(email: string): Promise<CarrierAuthRequestLinkResponse> {
    const res = await fetch(`${API_BASE_FOR_PUBLIC}/carrier-auth/request-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    });
    if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
            const body = await res.json();
            if (body?.error) detail = body.error;
        } catch { /* ignore */ }
        throw new Error(detail);
    }
    return (await res.json()) as CarrierAuthRequestLinkResponse;
}

/**
 * Exchange a magic-link token for a session JWT. Returns the JWT plus the
 * carrier metadata that the dashboard will need.
 *
 * On 401 the message tells us which case (not-found / expired / already-used)
 * and the UI surfaces it directly.
 */
export async function carrierAuthVerifyToken(token: string): Promise<CarrierAuthVerifyTokenResponse> {
    const res = await fetch(`${API_BASE_FOR_PUBLIC}/carrier-auth/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
    });
    if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
            const body = await res.json();
            if (body?.error) detail = body.error;
        } catch { /* ignore */ }
        throw new Error(detail);
    }
    return (await res.json()) as CarrierAuthVerifyTokenResponse;
}

/**
 * Sign out (currently a no-op server-side; carrier just discards their JWT).
 * Called best-effort — we don't fail the UX if the network call fails.
 */
export async function carrierAuthLogout(sessionToken: string): Promise<void> {
    try {
        await fetch(`${API_BASE_FOR_PUBLIC}/carrier-auth/logout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${sessionToken}`,
            },
        });
    } catch {
        // Ignore — server-side it's a no-op anyway, no harm in skipping.
    }
}

// ============================================================================
// Reactivation Requests (Phase 5b)
// ============================================================================

export interface ReactivationRequestRow {
    carrierContactId: number;
    email: string;
    displayName: string | null;
    carrierCode: string | null;
    carrierName: string | null;
    contactIsActive: boolean;
    isBlocked: boolean;
    attemptCount: number;
    firstAttemptUtc: string;       // ISO
    lastAttemptUtc: string;        // ISO
    distinctIps: string[];
}

export interface ListReactivationRequestsResponse {
    requests: ReactivationRequestRow[];
}

export function listReactivationRequests(token: string): Promise<ListReactivationRequestsResponse> {
    return request<ListReactivationRequestsResponse>('/mgmt/carrier-contacts/reactivation-requests', token);
}

export async function reactivateFromRequest(token: string, contactId: number): Promise<void> {
    await request<void>(`/mgmt/carrier-contacts/${contactId}/reactivate-from-request`, token, {
        method: 'POST',
    });
}

export async function dismissReactivationRequest(token: string, contactId: number): Promise<void> {
    await request<void>(`/mgmt/carrier-contacts/${contactId}/dismiss-request`, token, {
        method: 'POST',
    });
}

export async function blockFromRequest(token: string, contactId: number, reason?: string): Promise<void> {
    await request<void>(`/mgmt/carrier-contacts/${contactId}/block`, token, {
        method: 'POST',
        body: reason ? JSON.stringify({ reason }) : undefined,
    });
}

// ── Chat groups (admin) ─────────────────────────────────────────────────────

export interface ChatGroupMember {
    EntraObjectId: string;
    DisplayName: string | null;
    Email: string | null;
    CreatedUtc: string;
}

export async function adminListChatGroup(token: string, facilityCode: string): Promise<ChatGroupMember[]> {
    const res = await request<{ members: ChatGroupMember[] }>(`/mgmt/chat-groups/${encodeURIComponent(facilityCode)}`, token);
    return res.members;
}

export function adminAddChatGroupMember(
    token: string,
    facilityCode: string,
    member: { entraObjectId: string; displayName: string; email: string },
): Promise<void> {
    return requestNoBody(`/mgmt/chat-groups/${encodeURIComponent(facilityCode)}`, token, {
        method: 'POST',
        body: JSON.stringify(member),
    });
}

export function adminRemoveChatGroupMember(token: string, facilityCode: string, entraObjectId: string): Promise<void> {
    return requestNoBody(`/mgmt/chat-groups/${encodeURIComponent(facilityCode)}/${encodeURIComponent(entraObjectId)}`, token, {
        method: 'DELETE',
    });
}

// ── Chat threads (carrier + scheduler) ──────────────────────────────────────

export interface ChatConnectionBundle {
    acsThreadId: string;
    chatThreadId?: number;
    acsUserId: string;
    token: string;
    expiresOn: string;
    endpoint: string;
}

export interface ChatThreadSummary {
    ChatThreadId: number;
    AcsThreadId: string;
    ScopeType: 'APPOINTMENT' | 'GENERAL';
    CarrierCode?: string | null;
    /** ⭐ F223: set on a client's thread (CarrierCode is then null) */
    ClientCode?: string | null;
    FacilityCode: string;
    AppointmentId: number | null;
    LastMessageUtc: string | null;
}

// Carrier: open (or get) a thread for a facility / appointment.
export function carrierOpenThread(
    token: string,
    body: { facilityCode: string; scopeType: 'APPOINTMENT' | 'GENERAL'; appointmentId?: number | null },
): Promise<ChatConnectionBundle> {
    return request<ChatConnectionBundle>('/chat/threads/open', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// List threads (works for both carrier and scheduler — backend infers role).
export function listChatThreads(token: string): Promise<{ threads: ChatThreadSummary[] }> {
    return request<{ threads: ChatThreadSummary[] }>('/chat/threads', token);
}

// Scheduler: get an ACS token to join a specific thread.
export function schedulerGetThreadToken(token: string, chatThreadId: number): Promise<ChatConnectionBundle> {
    return request<ChatConnectionBundle>(`/chat/threads/${chatThreadId}/token`, token, { method: 'POST' });
}

// ============================================================================
// Driver assignment (scheduler side) — /api/appointments/{id}/drivers
// An appointment can have several drivers (multi-truck pickup, or an LTL
// appointment booked with a longer window) — see migration 134. Shared body
// shape with the carrier portal's own driver endpoint (different auth).
// ============================================================================

export type AddDriverBody =
    | { driverId: number }
    | { name: string; phone: string; email?: string | null };

export interface AppointmentDriverResult {
    appointmentDriverId: number;
    driverId: number | null;
    name: string;
    phone: string;
}

export function addAppointmentDriver(
    token: string,
    appointmentId: number,
    body: AddDriverBody,
): Promise<AppointmentDriverResult> {
    return request<AppointmentDriverResult>(`/appointments/${appointmentId}/drivers`, token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export function removeAppointmentDriver(
    token: string,
    appointmentId: number,
    appointmentDriverId: number,
): Promise<void> {
    return requestNoBody(`/appointments/${appointmentId}/drivers/${appointmentDriverId}`, token, {
        method: 'DELETE',
    });
}

// ============================================================================
// /api/mgmt/carrier-drivers — scheduler admin CRUD for the driver roster.
// Requires EntraGroupSchedulers (not full Admin) — see admin.ts assertScheduler.
// ============================================================================

export interface CarrierDriverAdminRow {
    driverId: number;
    carrierCode: string;
    name: string;
    phone: string;
    email: string | null;
    isActive: boolean;
    createdUtc: string;
}

export interface UpsertCarrierDriverBody {
    name: string;
    phone: string;
    email?: string | null;
}

export async function adminListCarrierDrivers(token: string, carrierCode: string): Promise<CarrierDriverAdminRow[]> {
    const res = await request<{ drivers: CarrierDriverAdminRow[] }>(
        `/mgmt/carrier-drivers?carrierCode=${encodeURIComponent(carrierCode)}`,
        token,
    );
    return res.drivers;
}

export async function adminCreateCarrierDriver(
    token: string,
    body: UpsertCarrierDriverBody & { carrierCode: string },
): Promise<number> {
    const res = await request<{ driverId: number }>('/mgmt/carrier-drivers/create', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    return res.driverId;
}

export function adminUpdateCarrierDriver(token: string, id: number, body: UpsertCarrierDriverBody): Promise<void> {
    return requestNoBody(`/mgmt/carrier-drivers/${id}/update`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

export function adminDisableCarrierDriver(token: string, id: number): Promise<void> {
    return requestNoBody(`/mgmt/carrier-drivers/${id}/disable`, token, { method: 'DELETE' });
}

export function adminRestoreCarrierDriver(token: string, id: number): Promise<void> {
    return requestNoBody(`/mgmt/carrier-drivers/${id}/restore`, token, { method: 'POST' });
}

// ============================================================================
// DOCK WORKER BOARD (kiosk) — public, IP-gated
// ============================================================================
//
// The wall board has no signed-in user: the backend authorizes the request by
// the caller's facility-network IP and derives WHICH facility from it, so this
// call sends no facility id and no token. Same public-fetch pattern as the
// carrier-auth calls above (deliberately not the Bearer-injecting request()).

/**
 * ⭐ F263 — THE WAREHOUSE TAB, SIGNED IN. Inside the Scheduler the board is read with
 * the person's own token for a facility they hold (GET mgmt/board), not by the
 * device's network address — James Grieco off-site got "This device is not
 * registered to a facility." The wall TVs keep `getDockBoardData` below.
 * A 409 CHOOSE_FACILITY means no facility was chosen and the person has several.
 */
export function getDockBoardSignedIn(
    token: string,
    cfgKey: string | null,
    facilityCode: string | null,
    signal?: AbortSignal,
): Promise<DockBoardDto> {
    const qs = new URLSearchParams();
    if (facilityCode) qs.set('facility', facilityCode);
    if (cfgKey) qs.set('cfg', cfgKey);
    const q = qs.toString();
    return request<DockBoardDto>(`/mgmt/board${q ? `?${q}` : ''}`, token, { signal, headers: { 'Cache-Control': 'no-cache' } });
}

/** Fetch the board projection for whichever facility this device's IP maps to. */
export async function getDockBoardData(
    cfgKey?: string | null,
    signal?: AbortSignal,
): Promise<DockBoardDto> {
    // ?cfg= names the screen's saved config (migration 136). Omitted → the
    // facility's default config → the shipped defaults.
    const qs = cfgKey ? `?cfg=${encodeURIComponent(cfgKey)}` : '';
    const res = await fetch(`${API_BASE_FOR_PUBLIC}/kiosk/board${qs}`, {
        headers: { 'Cache-Control': 'no-cache' },
        signal,
    });
    if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
            const body = await res.json();
            if (body?.error) detail = body.error;
        } catch { /* ignore */ }
        throw new Error(detail);
    }
    return (await res.json()) as DockBoardDto;
}


// ============================================================================
// STATUS PALETTE (dock.StatusColor) — public read
// ============================================================================
//
// Public on purpose: every surface needs the palette before it can paint,
// including the anonymous IP-gated board and the magic-link carrier portal.
// Six hex codes, no business data — see src/functions/statusColors.ts.

export interface StatusColorDto {
    statusCode: string;
    displayName: string;
    schedulerHex: string | null;
    boardHex: string | null;
    isBoardOnly: boolean;
    sortOrder: number;
}

export async function getStatusColors(): Promise<{ colors: StatusColorDto[] }> {
    const res = await fetch(`${API_BASE_FOR_PUBLIC}/status-colors`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as { colors: StatusColorDto[] };
}

/** Admin: retune one status. Both hexes are '#rrggbb' or null (not shown). */
// ---- dockview board configs (admin; migration 136) ------------------------

// Re-exported so admin components import their types from one place, the same
// way StatusColorDto is declared here.
export type { BoardConfigDto } from './types';

/**
 * What the admin screen SENDS. Deliberately not `Omit<BoardConfigDto, ...>`:
 * the DTO allows a null facility and null key because the board's shipped
 * defaults are expressed in the same shape, and a form that is about to POST a
 * row must have both. Same fields, stricter contract.
 */
export interface BoardConfigPayload {
    facilityCode: string;
    configKey: string;
    displayName: string;
    isDefault: boolean;
    isActive: boolean;
    doorsPerPage: number;
    rotateSeconds: number;
    refreshSeconds: number;
    showHolding: boolean;
    showOpenDoors: boolean;
    showReservedCards: boolean;
    textScale: BoardConfigDto['textScale'];
    standDownMinutes: number;
    doorGroup: string | null;
}

export async function getBoardConfigs(token: string): Promise<{ configs: BoardConfigDto[] }> {
    return request('/mgmt/board-configs', token);
}

export async function createBoardConfig(
    token: string, body: BoardConfigPayload,
): Promise<{ configs: BoardConfigDto[] }> {
    return request('/mgmt/board-configs/create', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function updateBoardConfig(
    token: string, id: number, body: BoardConfigPayload,
): Promise<{ configs: BoardConfigDto[] }> {
    return request(`/mgmt/board-configs/${id}/update`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

export async function deleteBoardConfig(
    token: string, id: number,
): Promise<{ configs: BoardConfigDto[] }> {
    return request(`/mgmt/board-configs/${id}/delete`, token, { method: 'DELETE' });
}

// ---- driver check-in configs (admin; migration 140) ------------------------

export type { CheckInConfigDto } from './types';

/**
 * What the admin screen SENDS. Deliberately not Omit<CheckInConfigDto, ...>:
 * the DTO allows a null facility and null key because the shipped defaults are
 * expressed in the same shape, and a form about to POST a row must have both.
 *
 * foodSafetyText is nullable HERE and not on the DTO, on purpose: null means
 * "use the shipped wording", and that is a thing you can send but never a thing
 * you receive — the API resolves it before it reaches the screen.
 */
export interface CheckInConfigPayload {
    facilityCode: string;
    configKey: string;
    displayName: string;
    isDefault: boolean;
    isActive: boolean;
    matchSignalOrder: string;
    confidentSignals: string;
    phoneMinDigits: number;
    phoneMatchDigits: number;
    multiMatchBehavior: CheckInConfigDto['multiMatchBehavior'];
    collectContainerNumber: boolean;
    collectTrailerNumber: boolean;
    /**
     * ---- ⛔⛤ THE TENTH LAYER (2026-09-23) --------------------------------
     *
     * `CollectPalletCount` was threaded through NINE places and a comment in
     * `checkInConfigRepository.ts` counts them out loud, under the rule:
     *
     *   ** A FLAG THAT STOPS AT ANY ONE LAYER IS A FEATURE THAT LOOKS SWITCHED
     *      OFF, NOT A BUG THAT LOOKS LIKE ONE. **
     *
     * ⛔⛔ AND THE COUNT ITSELF WAS THE MISTAKE. Nine were the SERVER's layers
     * plus the admin form's draft and loader — the write PAYLOAD between them was
     * never counted, so it was never checked. `tsc` found it in three errors.
     *   ** A LIST OF PLACES TO CHECK IS ITSELF A PLACE THAT CAN BE INCOMPLETE,
     *      AND ENUMERATING THEM FEELS LIKE VERIFYING THEM. **
     *
     * ✅ The compiler caught it because the admin draft is typed against this
     * interface. That is the argument for these payload types existing at all:
     * the one layer nobody counted is the one that refused to compile.
     */
    collectPalletCount: boolean;
    collectPoNumber: boolean;
    collectLanguage: boolean;
    requireFoodSafetyAck: boolean;
    foodSafetyWordingVer: string;
    foodSafetyText: string | null;
    dlCaptureMode: CheckInConfigDto['dlCaptureMode'];
    dlRetentionDays: number | null;
    autoArriveOnConfident: boolean;
    requireDoorOnArrive: boolean;
    autoReleaseDoorOnDeparted: boolean;
    walkInCreatesRequest: boolean;
    maxCheckInsPerMinute: number;
    duplicateWindowMinutes: number;
    /** ⭐ #309 / migration 168. 1-7; 2 = today and yesterday. Optional so an
     *  older server payload still renders the form. */
    kioskTruckDays?: number;
    /** ⭐ Migration 218 — the floor board's door-idle alert threshold. 0 = off. */
    doorIdleAlertMinutes?: number;
    idleResetSeconds: number;
    /**
     * ⭐ #73 / migration 191 — PER-SCREEN OVERRIDES OF `idleResetSeconds`.
     *
     * ⛔ NULL MEANS "USE THE GENERAL ONE", and that is not the same as absent:
     * absent leaves the stored value alone, null clears an override back to the
     * baseline. The admin screen sends null when the box is emptied.
     *   ** AN OVERRIDE THAT DEFAULTS TO THE GENERAL VALUE COSTS NOTHING WHEN UNSET
     *      AND EXPLAINS ITSELF WHEN SET. **
     *
     * ⚠️ `doneResetSeconds` has a FLOOR OF 3, not 10 — it is the confirmation
     * screen, where five seconds is already the sensible answer.
     */
    idleVehicleSeconds?: number | null;
    idleSafetySeconds?: number | null;
    doneResetSeconds?: number | null;
}

export async function getCheckInConfigs(token: string): Promise<{ configs: CheckInConfigDto[] }> {
    return request('/mgmt/checkin-configs', token);
}

export async function createCheckInConfig(
    token: string, body: CheckInConfigPayload,
): Promise<{ configs: CheckInConfigDto[] }> {
    return request('/mgmt/checkin-configs/create', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function updateCheckInConfig(
    token: string, id: number, body: CheckInConfigPayload,
): Promise<{ configs: CheckInConfigDto[] }> {
    return request(`/mgmt/checkin-configs/${id}/update`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

export async function deleteCheckInConfig(
    token: string, id: number,
): Promise<{ configs: CheckInConfigDto[] }> {
    return request(`/mgmt/checkin-configs/${id}/delete`, token, { method: 'DELETE' });
}

// ---- driver check-in kiosk (PUBLIC — IP-gated, no token) ------------------
//
// ⚠️ These two are deliberately token-free, like the dock board. The tablet has
// no user to sign in as; the gate is the facility's egress IP, checked
// server-side. See src/functions/driverCheckIn.ts.

/* ⚠️ `collectPalletCount` IS OPTIONAL HERE AND ONLY HERE. The server sends it
   from migration 192 onward; a tablet talking to an older backend gets nothing,
   and `?? false` at the call site reads that as "do not ask" — the safe way
   round, because a screen appearing mid-deploy is worse than one appearing late.
     ** AN OPTIONAL FIELD IS A PLACE FOR A DROPPED VALUE TO HIDE, SO IT EARNS ITS
        `?` ONLY WHERE ABSENCE IS A REAL STATE. HERE IT IS: AN OLDER SERVER. ** */
export interface KioskCheckInConfig {
    collectContainerNumber: boolean;
    collectTrailerNumber: boolean;
    /* ⛔ THIS ONE FLAG GOVERNS THE WHOLE PAPERWORK SCREEN — PO, BOL and
       reference alike. See src/lib/paperworkNumber.ts for why there are not
       three of them. */
    collectPoNumber: boolean;
    collectPalletCount?: boolean;
    collectLanguage: boolean;
    requireFoodSafetyAck: boolean;
    foodSafetyWordingVer: string;
    foodSafetyText: string;
    /**
     * ⭐ The texting notice shown at the phone step (2026-08-27).
     * ⛔ A NOTICE, NOT A RECORDED CONSENT — nothing writes dbo.SmsConsent from a
     * check-in yet; that is #323. ⚠️ Optional so an older server still renders.
     */
    smsNoticeText?: string;
    smsNoticeVersion?: string;
    dlCaptureMode: 'off' | 'optional' | 'required';
    idleResetSeconds: number;
    /**
     * ---- ⭐⭐ A TIMEOUT PER SCREEN (#73, migration 191) -------------------
     *
     * ⛔ ALREADY RESOLVED BY THE SERVER. `toKioskView` applies the `??` against
     * `idleResetSeconds` before sending, so these arrive as numbers and the page
     * does no fallback of its own. Sending the raw nullable overrides would make
     * every screen repeat the same rule, and one of them would get it wrong —
     * which is a TIMEOUT, so it would fail silently and only at a gate.
     *
     * ⚠️ OPTIONAL, for the same reason `smsNoticeText` is: an older server does
     * not send them and the tablet must still render. The page falls back to
     * `idleResetSeconds`, which is exactly the behaviour before 191.
     */
    idleVehicleSeconds?: number;
    idleSafetySeconds?: number;
    doneResetSeconds?: number;
    /**
     * ⭐ HOW SHORT A TRAILER / CONTAINER / PLATE NUMBER MAY BE (#73).
     *
     * ⛔ THE TABLET HOLDS NO RULE OF ITS OWN. `web/` is a separate tsconfig
     * project so it cannot import `lib/vehicleNumber.ts`, and a copy of the
     * number here would be a second definition that drifts.
     * ⚠️ ABSENT MEANS NO CLIENT-SIDE MINIMUM — the button enables and the server
     * answers. Inventing a number here would be the drift this avoids.
     *
     * ⛔⛔ THE BYPASS CODE IS DELIBERATELY NOT SENT. This payload is
     * unauthenticated by design; the driver never needs the code, and it
     * satisfies this length on its own so the tablet treats it as any other
     * value.
     *   ** THE TABLET KNOWS A LENGTH. THE SERVER KNOWS A MEANING. **
     */
    /* ---- ⛔⛤ A MAP SINCE 2026-09-24 ------------------------------------
       Trailer 1, container 11, plate 4. Symbia runs one- and two-digit trailers,
       so a single floor of 3 was refusing real numbers.
       ⚠️ THE TABLET GATES ON THE SMALLEST FLOOR ONLY — see DriverCheckIn.tsx.
       Enforcing 11 on the container box here would grey out Next on the bypass
       code, silently, with the server never consulted and nothing to read.
         ** A CLIENT-SIDE COPY OF A SERVER RULE ALSO COPIES EVERY EXCEPTION THE
            SERVER KNOWS ABOUT, AND IT KNOWS NONE OF THEM. ** */
    vehicleNumberMinLengths?: { trailerNumber: number; containerNumber: number; trailerPlate: number };
    /* ⭐ THE PAPERWORK BOXES' OWN FLOOR. They used to borrow the vehicle number's
       — both read `KIOSK_NUMBER_MIN_LENGTH` from `src/lib/kioskCodes.ts`, which is
       real and still owns the paperwork floor. The vehicle floors moved out of it
       on 2026-09-24; the paperwork floor did not. ⚠️ Tablet-side only: no server path refuses a short PO. */
    paperworkMinLength?: number;
}

/**
 * ⭐ WHAT THE GATE ACTUALLY STORED for this arrival (#73, 2026-09-23).
 *
 * ⛔ NULL OR ABSENT MEANS SAY NOTHING — the site does not ask, the driver typed
 * the bypass code, or he gave nothing. ⚠️ Absent also covers an OLD SERVER that
 * does not send this yet, and "say nothing" is the right behaviour there too.
 */
export interface KioskVehicleRecorded {
    kind: 'container' | 'trailer' | 'plate';
    value: string;
}

export interface KioskCheckInResult {
    status: 'checked-in' | 'already-checked-in';
    arrivalNumber?: number | null;
    truckNumber?: number | null;
    matched?: boolean;
    needsOffice?: boolean;
    minutesAgo?: number;
    /**
     * ⛔ THE SERVER'S ANSWER, NOT THE TABLET'S. The kiosk is never told the bypass
     * code, so it cannot tell a real number from the "I do not have it" sentinel —
     * and a screen that echoed what was typed would confirm "Container 123" to a
     * driver who told us he had nothing.
     *   ** THE SCREEN SHOULD CONFIRM WHAT WAS STORED, NOT WHAT WAS TYPED. **
     */
    vehicleRecorded?: KioskVehicleRecorded | null;
}

export async function getKioskCheckInConfig(
    cfgKey?: string | null,
): Promise<{ facilityCode: string; config: KioskCheckInConfig }> {
    const qs = cfgKey ? `?cfg=${encodeURIComponent(cfgKey)}` : '';
    const res = await fetch(`${API_BASE_FOR_PUBLIC}/kiosk/checkin/config${qs}`);
    if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`) as Error & { status?: number };
        err.status = res.status;
        throw err;
    }
    return res.json();
}

export interface KioskCheckInBody {
    driverName?: string;
    driverPhone?: string;
    carrierCode?: string;
    /** Only sent for a walk-up — a matched appointment supplies its own. */
    direction?: 'INBOUND' | 'OUTBOUND';
    /** Pallets on the truck. 0 is a real answer; omitted means not answered. */
    palletCount?: number;
    poNumber?: string;
    /**
     * ⭐ THE BOL, NEW 2026-09-23. `dock.Appointment` has carried `BolNumber` for
     * a while; `dock.DriverCheckIn` gains it in migration 192, because the driver
     * can now say which of the three his number is instead of the server guessing
     * from its shape.
     */
    bolNumber?: string;
    referenceNumber?: string;
    containerNumber?: string;
    trailerNumber?: string;
    /** ⭐ #146/#168 — the trailer's registration plate. Different fact from the number. */
    trailerPlate?: string;
    language?: string;
    foodSafetyAck?: boolean;
    foodSafetyWordingVer?: string;
    /** The appointment the driver picked from the lookup list, if he picked one. */
    appointmentId?: string;
}

export async function postKioskCheckIn(
    body: KioskCheckInBody, cfgKey?: string | null,
): Promise<KioskCheckInResult> {
    const qs = cfgKey ? `?cfg=${encodeURIComponent(cfgKey)}` : '';
    const res = await fetch(`${API_BASE_FOR_PUBLIC}/kiosk/checkin${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(payload?.error || `HTTP ${res.status}`) as Error & {
            status?: number; reload?: boolean;
        };
        err.status = res.status;
        err.reload = payload?.reload === true;
        throw err;
    }
    return payload as KioskCheckInResult;
}

/** One appointment the kiosk offers back to a driver. */
export interface KioskLookupMatch {
    appointmentId: string;
    appointmentNumber: string;
    scheduledStartUtc: string;
    /** Already formatted in the FACILITY's timezone by the server — never reformat it. */
    scheduledLocal: string;
    doorCode: string | null;
    clientCode: string | null;
    carrierCode: string | null;
    direction: string | null;
    driverName: string | null;
    poNumber: string | null;
    referenceNumber: string | null;
    containerNumber: string | null;
}

export interface KioskLookupResult {
    count: number;
    method: string | null;
    matches: KioskLookupMatch[];
}

/**
 * Ask what this driver appears to have here today. READ ONLY — nothing is
 * written, so a driver can abandon the kiosk at this point and nothing happened.
 *
 * ⚠️ NEVER THROWS. A lookup that fails must not stop a driver checking in; the
 * caller treats an error exactly like "nothing found" and collects details as
 * before. The server fails soft for the same reason.
 */
export async function postKioskLookup(
    body: { driverPhone?: string; poNumber?: string; referenceNumber?: string; containerNumber?: string },
    cfgKey?: string | null,
): Promise<KioskLookupResult> {
    const qs = cfgKey ? `?cfg=${encodeURIComponent(cfgKey)}` : '';
    try {
        const res = await fetch(`${API_BASE_FOR_PUBLIC}/kiosk/checkin/lookup${qs}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) return { count: 0, method: null, matches: [] };
        const payload = await res.json().catch(() => null);
        if (!payload || !Array.isArray(payload.matches)) return { count: 0, method: null, matches: [] };
        return payload as KioskLookupResult;
    } catch {
        return { count: 0, method: null, matches: [] };
    }
}

/**
 * Correct a check-in. Only the keys you SEND are changed — an absent key means
 * "leave it alone", an explicit null means "clear it".
 *
 * ⛔ The arrival number and truck number are not editable and never will be:
 * they are derived from check-in order, so a typed-over number means two trucks
 * claiming the same one. Void and re-check-in renumbers correctly.
 */
export async function editCheckIn(
    token: string, checkInId: string,
    changes: {
        driverName?: string | null; driverPhone?: string | null; carrierCode?: string | null;
        /* ⛔⛤ `bolNumber` ADDED 2026-09-23, for exactly the reason the note below
           gives about `trailerPlate`: the field existed everywhere except in a
           signature, so no screen could send one. This one was worse — the ROUTE
           could not name it either, so the value was write-once from the kiosk. */
        poNumber?: string | null; bolNumber?: string | null; referenceNumber?: string | null;
        containerNumber?: string | null; trailerNumber?: string | null;
        /**
         * ⛔⚤ ~~ABSENT~~ — ADDED 2026-09-23. The ROUTE has accepted `trailerPlate`
         * since #146 (`EditRequest`, and `COLS` in the repository writes it), and
         * this signature simply never listed it, so no screen could send one: the
         * plate was writable by the server and unreachable from the application.
         *   ** A FIELD THE SERVER ACCEPTS AND THE CLIENT CANNOT NAME IS A FIELD
         *      THAT DOES NOT EXIST. **
         *
         * ⚠️ 20 characters at the column, the zod and the input's `maxLength`.
         *
         * ⛔ THE SERVER REFUSES THE BYPASS CODE AND ANYTHING SHORTER THAN THE
         * MINIMUM on all three of these — see `badVehicleValue` in
         * `functions/driverCheckIn.ts`. Do NOT re-implement either rule here: the
         * kiosk is deliberately never told the code, and a copy on the desk screen
         * would be a second definition to keep in step.
         *   ** THE TABLET KNOWS A LENGTH. THE SERVER KNOWS A MEANING. **
         */
        trailerPlate?: string | null;
    },
): Promise<{ status: string }> {
    return request(`/mgmt/checkins/${checkInId}/edit`, token, {
        method: 'POST',
        body: JSON.stringify(changes),
    });
}

// ---- driver check-in arrivals (schedulers; migrations 139/141) -------------

export type { CheckInDto, VoidedCheckInDto, SuggestLoadDto, OcrCandidateDto, OcrMatchDto } from './types';

export async function getCheckIns(
    token: string, facilityCode: string, date?: string, includeVoided = false,
): Promise<{ facilityCode: string; date: string; checkIns: CheckInDto[]; voided?: VoidedCheckInDto[];
             /** ⭐ F103 — null when the settings could not be read (no colouring). */
             detention?: DetentionConfigDto | null }> {
    const qs = new URLSearchParams({ facilityCode });
    if (date) qs.set('date', date);
    /**
     * ⭐ OPT-IN, BECAUSE IT COSTS A SECOND QUERY. Voided arrivals cannot come out
     * of the sequence view - it excludes them so that voiding a mistake does not
     * renumber every live truck on the board - so the server runs a separate
     * statement for them. The panel asks only when the section is open.
     */
    if (includeVoided) qs.set('voided', '1');
    return request(`/mgmt/checkins?${qs.toString()}`, token);
}

/** Attach an unmatched arrival to an appointment. Only ever fills a blank. */
export async function attachCheckIn(
    token: string, checkInId: string, appointmentId: string,
    /**
     * ⭐ #300 — only ever true after a person answered the overwrite prompt. See the
     * server's note on why this is a separate flag from `confirmed`.
     */
    replaceBookedDriver = false,
    /**
     * ⭐⭐ #73 — MAY THE TRUCK'S CONTAINER / TRAILER NUMBER REPLACE THE BOOKING'S?
     *
     * ⛔⛔ NO DEFAULT, AND THAT IS THE WHOLE DESIGN. Three instructions:
     *   `undefined` — nobody has been asked; a disagreement comes back as a 409
     *                  and NOTHING is written.
     *   `true`      — a person said yes.
     *   `false`     — a person said no; the attach GOES AHEAD and the booking keeps
     *                  its numbers.
     *
     * ⚠️ Giving this `= false` like `replaceBookedDriver` above would say "a person
     * declined" on the very first call and the prompt could never appear.
     *   ** A DEFAULT ON A TRI-STATE ANSWERS THE QUESTION BEFORE IT IS ASKED. **
     */
    overwriteVehicleNumbers?: boolean,
    overwritePaperworkNumbers?: boolean,
): Promise<{ status: string }> {
    return request(`/mgmt/checkins/${checkInId}/attach`, token, {
        method: 'POST',
        /* ⛔ THE KEY IS OMITTED WHEN UNANSWERED, not sent as null. `JSON.stringify`
           drops an undefined value, which is exactly right here: the route's zod
           reads an ABSENT key as "not asked", and an explicit null would fail it. */
        body: JSON.stringify({
            appointmentId, replaceBookedDriver,
            overwriteVehicleNumbers, overwritePaperworkNumbers,
        }),
    });
}

/**
 * ⭐⭐ IS THIS FAILURE THE "the load says another box" ONE? (#73, 2026-09-23)
 *
 * ⛔ READS THE CODE, NEVER THE SENTENCE — the same rule, and the same reason, as
 * `driverConflict` above. This is a prompt; `AppointmentTaken` is a refusal; and
 * deciding which by matching English breaks the day somebody improves the wording,
 * in the direction that turns a question into a dead end.
 *
 * ⚠️ EVERY FIELD IS CHECKED, not just the array's presence. This payload becomes
 * the text of a dialog a scheduler answers by choosing between two numbers, and a
 * half-formed conflict would render as a sentence with "undefined" in it on the
 * one screen where reading carefully is the entire job.
 */
export interface AttachConflictRow {
    field: string; appointmentValue: string; driverValue: string; detail: string;
}

/**
 * ⭐ ONE READER, TWO CODES (2026-09-23).
 *
 * ⛔⛤ THIS WAS COPIED RATHER THAN SHARED when the paperwork prompt arrived, and
 * the copy lasted about a minute. The two prompts differ in exactly one thing —
 * the code they answer to — and everything that makes this function careful (every
 * field checked, an empty list treated as "not a prompt") would have had to be
 * kept true in two places.
 *   ** WHEN TWO FUNCTIONS DIFFER BY ONE VALUE, THAT VALUE IS AN ARGUMENT. **
 *
 * ⚠️ EVERY FIELD IS CHECKED, not just the array's presence. This payload becomes
 * the text of a dialog a scheduler answers by choosing between two numbers, and a
 * half-formed conflict would render as a sentence with "undefined" in it on the one
 * screen where reading carefully is the entire job.
 */
function readAttachConflict(err: unknown, code: string): { conflicts: AttachConflictRow[] } | null {
    const e = err as { details?: { code?: string; conflicts?: unknown } } | null;
    /* ⛔ READS THE CODE, NEVER THE SENTENCE — the same rule, and the same reason,
       as `driverConflict`. Deciding which prompt this is by matching English breaks
       the day somebody improves the wording, in the direction that turns a question
       into a dead end. */
    if (e?.details?.code !== code) return null;
    const raw = Array.isArray(e.details.conflicts) ? e.details.conflicts : [];
    const conflicts = raw.filter((c): c is AttachConflictRow => {
        const o = c as Record<string, unknown> | null;
        return !!o
            && typeof o.field === 'string'
            && typeof o.appointmentValue === 'string'
            && typeof o.driverValue === 'string'
            && typeof o.detail === 'string';
    });
    /* ⛔ A CONFLICT RESPONSE WITH NOTHING LEGIBLE IN IT IS NOT A PROMPT. Returning
       null sends the caller down its ordinary error path, which shows the server's
       own sentence — worse than the dialog, and far better than an empty one. */
    return conflicts.length > 0 ? { conflicts } : null;
}

export function vehicleNumberConflict(err: unknown): { conflicts: AttachConflictRow[] } | null {
    return readAttachConflict(err, 'VehicleNumberConflict');
}

/**
 * ⭐⭐ IS THIS FAILURE THE "the booking says another PO" ONE? (2026-09-23)
 *
 * ⛔⛔ A SEPARATE CODE, AND THEREFORE A SEPARATE PROMPT. Pete refused a combined
 * one: *"A booking's PO is the thing the whole load is identified by downstream; a
 * trailer number is a description of the box in the yard. Putting them behind one
 * button would make the cheap mistake and the expensive one cost the same click."*
 *   ** THE FIELDS A PROMPT COVERS ARE PART OF THE PROMPT'S MEANING. **
 */
export function paperworkNumberConflict(err: unknown): { conflicts: AttachConflictRow[] } | null {
    return readAttachConflict(err, 'PaperworkNumberConflict');
}

/** ⭐ What a successful attach reports back. `palletVariance` is a WARNING. */
export interface AttachResult {
    status: string;
    documentsCarried?: number;
    /** ⚠️ NULL/absent means nothing to say, which covers four different reasons. */
    palletVariance?: { appointmentCount: number; driverCount: number } | null;
    /**
     * ⭐⭐ "THAT NUMBER DOES NOT LOOK LIKE THAT FIELD" — judged on the server from
     * THIS CLIENT'S OWN HISTORY, never from a global convention.
     * ⛔ A HINT, NOT A CORRECTION. Nothing is moved between columns on the strength
     * of it; it is a sentence for the one person already looking.
     */
    shapeHints?: { claimed: string; looksLike: string; detail: string }[];
}

/**
 * ⭐⭐ IS THIS FAILURE THE "already booked to somebody" ONE? (#300, 2026-08-27)
 *
 * ⛔ READS THE CODE, NEVER THE SENTENCE. The server sends `code: 'DriverConflict'`
 * precisely so a caller can tell a conflict it may OFFER TO RESOLVE from one that
 * is final — `AppointmentTaken` is a refusal, this is a prompt. ⚠️ Matching on the
 * English would break the day somebody improves the wording, and it would break by
 * turning a question into a dead end.
 */
export function driverConflict(err: unknown): { bookedDrivers: string[] } | null {
    const e = err as { details?: { code?: string; bookedDrivers?: unknown } } | null;
    if (e?.details?.code !== 'DriverConflict') return null;
    const names = Array.isArray(e.details.bookedDrivers)
        ? e.details.bookedDrivers.filter((n): n is string => typeof n === 'string')
        : [];
    return { bookedDrivers: names };
}

/**
 * Void an arrival. A reason is REQUIRED, not optional — voiding removes a truck
 * from the day's arrival order, and "why is there no #3?" is a question somebody
 * asks months later.
 */
export async function voidCheckIn(
    token: string, checkInId: string, reason: string,
): Promise<{ status: string }> {
    return request(`/mgmt/checkins/${checkInId}/void`, token, {
        method: 'POST',
        body: JSON.stringify({ reason }),
    });
}

// ---------------------------------------------------------------------------
// Driver identity — licence capture at the scheduler's desk (migration 144)
// ---------------------------------------------------------------------------

/**
 * Send a scanned AAMVA barcode to be parsed and stored.
 *
 * ⚠️ `raw` IS THE DRIVER'S LICENCE. It contains his date of birth and home
 * address. It goes straight to the server, is never kept in component state
 * beyond the keystroke burst, never logged, and never rendered.
 *
 * `confirmed` is only set after the scheduler has answered the "this arrival is
 * already recorded as somebody else" prompt.
 */
export async function scanLicence(
    token: string, checkInId: string, raw: string, confirmed = false,
): Promise<LicenceScanResultDto> {
    return request(`/mgmt/checkins/${checkInId}/licence`, token, {
        method: 'POST',
        body: JSON.stringify({ rawBarcode: raw, confirmed }),
    });
}

/**
 * Type the details when the card will not scan — a damaged licence, a paper
 * temporary, a jurisdiction we cannot read.
 *
 * ⚠️ A LOWER GRADE OF EVIDENCE, and the stored row says so. The licence number
 * is required because it is the key the driver is recognised by next time.
 */
export async function enterLicenceManually(
    token: string, checkInId: string,
    details: {
        firstName?: string | null; lastName?: string | null;
        licenceNumber: string; licenceState?: string | null;
        licenceExpiry?: string | null; vehicleClass?: string | null;
        confirmed?: boolean;
    },
): Promise<LicenceScanResultDto> {
    return request(`/mgmt/checkins/${checkInId}/licence/manual`, token, {
        method: 'POST',
        body: JSON.stringify(details),
    });
}

/**
 * What we know about the driver on this arrival.
 *
 * ⭐ The findings are recomputed server-side on every call rather than stored: a
 * licence that was valid when it was scanned expires later, and a cached
 * "looks fine" would still say so the morning after it lapsed.
 */
export async function getCheckInIdentity(
    token: string, checkInId: string,
): Promise<CheckInIdentityDto> {
    return request(`/mgmt/checkins/${checkInId}/identity`, token);
}

// ---------------------------------------------------------------------------
// Texting a waiting driver his dock door
// ---------------------------------------------------------------------------

/**
 * WHICH OF THE THREE SENDABLE MESSAGES THIS IS.
 *
 * ⭐ ONE TYPE, SHARED BY EVERY CALLER, and that is the whole point of it living
 * here. The kind travels through four places — the button that picks it, the
 * dialog that previews it, this client, and the route's `KindSchema` — and when
 * it was spelled out as a union at each of them, adding `work` meant finding all
 * four. Two got found. A named type makes the compiler find the rest.
 *
 * ⛔ THIS LIST IS NOT THE SAME AS `DoorTextKind` ON THE SERVER, which also has
 * `reminder`. A reminder is raised by a timer, never by a person pressing a
 * button, so it must not be reachable from a UI — the server's `KindSchema`
 * deliberately omits it too.
 */
export type DoorTextKind = 'door' | 'paperwork' | 'work';

/**
 * What would be sent, to whom, and what it costs.
 *
 * ⭐ The message comes from the server so the preview and the send cannot
 * disagree — and so a door reassigned between the two sends the NEW door.
 */
export function getDoorText(
    token: string, appointmentId: string, note?: string | null,
    kind: DoorTextKind = 'door',
): Promise<DoorTextDto> {
    const qs = new URLSearchParams();
    if (note) qs.set('note', note);
    if (kind !== 'door') qs.set('kind', kind);
    const q = qs.toString();
    return request(`/mgmt/appointments/${appointmentId}/door-text${q ? '?' + q : ''}`, token);
}

/**
 * Send it.
 *
 * ⛔ `phones` NAMES WHICH OF THE OFFERED NUMBERS TO USE — it cannot introduce a
 * new one. The server checks each against the recipients it computed, so this
 * route cannot be used to push SMS to an arbitrary number.
 */
export function sendDoorText(
    token: string, appointmentId: string, phones: string[], note?: string | null,
    kind: DoorTextKind = 'door',
): Promise<DoorTextSendResultDto> {
    return request(`/mgmt/appointments/${appointmentId}/door-text`, token, {
        method: 'POST',
        body: JSON.stringify({ phones, note: note ?? null, kind }),
    });
}

/**
 * ---- ⭐⭐ TEXT A CHECKED-IN DRIVER, APPOINTMENT OR NOT (#340) --------------
 *
 * > Pete, 2026-09-09: *"I said we can text the driver period. Not a message
 * > telling me I can't do it because of the status."*
 *
 * ⛔ EVERY OTHER SEND ABOVE IS KEYED ON AN APPOINTMENT, because the body is
 * composed from the load and its door. These two are keyed on the ARRIVAL, so an
 * unmatched truck can be texted like any other.
 *
 * ⚠️ THE NOTE IS THE MESSAGE HERE, and it is REQUIRED. With no appointment there
 * is no sentence to append to — the server composes a facility stem and appends
 * your line, so an empty note would send a driver a facility name and nothing
 * else. The server refuses it; do not "helpfully" default it here.
 */
export interface CheckInTextDto {
    checkInId: string;
    facilityCode: string;
    facilityName: string | null;
    driverName: string | null;
    recipients: { name: string | null; phone: string; source: 'booking' | 'checkin' }[];
    /** ⭐ The exact body that will be sent — composed by the same function the send uses. */
    message: string;
    appointmentId: string | null;
    maxNote: number;
    segments: number;
    unicode: boolean;
}

export function getCheckInText(
    token: string, checkInId: string, note?: string | null,
): Promise<CheckInTextDto> {
    const qs = new URLSearchParams();
    if (note) qs.set('note', note);
    const q = qs.toString();
    return request(`/mgmt/checkins/${checkInId}/text${q ? '?' + q : ''}`, token);
}

export function sendCheckInText(
    token: string, checkInId: string, phones: string[], note: string,
): Promise<CheckInTextSendResultDto> {
    return request(`/mgmt/checkins/${checkInId}/text`, token, {
        method: 'POST',
        body: JSON.stringify({ phones, note }),
    });
}

/** This facility's wording for the dock-door text, plus two worked examples. */
export function getDoorTextConfig(
    token: string, facilityCode: string,
): Promise<DoorTextConfigResponseDto> {
    return request(`/mgmt/door-text-config?facilityCode=${encodeURIComponent(facilityCode)}`, token);
}

/**
 * Save it.
 *
 * ⛔ The server re-validates every template — a screen-only check is a check
 * anybody with curl can skip, and the cost of skipping it is a driver receiving
 * "Please proceed to DOOR {dor}."
 */
export function saveDoorTextConfig(
    token: string,
    body: Pick<DoorTextConfigDto,
        'includeDoor' | 'includeAppointment' | 'includeTruckNumber' | 'includeFacility'
        | 'includeSchedule' | 'includeQueue' | 'greetingTemplate' | 'doorLineTemplate'
        | 'noDoorLineTemplate' | 'customText' | 'paperworkTemplate'
        | 'enablePaperworkText' | 'reminderTemplate'> & { facilityCode: string },
): Promise<DoorTextConfigResponseDto & { status: string }> {
    return request('/mgmt/door-text-config', token, { method: 'POST', body: JSON.stringify(body) });
}

// ---------------------------------------------------------------------------
// Scanned paperwork (migrations 145 + 146)
// ---------------------------------------------------------------------------

export function getDocumentConfig(
    token: string, facilityCode: string,
): Promise<{ config: DocumentConfigDto; documentTypes: string[]; tokens: string[] }> {
    return request(`/mgmt/document-config?facilityCode=${encodeURIComponent(facilityCode)}`, token);
}

export function saveDocumentConfig(
    token: string, body: Omit<DocumentConfigDto, 'documentConfigId' | 'isDefaultShipped' | 'configKey' | 'displayName'>
        & { configKey?: string | null; displayName?: string | null },
): Promise<{ status: string; config: DocumentConfigDto }> {
    return request('/mgmt/document-config', token, { method: 'POST', body: JSON.stringify(body) });
}

export interface SharePointTestStep { step: string; ok: boolean; detail: string }
export interface SharePointTestResult {
    ok: boolean;
    steps: SharePointTestStep[];
    sampleFiles: string[];
}

/**
 * POST /api/mgmt/document-config/test-sharepoint — the "Test connection" button (#150).
 *
 * ⭐ TESTS WHAT IS IN THE FORM, NOT WHAT IS SAVED. Somebody typing a folder path wants
 * to know whether THAT path works before committing it; a test that only checks the
 * stored value can only be run after making the mistake.
 *
 * ⛔ NEVER REJECTS ON A CONNECTION FAILURE — it returns steps. The failure IS the
 * answer, and a thrown error would have the screen render a generic message and throw
 * the diagnosis away.
 */
export function testSharePointFolder(
    token: string, hostname: string, sitePath: string, folderPath: string,
): Promise<SharePointTestResult> {
    return request('/mgmt/document-config/test-sharepoint', token,
        { method: 'POST', body: JSON.stringify({ hostname, sitePath, folderPath }) });
}

/**
 * ⭐ The preview comes from the SERVER, rendered by the same function that names
 * the real thing. A client-side copy would drift, and a preview that lies is
 * worse than none — it is confidently wrong exactly when somebody is deciding
 * whether the template is right.
 */
export function checkDocumentTemplate(
    token: string, template: string, fileNameCase: 'upper' | 'lower' | 'asis',
): Promise<TemplateCheckDto> {
    return request('/mgmt/document-config/validate', token, {
        method: 'POST',
        body: JSON.stringify({ template, fileNameCase }),
    });
}

/**
 * Store a scanned file.
 *
 * ⚠️ NO Content-Type HEADER IS SET, deliberately. `fetch` adds
 * multipart/form-data WITH the boundary parameter when the body is a FormData;
 * setting it by hand omits the boundary and the server cannot parse the body at
 * all. `request()` was setting `application/json` on ANY body — this was caught
 * writing this function, and both `request` and `requestNoBody` now skip the
 * header for FormData.
 */
export function uploadDocument(
    token: string,
    facilityCode: string,
    file: File,
    opts: {
        appointmentId?: string | null;
        checkInId?: string | null;
        documentType?: string | null;
        source?: 'Upload' | 'Folder';
        sourceRef?: string | null;
    } = {},
): Promise<{ status: string; document: DocumentDto }> {
    const form = new FormData();
    form.append('facilityCode', facilityCode);
    form.append('file', file, file.name);
    if (opts.appointmentId) form.append('appointmentId', opts.appointmentId);
    if (opts.checkInId) form.append('checkInId', opts.checkInId);
    if (opts.documentType) form.append('documentType', opts.documentType);
    if (opts.source) form.append('source', opts.source);
    if (opts.sourceRef) form.append('sourceRef', opts.sourceRef);
    return request('/mgmt/documents/upload', token, { method: 'POST', body: form });
}

export function getUnfiledDocuments(
    token: string, facilityCode: string,
): Promise<{ facilityCode: string; documents: DocumentDto[] }> {
    return request(`/mgmt/documents/unfiled?facilityCode=${encodeURIComponent(facilityCode)}`, token);
}

export function getAppointmentDocuments(
    token: string, appointmentId: string,
): Promise<{ documents: DocumentDto[] }> {
    return request(`/mgmt/appointments/${appointmentId}/documents`, token);
}

/**
 * ---- ⭐ EVERYTHING FILED UNDER ONE LOAD ------------------------------------
 *
 * ⛔ NOT THE SAME AS `getAppointmentDocuments`, WHICH STAYS. That returns rows whose
 * AppointmentId is set. This also returns paperwork filed against the ARRIVAL — a
 * walk-in has paperwork before he has an appointment, so it is filed on the check-in
 * — plus the drivers' licences. A scheduler asking "what have we got on this load"
 * was previously shown a subset with nothing to say it was one.
 *
 * ⛔ THE LICENCE FIELDS ARE DECODED VALUES ONLY. The raw AAMVA barcode carries date of
 * birth and home address and is never sent to a browser.
 */
export interface LicenceSummaryDto {
    checkInId: string;
    checkInName: string | null;
    arrivalSeq: number | null;
    licenceNumber: string | null;
    licenceState: string | null;
    firstName: string | null;
    lastName: string | null;
    licenceExpiry: string | null;
    vehicleClass: string | null;
    endorsements: string | null;
    restrictions: string | null;
    scannedUtc: string | null;
    scannedBy: string | null;
    /** ⚠️ Typed by a scheduler, not scanned — a different grade of evidence. */
    isManualEntry: boolean;
    lastSeenUtc: string | null;
}

export interface ArrivalDocumentsDto {
    checkInId: string;
    arrivalSeq: number | null;
    driverName: string | null;
    documents: DocumentDto[];
}

export interface AppointmentPaperworkDto {
    appointmentId: string;
    documents: DocumentDto[];
    arrivals: ArrivalDocumentsDto[];
    licences: LicenceSummaryDto[];
    /** ⚠️ Always empty until the labor-clock app starts sending truck photos. */
    photos: never[];
}

export function getAppointmentPaperworkDossier(
    token: string, appointmentId: string,
): Promise<AppointmentPaperworkDto> {
    return request(`/mgmt/appointments/${appointmentId}/paperwork-dossier`, token);
}

/** Which of these scans have we already stored? Greys out the folder list. */
export function knownScans(
    token: string, facilityCode: string, refs: string[],
): Promise<{ known: string[] }> {
    return request('/mgmt/documents/known', token, {
        method: 'POST',
        body: JSON.stringify({ facilityCode, refs }),
    });
}

/**
 * ⛔ The confirm step. Only ever reached by a scheduler pressing Save, or dropping.
 *
 * ⭐ ONE TARGET OR THE OTHER (#150) — a load, or a driver standing at the gate. The
 * server links the other side when it can and says so in `alsoLinked`; passing both
 * is a 400 rather than a guess.
 */
export function fileDocument(
    token: string,
    documentId: string,
    target: { appointmentId: string } | { checkInId: string },
    documentType: string | null,
): Promise<{
    status: string;
    document: DocumentDto;
    alsoLinked: 'appointment' | 'checkIn' | null;
}> {
    return request(`/mgmt/documents/${documentId}/file`, token, {
        method: 'POST',
        body: JSON.stringify({ ...target, documentType }),
    });
}

/**
 * ---- ⭐ WHICH LOADS COULD THIS ARRIVAL BE? --------------------------------
 *
 * Pete, 2026-08-25: *"Scheduler should be associating the driver to an appointment after
 * the drivers license scan. Or confirming it, if it finds only one appointment."*
 *
 * ⭐ RUN ON DEMAND, NOT AT THE GATE. The server answers from the SAME matcher the kiosk
 * used to call — there is one matcher on this platform — but now, when the licence has
 * been scanned and the day's appointments are current.
 *
 * ⚠️ `method` SAYS WHICH SIGNAL MATCHED (phone, container, reference, po). Show it: a
 * scheduler can check "matched on container" against the paper in their hand, where a
 * bare confidence score is unverifiable and gets trusted blindly or ignored.
 */
export function getCheckInCandidates(
    token: string, checkInId: string,
): Promise<{ matches: KioskLookupMatch[]; method: string | null }> {
    return request(`/mgmt/checkins/${checkInId}/candidates`, token);
}

/** What one ARRIVAL is carrying — including a walk-in's, which has no load yet. */
export function getCheckInDocuments(
    token: string, checkInId: string,
): Promise<{ documents: DocumentDto[] }> {
    return request(`/mgmt/checkins/${checkInId}/documents`, token);
}

// ---------------------------------------------------------------------------
// SharePoint scans (#150)
// ---------------------------------------------------------------------------

export interface ScanFileDto {
    id: string;
    name: string;
    bytes: number | null;
    modifiedUtc: string | null;
    modifiedBy: string | null;
    sourceRef: string;
    /** ⚠️ Already in Dock. Should be rare — it means last time's move failed. */
    known: boolean;
}

export interface ScanListingDto {
    /** 'SharePoint' | 'NotSharePoint' | 'NotConfigured' | 'Failed' */
    source: string;
    /** Why the list is empty, when it is empty for a reason worth saying. */
    reason: string | null;
    folderPath?: string;
    truncated: boolean;
    files: ScanFileDto[];
}

/**
 * ⭐ READ SERVER-SIDE. Unlike the local-folder path this needs no browser grant, no
 * Chrome, and no per-machine setup — which is why a tablet at the guard shack can see
 * the same tray as the office.
 */
export function listScans(token: string, facilityCode: string): Promise<ScanListingDto> {
    return request(`/mgmt/documents/scans?facilityCode=${encodeURIComponent(facilityCode)}`, token);
}

/**
 * ⛔ Bin a scan that is not paperwork. RECYCLE BIN, not erase — SharePoint keeps it
 * for 93 days. The server refuses any item that is not in this facility's scan folder.
 */
export function deleteScan(
    token: string, facilityCode: string, itemId: string,
): Promise<{ status: string; name: string }> {
    return request('/mgmt/documents/scans/delete', token, {
        method: 'POST',
        body: JSON.stringify({ facilityCode, itemId }),
    });
}

/** A short-lived link to look at a scan before deciding whether it is paperwork. */
export function getScanPreviewUrl(
    token: string, facilityCode: string, itemId: string,
): Promise<{ url: string; name: string }> {
    const params = new URLSearchParams({ facilityCode, itemId });
    return request(`/mgmt/documents/scans/preview?${params}`, token);
}

/** Copy one scan into Dock and move the original into Filed/YYYY-MM. */
export function ingestScan(
    token: string, facilityCode: string, itemId: string, documentType?: string | null,
): Promise<{
    status: 'ingested' | 'duplicate';
    document: DocumentDto;
    /** ⚠️ False means it is stored but still sitting in the SharePoint folder. */
    moved: boolean;
    moveError: string | null;
}> {
    return request('/mgmt/documents/scans/ingest', token, {
        method: 'POST',
        body: JSON.stringify({ facilityCode, itemId, documentType: documentType ?? null }),
    });
}

export function voidDocumentApi(
    token: string, documentId: string, reason: string,
): Promise<{ status: string }> {
    return request(`/mgmt/documents/${documentId}/void`, token, {
        method: 'POST',
        body: JSON.stringify({ reason }),
    });
}

/**
 * ⭐ Move a filed document back to the waiting pile.
 *
 * ⛔ NOT A DELETE AND NOT A VOID. The row, the bytes, the name and the audit
 * trail all survive; only its home is cleared — see `unfileDocument`.
 */
export async function unfileDocumentApi(token: string, documentId: string): Promise<{ count: number }> {
    const res = await fetch(`${API_BASE}/mgmt/documents/${encodeURIComponent(documentId)}/unfile`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        /* ⚠️ THE SERVER'S SENTENCE, NOT A STATUS CODE. A 409 means a real rule
           refused — a signed copy stays with its appointment — and that sentence is the
           only thing that explains the refusal to the person who tried. */
        let error = `HTTP ${res.status}`;
        if ((res.headers.get('content-type') ?? '').includes('application/json')) {
            const body = await res.json().catch(() => null) as Record<string, unknown> | null;
            if (body && typeof body.error === 'string') error = body.error;
        }
        throw new ApiCallError({ error, status: res.status });
    }
    const body = await res.json() as { count?: number };
    return { count: body.count ?? 0 };
}

/** A short-lived link. Fetched on click, never stored. */
/**
 * ⭐ ASK WHAT LOAD THIS PAGE BELONGS TO (#191).
 *
 * ⚠️ `checkInId` SCOPES THE RANKING, it does not identify the paperwork. The
 * server reads the client and carrier off that ARRIVAL rather than trusting
 * anything sent here — a caller that could name its own client could choose
 * whose ranking to apply to somebody else's document.
 */
export function getSuggestLoad(
    token: string, documentId: string, checkInId?: string | null,
): Promise<SuggestLoadDto> {
    const qs = checkInId ? `?checkInId=${encodeURIComponent(checkInId)}` : '';
    return request(`/mgmt/documents/${documentId}/suggest-load${qs}`, token);
}

export function getDocumentUrl(token: string, documentId: string): Promise<{ url: string }> {
    return request(`/mgmt/documents/${documentId}/download`, token);
}

/** ⭐ Either kind of paperwork the tray holds. Exactly one shape, never both. */
export type PreviewTarget =
    | { kind: 'document'; documentId: string }
    | { kind: 'scan'; facilityCode: string; itemId: string };

/**
 * ---- ⭐ THE BYTES, FOR THE PANEL BESIDE THE TRAY ---------------------------
 *
 * Pete, 2026-08-25: *"can we do the document viewer as a popup next to the paperwork
 * instead of a new tab?"*
 *
 * ⛔ IT DOES NOT GO THROUGH `request`. That helper reads a JSON body; this response is
 * a PDF or a PNG, and a Blob is the whole point.
 *
 * ⚠️ AND THE OBJECT URL IS THE CALLER'S TO REVOKE. Every one held is a copy of the
 * file kept alive in the tab's memory; a scheduler clicking through a morning's scans
 * would accumulate all of them. The viewer revokes on close and on replace.
 *
 * ⚠️ 415 AND 413 ARE NOT FAILURES. A .docx bill of lading is good paperwork that this
 * viewer cannot draw. They arrive as an ApiCallError carrying `code`, so the panel can
 * say "download it instead" rather than "something went wrong".
 */
export async function fetchPreviewBlob(
    token: string, target: PreviewTarget,
): Promise<{ url: string; contentType: string }> {
    const q = new URLSearchParams(
        target.kind === 'document'
            ? { documentId: target.documentId }
            : { facilityCode: target.facilityCode, itemId: target.itemId },
    );

    const res = await fetch(`${API_BASE}/mgmt/documents/preview-content?${q}`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
        let error = `HTTP ${res.status}`;
        let code: string | undefined;
        if ((res.headers.get('content-type') ?? '').includes('application/json')) {
            const body = await res.json().catch(() => null) as Record<string, unknown> | null;
            if (body && typeof body.error === 'string') error = body.error;
            if (body && typeof body.reason === 'string') code = body.reason;
        }
        throw new ApiCallError({ status: res.status, error, code });
    }

    const contentType = (res.headers.get('content-type') ?? '').split(';')[0].trim();
    const blob = await res.blob();
    /**
     * ⚠️ THE BLOB IS RETYPED FROM THE HEADER THE SERVER CHOSE. `res.blob()` carries the
     * response's type through in every browser we care about, but an object URL whose
     * type is empty renders as a download prompt, and the server's header is a value
     * from its own allowlist — so it is restated rather than relied upon.
     */
    return { url: URL.createObjectURL(new Blob([blob], { type: contentType })), contentType };
}

// ---------------------------------------------------------------------------
// The load-received stamp (#110)
// ---------------------------------------------------------------------------

/** What the stamp will say, and where this carrier's box went last time. */
export function getSignPreview(
    token: string, documentId: string, facilityCode: string,
    opts: { carrier?: string | null; appointment?: string | null } = {},
): Promise<SignPreviewDto> {
    const q = new URLSearchParams({ facilityCode });
    if (opts.carrier) q.set('carrier', opts.carrier);
    if (opts.appointment) q.set('appointment', opts.appointment);
    return request(`/mgmt/documents/${documentId}/sign?${q.toString()}`, token);
}

/**
 * Produce the signed copy.
 *
 * ⛔ THE BODY CARRIES A PLACE, NOT A PERSON. The name, the date and the time all
 * come from the server — the name from the validated token, the clock from the
 * host. Adding any of them here would let whoever is holding the mouse decide
 * who received a load and when.
 */
export function signDocumentApi(
    token: string, documentId: string, placement: { page: number; fx: number; fy: number },
): Promise<SignResultDto> {
    return request(`/mgmt/documents/${documentId}/sign`, token, {
        method: 'POST',
        body: JSON.stringify(placement),
    });
}

export type {
    DriverIdentityDto, LicenceScanResultDto, CheckInIdentityDto,
    ReleaseCheckDto, ReleaseOverrideDto,
    DocumentConfigDto, DocumentDto, TemplateCheckDto, SignPreviewDto, SignResultDto,
    DoorTextDto, DoorTextRecipientDto, DoorTextSendResultDto,
    DoorTextConfigDto, DoorTextConfigResponseDto, DoorQueueDto,
    ReminderScheduleRowDto, ReminderScheduleResponseDto, ReminderPreviewDto,
} from './types';

export async function updateStatusColor(
    token: string,
    statusCode: string,
    schedulerHex: string | null,
    boardHex: string | null,
): Promise<{ colors: StatusColorDto[] }> {
    return request(`/mgmt/status-colors/${encodeURIComponent(statusCode)}/update`, token, {
        method: 'PATCH',
        body: JSON.stringify({ schedulerHex, boardHex }),
    });
}

// ---------------------------------------------------------------------------
// Reminder schedules (#167)
// ---------------------------------------------------------------------------

/** GET /api/mgmt/reminder-schedule?facilityCode= — own entries AND inherited ones. */
export function getReminderSchedule(
    token: string, facilityCode: string,
): Promise<ReminderScheduleResponseDto> {
    return request(
        `/mgmt/reminder-schedule?facilityCode=${encodeURIComponent(facilityCode)}`, token);
}

/**
 * POST /api/mgmt/reminder-schedule — create or update one entry.
 * ⚠️ Omit reminderScheduleId to create. A 409 means an entry with the same scope,
 * offset and direction already exists — edit that one rather than adding a second.
 */
export function saveReminderSchedule(
    token: string,
    body: {
        reminderScheduleId?: number | null;
        scopeType: 'Company' | 'Facility';
        scopeKey: string;
        label: string;
        offsetHours: number;
        direction: string | null;
        messageTemplate: string | null;
        isActive: boolean;
    },
): Promise<{ status: string; reminderScheduleId: number; message: string;
             preview: ReminderPreviewDto | null }> {
    return request('/mgmt/reminder-schedule', token,
        { method: 'POST', body: JSON.stringify(body) });
}

/**
 * DELETE /api/mgmt/reminder-schedule?id=
 * ⛔ Refused with 409 if the entry has ever sent — deleting it would erase the
 * record of what those drivers received. Switch it off instead.
 */
export function deleteReminderSchedule(
    token: string, id: number,
): Promise<{ status: string }> {
    return request(`/mgmt/reminder-schedule?id=${id}`, token, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// APPOINTMENT UPLOAD (#159)
// ---------------------------------------------------------------------------

export interface UploadRowDto {
    rowNumber: number;
    ok: boolean;
    errors: string[];
    warnings: string[];
    values: Record<string, string | number | null> | null;
}

export interface UploadLimitWarningDto {
    localDate: string;
    direction: string;
    count: number;
    message: string;
}

export interface UploadPreflightDto {
    rowNumber: number | null;
    severity: 'error' | 'warning';
    type: string;
    detail: string;
}

export interface UploadPreviewDto {
    uploadId: number;
    facilityCode: string;
    fileName: string;
    defaultDate: string;
    /** ⭐ Whose freight the scheduler said this is. Null when they did not say. */
    clientCode: string | null;
    templateVersion: number | null;
    fatal: string | null;
    rowsRead: number;
    rowsValid: number;
    rowsRejected: number;
    rowsDropped: number;
    status: 'Previewed' | 'Committed' | 'Discarded';
    duplicateOf: {
        uploadId: number; uploadedUtc: string; batchId: string | null; rowsValid: number;
    } | null;
    limitWarnings: UploadLimitWarningDto[];
    preflight: UploadPreflightDto[];
    /**
     * ⭐ Rows the WMS recognised (#173/#174). ⚠️ Sparse — only rows with a verdict
     * worth reporting appear, and an empty list is the ordinary case for a client
     * with no match rule or a truck whose receipt has not been made yet.
     */
    matches: UploadMatchDto[];
    /** ⭐ Set when the file was the client's own, read through a saved mapping. */
    viaMapping: {
        mapId: number;
        driftMessage: string | null;
    } | null;
    /**
     * ⭐ WHICH TAB WE READ, AND WHAT ELSE THE WORKBOOK HELD (#132).
     * ⚠️ `sheetNames` is EMPTY when the workbook never opened, and empty on a
     * re-read of a stored upload — there is no file in hand to read another tab of.
     */
    sheetName: string | null;
    sheetNames: string[];
    /**
     * ⭐ WHICH DAYS THIS FILE HOLDS, AND WHICH ONES WE TOOK (#135).
     *
     * ⛔ `rowsOtherDates` must never be hidden — see the server's UploadPreview.
     * ⚠️ `imported` EMPTY MEANS NO FILTER RAN — every day was read. It does NOT mean
     * nothing was imported; those are opposite facts.
     */
    dates: {
        seen: string[];
        imported: string[];
        rowsOtherDates: number;
        /**
         * ⭐ Rows the tick list does NOT govern — a row with no readable date is
         * imported whichever days are ticked. ⛔ Shown, because a control that
         * silently does not cover part of the file is worse than no control.
         */
        rowsNoDate: number;
        /**
         * ⛔ Cells holding something that is NOT a date — the mis-mapped-column
         * alarm. ⚠️ Blank cells are ordinary; `drop` or `700` means wrong column.
         */
        rowsNotADate: number;
        notADateExamples: string[];
        /** ⚠️ The mapping filters on a different column than it dates from. */
        dateFilterIsOverride: boolean;
        /**
         * ⛔ Rows left out because they carry NO date — "not scheduled yet" in a
         * client's rolling export. ⚠️ Must be shown; it is often a large number.
         */
        rowsBlankDateDropped: number;
    };
    rows: UploadRowDto[];
}

export interface UploadMatchDto {
    rowNumber: number;
    /** ⚠️ Null on a re-read of a stored upload — the reasoning was not kept. */
    verdict: { kind: string; [k: string]: unknown } | null;
    /** The WMS order/receipt number, or null when nothing was attached. */
    orderNumber: string | null;
    /** ⛔ Null on the quiet verdicts. A matched or unmatched row says nothing. */
    message: string | null;
}

export interface UploadCommitResult {
    batchId: string;
    created: number;
    appointmentIds: string[];
    undo: string;
    doorsDropped: number[];
    driversImported: number;
    driversSkipped: number[];
    /** ⭐ Rows where a WMS match filled a PO or Reference the sheet left blank. */
    wmsEnriched: number[];
}

/**
 * GET /api/mgmt/appointments/upload-template
 *
 * ⚠️ RETURNS A BLOB, NOT JSON, so it cannot go through `request()`. The file is
 * generated per request from `UPLOAD_FIELDS` — never a static asset — so a column
 * added to the parser cannot leave a stale template in circulation.
 */
export async function downloadUploadTemplate(token: string): Promise<{ blob: Blob; fileName: string }> {
    const res = await fetch(`${API_BASE}/mgmt/appointments/upload-template`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        let errBody: Record<string, unknown> = {};
        try { errBody = await res.json(); } catch { /* not json */ }
        throw new ApiCallError({
            status: res.status,
            error: typeof errBody.error === 'string' ? errBody.error : `HTTP ${res.status}`,
        });
    }
    // ⚠️ The server names the file (it carries the template version). Parsed from
    // Content-Disposition so a version bump reaches the user's Downloads folder
    // without a frontend change.
    const cd = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^";]+)"?/i.exec(cd);
    return { blob: await res.blob(), fileName: match?.[1] ?? 'symbia-appointment-upload.xlsx' };
}

/**
 * POST /api/mgmt/appointments/upload — parse, judge, store a PREVIEW.
 *
 * ⛔ NOTHING IS CREATED BY THIS CALL. It returns verdicts and an upload id; the
 * appointments appear only when `commitAppointmentUpload` is called with that id.
 */
export interface UploadScopeOptions {
    /** ⭐ #132 — the tab to read. Omitted means "decide as before". */
    sheetName?: string | null;
    /**
     * ⭐ #135 — the days to import. ⛔ OMITTED AND EMPTY MEAN THE SAME THING: no
     * filter. Sending `[]` to mean "none" would be a request to import nothing,
     * which no screen offers and which the server refuses to interpret that way.
     */
    importDates?: readonly string[] | null;
}

export async function previewAppointmentUpload(
    token: string, facilityCode: string, defaultDate: string, file: File,
    clientCode?: string | null, scope?: UploadScopeOptions,
): Promise<UploadPreviewDto> {
    const form = new FormData();
    form.append('facilityCode', facilityCode);
    form.append('defaultDate', defaultDate);
    // ⚠️ OMITTED WHEN BLANK, not sent as ''. An empty field would be indistinguishable
    // from a code somebody cleared, and the server's "no client" branch is the one
    // that must run when nobody chose.
    if (clientCode && clientCode.trim()) form.append('clientCode', clientCode.trim());
    // ⚠️ Same rule for the tab: omitted, never ''. An empty `sheetName` would reach
    // `wb.getWorksheet('')` and refuse a file nobody asked to narrow.
    if (scope?.sheetName && scope.sheetName.trim()) {
        form.append('sheetName', scope.sheetName.trim());
    }
    /**
     * ⚠️ ONE FIELD PER DATE — the multipart way to send a list, read back with
     * `formData.getAll`. ⛔ Not a JSON string: a blob that half-parses reaches the
     * server as something that is not an array of strings, and a `Set` built from it
     * matches nothing while looking like a filter that ran.
     */
    for (const d of scope?.importDates ?? []) form.append('importDates', d);
    form.append('file', file);
    // ⚠️ NO Content-Type HEADER — see requestNoBody. fetch sets the multipart
    // boundary itself and setting the header by hand omits it.
    return request<UploadPreviewDto>('/mgmt/appointments/upload', token, {
        method: 'POST',
        body: form,
    });
}

// ---------------------------------------------------------------------------
// ⭐ READING A CLIENT'S OWN SPREADSHEET (#174 part A)
// ---------------------------------------------------------------------------

export interface SheetMapFieldDto {
    ourField: string;
    /** Their column heading. Null when this field is a fixed value. */
    sourceHeader: string | null;
    /** ⛔ The fact their file omits — most often Direction. */
    constantValue: string | null;
    transform: 'none' | 'hhmmNumber' | 'upperCase' | 'digitsOnly';
}

export interface SheetMapDto {
    mapId: number;
    facilityCode: string | null;
    clientCode: string;
    sheetName: string | null;
    headerRow: number;
    firstDataRow: number;
    expectedHeaders: string[];
    /** ⭐ The column that says which day a row is for. Null = take every row. */
    dateFilterHeader: string | null;
    fields: SheetMapFieldDto[];
    isActive: boolean;
}

export interface SheetInspectionDto {
    sheets: Array<{
        name: string;
        rowCount: number;
        firstRows: Array<{ rowNumber: number; values: Array<string | number | boolean | null> }>;
    }>;
    existing: SheetMapDto | null;
    ourFields: Array<{ key: string; header: string; hint: string; required: boolean }>;
    /** ⚠️ A first guess only — a person confirms every row of it. */
    suggested: Array<{ ourField: string; sourceHeader: string; basis: string; transform: string }>;
    /** Set instead of the rest when the file could not be opened at all. */
    fatal?: string;
}

/**
 * POST /api/mgmt/appointments/upload-inspect — what is in this file?
 *
 * ⛔ STORES NOTHING. A scheduler looking at a client's workbook has not decided
 * anything yet, and writing a row here would leave half-made mappings behind every
 * time somebody opened the wrong file.
 */
export async function inspectClientSheet(
    token: string, facilityCode: string, file: File, clientCode?: string | null,
): Promise<SheetInspectionDto> {
    const form = new FormData();
    form.append('facilityCode', facilityCode);
    if (clientCode && clientCode.trim()) form.append('clientCode', clientCode.trim());
    form.append('file', file);
    return request<SheetInspectionDto>('/mgmt/appointments/upload-inspect', token, {
        method: 'POST',
        body: form,
    });
}

/** GET /api/mgmt/appointments/upload-map — ⚠️ a null map is a 200, not a 404. */
export function getClientSheetMap(
    token: string, facilityCode: string, clientCode: string,
): Promise<{ map: SheetMapDto | null }> {
    return request<{ map: SheetMapDto | null }>(
        `/mgmt/appointments/upload-map?facilityCode=${encodeURIComponent(facilityCode)}`
        + `&clientCode=${encodeURIComponent(clientCode)}`, token);
}

export interface SaveSheetMapInput {
    facilityCode: string;
    clientCode: string;
    label?: string | null;
    sheetName: string | null;
    headerRow: number;
    firstDataRow: number;
    /** ⛔ The headings present in the file being mapped — the staleness guard. */
    expectedHeaders: string[];
    dateFilterHeader: string | null;
    fields: SheetMapFieldDto[];
}

/** POST /api/mgmt/appointments/upload-map */
export function saveClientSheetMap(
    token: string, input: SaveSheetMapInput,
): Promise<{ mapId: number }> {
    return request<{ mapId: number }>('/mgmt/appointments/upload-map', token, {
        method: 'POST',
        body: JSON.stringify(input),
    });
}

/** POST /api/mgmt/appointments/upload-map/remove */
export function removeClientSheetMap(
    token: string, facilityCode: string, clientCode: string,
): Promise<{ removed: boolean }> {
    return request<{ removed: boolean }>('/mgmt/appointments/upload-map/remove', token, {
        method: 'POST',
        body: JSON.stringify({ facilityCode, clientCode }),
    });
}

/**
 * POST /api/mgmt/appointments/upload/{uploadId}/commit
 *
 * ⛔ THE BODY CARRIES NO ROWS. The server commits what the server parsed; sending
 * rows back would make every validation rule advisory.
 */
export function commitAppointmentUpload(
    token: string,
    uploadId: number,
    opts: {
        status?: 'REQUESTED' | 'APPROVED';
        acknowledgeDailyLimit?: boolean;
        /**
         * ⭐ #140 — the SourceRowNumbers ticked on the preview.
         * ⛔ NUMBERS, NEVER VALUES. The server intersects them with what it already
         * parsed; the browser cannot introduce a row this way. Omitted or empty
         * means every valid row, which is the default the screen opens with.
         */
        rowNumbers?: number[];
    } = {},
): Promise<UploadCommitResult> {
    return request<UploadCommitResult>(`/mgmt/appointments/upload/${uploadId}/commit`, token, {
        method: 'POST',
        body: JSON.stringify(opts),
    });
}

/** POST /api/mgmt/appointments/upload/{uploadId}/discard */
export function discardAppointmentUpload(
    token: string, uploadId: number,
): Promise<{ uploadId: number; status: string }> {
    return request<{ uploadId: number; status: string }>(
        `/mgmt/appointments/upload/${uploadId}/discard`, token, { method: 'POST' });
}

export interface UploadLogRowDto {
    UploadId: number;
    FileName: string;
    FileBytes: number;
    TemplateVersion: number | null;
    DefaultDate: string;
    RowsRead: number;
    RowsValid: number;
    RowsRejected: number;
    RowsDropped: number;
    Status: string;
    MassCreateBatchId: string | null;
    FatalReason: string | null;
    UploadedUtc: string;
    UploadedBy: string;
    CommittedUtc: string | null;
    CommittedBy: string | null;
}

export interface UploadBackoutResult {
    /** ⭐ Removed entirely — nothing ever happened to them. */
    deleted: number;
    /** ⚠️ Kept and marked Cancelled — something happened, so the record survives. */
    cancelled: number;
    /**
     * ⛔ Every appointment that survived, with why — cancelled AND untouched. Never
     * empty-and-ignored: each names the row in the client's OWN file.
     */
    kept: Array<{ appointmentId: string; sourceRowNumber: number | null; reason: string }>;
    /** ⭐ The server's own sentence, accounting for every appointment. */
    message: string;
}

/**
 * POST /api/mgmt/appointments/upload/{uploadId}/backout — remove what it created (#139).
 *
 * ⛔ THIS DELETES. Every other undo in the app cancels. Pete: *"it would be best to
 * back them out completely, not make them Cancelled."*
 *
 * ⚠️ `localDate` IS THE SITE'S LOCAL DAY AND IS ENFORCED SERVER-SIDE. Pete scoped
 * this to the day on screen; a scope that lives only in which button is on which page
 * is not a scope.
 */
export function backOutUpload(
    token: string, uploadId: number, facilityCode: string, localDate: string,
): Promise<UploadBackoutResult> {
    return request<UploadBackoutResult>(
        `/mgmt/appointments/upload/${uploadId}/backout`, token,
        { method: 'POST', body: JSON.stringify({ facilityCode, localDate }) });
}

/**
 * PATCH /api/appointments/{id}/actual-times — correct a day that already happened (#138).
 *
 * ⛔ OMIT A FIELD TO LEAVE IT ALONE; SEND `null` TO CLEAR IT. Sending only the
 * arrival must not wipe a departure the truck really has, so the two states are kept
 * distinct all the way to the server.
 */
export function setActualTimes(
    token: string, appointmentId: number,
    times: { actualArrivalUtc?: string | null; actualDepartureUtc?: string | null },
): Promise<{ kind: string; actualArrivalUtc: string | null; actualDepartureUtc: string | null }> {
    return request(`/appointments/${appointmentId}/actual-times`, token,
        { method: 'PATCH', body: JSON.stringify(times) });
}

/** GET /api/mgmt/appointments/uploads — Pete: "Needs a log like normal". */
export function listAppointmentUploads(
    token: string, facilityCode: string, take = 50,
): Promise<{ uploads: UploadLogRowDto[] }> {
    const params = new URLSearchParams({ facilityCode, take: String(take) });
    return request<{ uploads: UploadLogRowDto[] }>(
        `/mgmt/appointments/uploads?${params}`, token);
}

// ---------------------------------------------------------------------------
// ⭐ #145 — APPOINTMENT SEARCH
// ---------------------------------------------------------------------------

export interface AppointmentSearchHit {
    appointmentId: string;
    appointmentNumber: string | null;
    /**
     * ⭐⭐ WHICH FACILITY THIS ROW IS AT (decision 210, 2026-09-22).
     *
     * ⛔ EVERY hit carries it, not only the cross-facility ones. A field present on
     * some rows and absent on others is read by the screen as "same as the picker",
     * which is true right up until it is not.
     */
    facilityCode: string;
    /**
     * ⭐ Facility-local yyyy-mm-dd, computed on the SERVER — the day to jump to.
     * ⛔ For an `exactHits` row this is THAT facility's local day, not the picker's.
     */
    localDate: string;
    scheduledStartUtc: string;
    statusId: number;
    statusCode: string | null;
    direction: string | null;
    clientCode: string | null;
    clientName: string | null;
    carrierCode: string | null;
    carrierName: string | null;
    doorName: string | null;
    poNumber: string | null;
    referenceNumber: string | null;
    containerNumber: string | null;
    bolNumber: string | null;
    asnNumber: string | null;
    trailerNumber: string | null;
    driverName: string | null;
    palletCount: number | null;
    /** ⭐ Which field matched, so a row that looks unrelated explains itself. */
    matchedOn: string | null;
    /** ⭐ And the value, sent from the server so the screen never has to guess it. */
    matchedValue: string | null;
}

export interface AppointmentSearchResult {
    /** ⚠️ A refusal is NOT an error — it is a sentence to show under the box. */
    refused: boolean;
    reason?: string;
    results: AppointmentSearchHit[];
    /** ⭐ True when the 50-row cap was hit. Must be shown, never swallowed. */
    capped: boolean;
    /** ⛔ What was actually searched. Rendered on EVERY result, not only empty ones. */
    scopeSentence?: string;
    windowDays?: number | null;
    /** ⭐ #255 — which rung ran, and the next one out. */
    scope?: 'day' | 'week' | 'month' | 'window' | 'all';
    nextScope?: 'day' | 'week' | 'month' | 'window' | 'all' | null;
    nextScopeLabel?: string | null;
    /**
     * ⭐ #255 — how many matches sit OUTSIDE the scope, capped at 51.
     *
     * ⚠️ null MEANS THE QUESTION WAS NOT ASKED, not zero. The server skips it when
     * the list is already capped or the scope was already 'all'. A screen that read
     * null as 0 would print "nothing anywhere else" without anyone having looked.
     */
    outsideScope?: number | null;
    /** ⭐ #256 — its own list, never merged into `results`. */
    wms?: WmsSearchSection;
    /**
     * ---- ⭐⭐ DECISION 210 — EXACT MATCHES, ANY DATE, ANY FACILITY -----------
     *
     * ⛔ Pete: `A00000364` read "Nothing matched" on All dates. It exists, at
     * facility 262, while he was on Denver.
     *   ** A SEARCH BOUND TO ONE FACILITY CANNOT SAY "NOTHING MATCHED". IT CAN
     *      ONLY SAY "NOTHING HERE". **
     *
     * ⚠️ ITS OWN LIST, never merged into `results`: `capped` and `outsideScope`
     * are computed from the scoped query alone, and a merged list would make both
     * describe a set neither counted. Absent or empty is the normal case.
     */
    exactHits?: AppointmentSearchHit[];
}

export type SearchScope = 'day' | 'week' | 'month' | 'window' | 'all';

/**
 * ⭐ #256 — AN ORDER IN THE WMS. NOT AN APPOINTMENT.
 *
 * ⛔ DELIBERATELY A SEPARATE TYPE, not an AppointmentSearchHit with extra fields.
 * They are different objects with different powers — a door can be assigned to an
 * appointment and not to an order — and a shared type is the first step towards a
 * shared list, which is how somebody ends up checking a truck in against a row
 * that is not a booking.
 */
export interface WmsSearchHit {
    orderNumber: string;
    orderType: string | null;
    /** 'Camelot' / 'Deposco' — the badge Pete asked for. */
    wmsSource: string | null;
    businessUnit: string | null;
    customer: string | null;
    status: string | null;
    plannedArrivalDate: string | null;
    plannedShipDate: string | null;
    containerNumber: string | null;
    bolNumber: string | null;
    proNumber: string | null;
    /** ⭐ Already booked at this facility, on ANY date. The most useful fact here. */
    alreadyBooked: boolean;
    /** ⭐ Which site the WMS has it under — the search spans all of them (2026-08-27). */
    facility?: string | null;
    /** ⚠️ True when this row belongs to a DIFFERENT site than the one on screen. */
    elsewhere?: boolean;
    /** ⚠️ How stale the cache row is. A cache that implies it is live is a lie. */
    rowUpdatedAt: string | null;
    /**
     * ⛔ THE COLUMN THAT MATCHED, NOT WHAT IT MEANS (open item 35, 2026-09-27).
     * ⚠️ TWO FIELDS HERE, FOUR ON `bookedOn`, AND THAT IS NOT AN OVERSIGHT: this
     * search compares a string the scheduler typed against a WMS column, so
     * echoing their own input back as evidence would be circular.
     * ⛔ `AppointmentSearchHit.matchedOn` IS A DIFFERENT FIELD THAT HAPPENS TO
     * SPELL THE SAME — its values include 'Driver'. It is untouched.
     */
    matchedWmsColumn: string | null;
    matchedWmsValue: string | null;
}

export interface WmsSearchSection {
    /** ⚠️ false = the WMS could not be read. NOT the same as "nothing found". */
    available: boolean;
    reason?: string;
    results: WmsSearchHit[];
    capped: boolean;
}

export interface BulkStatusResult {
    action: string;
    requested: number;
    changed: number;
    skipped: { appointmentId: number; reason: string }[];
    /** ⚠️ Always false today — bulk matches the single-appointment path, which is opt-in. */
    notified: boolean;
}

/**
 * POST /api/mgmt/appointments/bulk-status — change many at once (#254).
 *
 * ⛔ THE IDS ARE THE ONES ON SCREEN. Never "everything the filter matched" — see
 * the note on the select-all checkbox in AppointmentSearch.
 */
export function bulkAppointmentStatus(
    token: string,
    body: { facilityCode: string; appointmentIds: number[]; action: string; reason?: string | null },
): Promise<BulkStatusResult> {
    return request<BulkStatusResult>('/mgmt/appointments/bulk-status', token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/**
 * GET /api/appointments/search
 *
 * ⛔ `date` IS THE ANCHOR THE WINDOW IS CENTRED ON — the day the calendar is standing
 * on, not today. A scheduler who has navigated to next month expects the search to be
 * about where they are looking.
 */
export function searchAppointments(
    token: string, facilityCode: string, q: string, date: string,
    /* ⛔⛤ 2026-09-22 -- the default was ~~'day'~~. Pete: *"we need to set date as
       an option, not as the default. We are missing lots of records with the date
       range set."* ⚠️ Callers pass a scope explicitly, so this default is a
       backstop rather than the behaviour -- the screen's own default is what
       changed. */
    scope: SearchScope = 'all', statusCodes: string[] = [],
): Promise<AppointmentSearchResult> {
    const params = new URLSearchParams({ facilityCode, q, date, scope });
    /**
     * ⚠️ REPEATED `status` PARAMS, not a comma-joined one. The server reads them
     * with getAll; a joined string would arrive as a single unrecognised code and
     * filter everything out — a search returning less than it should, silently.
     */
    for (const code of statusCodes) params.append('status', code);
    return request<AppointmentSearchResult>(`/appointments/search?${params}`, token);
}

/** GET /api/mgmt/facilities/{code}/search-window — #145's config. */
export function getFacilitySearchWindow(
    token: string, facilityCode: string,
): Promise<{ searchWindowDays: number | null; defaultWindowDays: number }> {
    return request(`/mgmt/facilities/${encodeURIComponent(facilityCode)}/search-window`, token);
}

/**
 * PUT /api/mgmt/facilities/{code}/search-window
 *
 * ⛔ `null` IS A REAL VALUE meaning "use the shipped default" — clearing the box must
 * send null, not 0 and not the default number. A typed 90 and an unset 90 mean
 * different things the day the default changes.
 */
export function setFacilitySearchWindow(
    token: string, facilityCode: string, searchWindowDays: number | null,
): Promise<{ searchWindowDays: number | null }> {
    return request(`/mgmt/facilities/${encodeURIComponent(facilityCode)}/search-window`, token,
        { method: 'PUT', body: JSON.stringify({ searchWindowDays }) });
}

/**
 * GET /api/mgmt/appointments/backout-sources — what can be undone on this day (#141).
 *
 * ⛔ REPLACES FILTERING THE UPLOAD LOG. Mass create never writes an upload row, so the
 * old client-side filter could only ever find uploaded batches — a mass-created batch
 * had no button at all once the dialog closed.
 */
export interface BackoutSourceDto {
    kind: 'Upload' | 'MassCreateBatch';
    id: string;
    label: string;
    appointmentCount: number;
    createdUtc: string | null;
}

export function listBackoutSources(
    token: string, facilityCode: string, date: string,
): Promise<{ sources: BackoutSourceDto[] }> {
    const params = new URLSearchParams({ facilityCode, date });
    return request(`/mgmt/appointments/backout-sources?${params}`, token);
}

// ---------------------------------------------------------------------------
// ⭐ #143 — LIVE LABOUR
// ---------------------------------------------------------------------------

export interface LabourSessionDto {
    labourSessionId: string;
    workerName: string | null;
    taskLabel: string | null;
    appointmentId: string | null;
    appointmentNumber: string | null;
    clientName: string | null;
    carrierName: string | null;
    doorName: string | null;
    direction: string | null;
    palletCount: number | null;
    startedUtc: string;
    finishedUtc: string | null;
    closeReason: string | null;
    /**
     * ⚠️ Derived from FinishedUtc on the server — never stored (arch §1E).
     * ⛔ AND IT MEANS LESS THAN IT SAYS: "the assignment is OPEN", which since
     * migration 201 is not the same as "somebody is working it". Use
     * `assignmentState`.
     */
    state: 'working' | 'done';
/**
 * ---- ⛔⛔⛤⛤ THIS FILE IS A HAND-MAINTAINED MIRROR, AND IT LAGGED TEN DAYS ----
 *
 * The server has returned `assignmentState`, `breakKind`, `workedSeconds` and
 * `activeSinceUtc` since 2026-09-14 (decision 94). These interfaces never
 * declared them, so no component could read them, so the Labor rail drew
 * "In progress" with a spinning truck over a task nobody was touching — for ten
 * days, while `labourRepository.ts` carried a comment saying it would.
 *   ** A HAND-MAINTAINED TYPE MIRROR IS A SECOND SOURCE OF TRUTH, AND THE HALF
 *      THAT LAGS IS INVISIBLE: THE COMPILER REPORTS THE MISSING FIELD EXACTLY AS
 *      THOUGH THE SERVER HAD NEVER SENT IT. **
 * ⬜ A guard comparing these two files against `src/lib/schemas.ts` and the
 *    repository DTOs is an open item.
 */
    /**
     * ⭐⭐ THE HONEST STATE — `dbo.vw_LaborAssignmentAll.State`.
     * ⛔ Active means an OPEN WORK INTERVAL exists: a person on this truck, now.
     * OnBreak and Paused are both "assigned, not working", and they are different
     * sentences to a supervisor — one man is back in ten minutes, the other has
     * moved to another truck.
     */
    assignmentState: 'Active' | 'OnBreak' | 'Paused' | 'Finished';
    /** ⭐ 'Break' or 'Meal' when `assignmentState` is OnBreak; null otherwise. */
    breakKind: string | null;
    /**
     * ⭐ WORK intervals only, so a break does not inflate it. ⚠️ NOT the elapsed
     * time the row used to show — that was wall-clock since the assignment opened
     * and kept counting straight through a pause.
     */
    workedSeconds: number | null;
    /** ⭐ When the CURRENT work interval began — null unless Active. */
    activeSinceUtc: string | null;
    /**
     * ⭐ WHEN THE CLOCK LAST STOPPED — migration 217.
     * ⛔ RAW: an Active assignment has one too (the end of the interval before the
     * running one). Pair it with `assignmentState` via `pausedSince()` in
     * `@/lib/labourState`, never by hand.
     */
    lastIntervalEndedUtc: string | null;
    /**
     * ⭐ F131 — the latest correction a scheduler made to this record, or null.
     * ⛔ The fields above stay AS RECORDED; the panel shows these values with a
     * "corrected" mark. ⚠️ Optional so an older backend still renders.
     */
    correction?: LabourCorrectionDto | null;
}

/** ⭐ F131 — `dbo.vw_LaborSessionLatestCorrection`, as the labour read returns it. */
export interface LabourCorrectionDto {
    correctionId: string;
    workerName: string;
    taskLabel: string;
    startedUtc: string;
    finishedUtc: string | null;
    palletsAtFinish: number | null;
    reason: string;
    correctedBy: string;
    correctedUtc: string;
    closedOpenSession: boolean;
    correctionCount: number;
}

export interface LabourDayDto {
    sessions: LabourSessionDto[];
    working: number;
    done: number;
    /**
     * ⭐ Open sessions at OTHER facilities right now — counted only when this
     * facility has none (2026-08-27). Pete: *"labor is not showing for a task
     * in-progress."* An empty panel had three indistinguishable causes; this
     * separates "a quiet floor" from "the facility codes do not match".
     * ⚠️ Optional so an older payload still renders.
     */
    elsewhere?: number;
    /** ⛔ Non-null when the day could not be resolved at all — never a silent zero. */
    unavailable?: string | null;
    /** ⭐ F131 — non-null when the corrections could not be read; the list still renders. */
    correctionsUnavailable?: string | null;
}

/**
 * GET /api/mgmt/labour — who is on which truck, for one facility-local day.
 *
 * ⛔ READ-ONLY BY DESIGN. Pipeline owns labour; there is no Dock write for it, and if
 * one is ever needed it belongs in Pipeline with this calling it.
 */
export function getLabourForDay(
    token: string, facilityCode: string, date: string,
    /**
     * ---- ⭐⭐ #68 ONE APPOINTMENT MEANS ALL OF ITS LABOUR (2026-09-22) --------
     *
     * > Pete, 2026-09-21: *"When singling out an appointment, it should always show
     * > all the labor, communications, paperwork, history on that appointment."*
     *
     * ⛔ THE SERVER REPLACES THE DAY BOUND, IT DOES NOT WIDEN IT. A session that
     * started on Tuesday and finished on Wednesday belongs to the load, not to
     * either day — and the day filter is exactly what hid it.
     * ⚠️ `date` IS STILL REQUIRED. It is what the route uses when no appointment is
     * named, and making it optional would let a caller ask for neither.
     */
    appointmentId?: string | null,
): Promise<LabourDayDto> {
    const params = new URLSearchParams({ facilityCode, date });
    if (appointmentId) params.set('appointmentId', appointmentId);
    return request<LabourDayDto>(`/mgmt/labour?${params}`, token);
}

// ---------------------------------------------------------------------------
// ⭐ F131 — LABOUR CORRECTIONS (Decisions 309 / 310)
// ---------------------------------------------------------------------------
//
// ⛔ DOCK STILL WRITES NO LABOUR. These go to Dock's `mgmt/labour/{id}/correction`,
// which asks Pipeline to record the correction. The company and the person on the
// record come from the sign-in on the server; nothing here names either.

export interface LabourCorrectionRowDto {
    laborSessionCorrectionId: number;
    supersedesCorrectionId: number | null;
    pinUserId: number;
    workerName: string;
    taskTypeId: number;
    taskCode: string;
    taskName: string;
    startedUtc: string;
    finishedUtc: string | null;
    palletsAtFinish: number | null;
    reason: string;
    correctedBy: string;
    correctedUtc: string;
    closedOpenSession: boolean;
}

export interface LabourCorrectionViewDto {
    session: {
        laborSessionId: number;
        facilityCode: string;
        pinUserId: number;
        taskTypeId: number;
        taskCode: string;
        startedUtc: string;
        finishedUtc: string | null;
        /** The shift clock — not correctable here. */
        isShift: boolean;
    };
    latest: LabourCorrectionRowDto | null;
    /** Newest first. */
    history: LabourCorrectionRowDto[];
    workers: { pinUserId: number; name: string; active: boolean }[];
    tasks: { taskTypeId: number; code: string; name: string }[];
    limits: { reasonMax: number; windowDays: number; futureSlackMinutes: number; palletsMax: number };
}

export interface LabourCorrectionInput {
    facilityCode: string;
    /** ⭐ One per opening of the screen, so a retried save cannot write twice. */
    clientEventId: string;
    reason: string;
    pinUserId: number;
    taskTypeId: number;
    startedUtc: string;
    /** Null leaves a running task running; a time closes it out. */
    finishedUtc: string | null;
    palletsAtFinish: number | null;
}

export function getLabourCorrection(
    token: string, facilityCode: string, labourSessionId: string,
): Promise<LabourCorrectionViewDto> {
    const params = new URLSearchParams({ facilityCode });
    return request<LabourCorrectionViewDto>(
        `/mgmt/labour/${encodeURIComponent(labourSessionId)}/correction?${params}`, token);
}

export function postLabourCorrection(
    token: string, labourSessionId: string, input: LabourCorrectionInput,
): Promise<{ correction: LabourCorrectionRowDto; closedOut: boolean; replayed: boolean }> {
    return request(`/mgmt/labour/${encodeURIComponent(labourSessionId)}/correction`, token, {
        method: 'POST',
        body: JSON.stringify(input),
    });
}

/* ────────────────────────────────────────────────────────────────────────────
 * #263 — the Communications slider's feed.
 * ────────────────────────────────────────────────────────────────────────── */

export interface CommunicationRowDto {
    notificationSentId: number;
    /** The engine's type code, e.g. DOCK_APPT_DOOR_DRIVER. Not a label. */
    typeCode: string;
    channel: string;
    outcome: string;
    attemptCount: number;
    createdUtc: string;
    subject: string | null;
    body: string | null;
    errorDetail: string | null;
    appointmentId: string | null;
    /**
     * ⭐⭐ THE ARRIVAL, WHEN THE MESSAGE NAMED ONE (2026-09-10). This is what lets
     * the rail scope Communications to a driver EXACTLY, instead of matching the
     * last four digits of his phone inside the masked recipient string.
     * ⚠️ Null on most rows — only the ad-hoc check-in text writes a 'checkin:' key.
     * ⚠️ Optional so an older payload still renders.
     */
    checkInId?: string | null;
    appointmentNumber: string | null;
    facilityCode: string | null;
    /**
     * ⛔ ALREADY MASKED WHEN IT ARRIVES — "Tony ····4417". The server does this
     * before composing the response, so a full number never reaches the browser.
     * ⚠️ Do not "improve" this by asking for the raw value to format it here.
     */
    recipients: string[];
    /**
     * ⭐ The load's own numbers (2026-08-27) — so the panel's filter matches what a
     * scheduler is holding, not only what we generated. Null when the message named
     * no appointment.
     */
    poNumber?: string | null;
    referenceNumber?: string | null;
    containerNumber?: string | null;
}

export interface CommunicationsPageDto {
    rows: CommunicationRowDto[];
    windowTotal: number;
    /** ⚠️ In the window but belonging to another facility. Shown, never hidden. */
    unattributed: number;
    capped: boolean;
    /** Non-null when the feed itself could not be read. */
    unavailable: string | null;
    hours: number;
    /**
     * ⭐ #68 — THE APPOINTMENT THE SERVER ACTUALLY SCOPED TO, echoed back.
     *
     * ⛔ NOT "what the screen asked for". A malformed id is treated as absent by
     * the route, so a panel that assumed its own request succeeded would label an
     * unscoped feed as scoped.
     *   ** A SCOPE SENTENCE BUILT FROM WHAT WAS ASKED, RATHER THAN WHAT HAPPENED,
     *      IS A GUESS WITH GOOD POSTURE. **
     * ⚠️ Optional, so an older server that does not send it does not break the
     * screen — the field is simply absent and the panel falls back to unscoped.
     */
    scopedTo?: string | null;
}

/**
 * GET /api/mgmt/communications — what went out, and whether it arrived.
 *
 * ⛔ READ-ONLY AND SCHEDULER-GATED. There is no resend here on purpose: every
 * message goes through Pipeline's engine, and a second path onto a driver's phone
 * would be a second path to get consent, dedup and the audit trail wrong.
 *
 * ⛔⛔ `facilityCode` IS REQUIRED (2026-08-27). It used to be optional, with absent
 * meaning EVERY facility — which let one site read another site's drivers and carrier
 * contacts (§1K). ⚠️ It came from misreading Pete's *"to just view all comms"*: he
 * meant every KIND of message, not every SITE.
 *
 * ⭐ The route now answers 400 without it. This signature makes it a COMPILE ERROR so
 * no caller can rediscover the hole.
 */
/**
 * ⛔⛔ FLATTEN A PAPERWORK DOSSIER INTO ONE LIST, WITHOUT DOUBLING IT.
 *
 * Pete, 2026-08-27: *"When you add a second document to the driver card, instead of
 * 2 there was four! Then deleted two of them and it delete all four."*
 *
 * ⚠️ `documents` IS "AppointmentId IS SET" AND `arrivals[].documents` IS "CheckInId
 * IS SET" — AND THOSE TWO SETS OVERLAP. A document dropped onto the card of a driver
 * who is already matched to a load is filed with BOTH ids, deliberately: it belongs
 * to this visit AND to this load. The server's `PaperworkCount` already knows, which
 * is why it counts with an `OR` rather than adding the two.
 *
 * ⛔ THREE SEPARATE SCREENS SPREAD THE TWO LISTS AND NONE OF THEM DEDUPED — the
 * documents dialog, the paperwork list and the calendar's shortcut. ⭐ One helper, so
 * the next screen that needs a flat pile cannot reintroduce it.
 *
 * ⚠️ TWO TILES FOR ONE FILE IS WORSE THAN A MISCOUNT. A scheduler checking paperwork
 * against a carrier's manifest gets a number that is quietly double, and filenames
 * ending -1 and -2 make the duplication look deliberate.
 *
 * ⛔ FIRST OCCURRENCE WINS and the appointment list is first on purpose: it carries
 * the load context these screens are titled with. The arrival copy is the same row
 * read through the other key, so nothing is lost by dropping it.
 */
export function flattenDossierDocuments(
    dossier: { documents: DocumentDto[]; arrivals: { documents: DocumentDto[] }[] },
): DocumentDto[] {
    const seen = new Set<string>();
    return [...dossier.documents, ...dossier.arrivals.flatMap(a => a.documents)]
        .filter(d => {
            const id = String(d.documentId);
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
}

export function getCommunications(
    token: string,
    /**
     * ⭐⭐ #68 — `appointmentId` SCOPES THE READ ON THE SERVER (2026-09-22).
     *
     * ⛔⛤ THE PANEL USED TO FILTER WHAT IT RECEIVED, and what it received was a
     * rolling `hours` window for the whole facility. A message older than that
     * window was not hidden by the screen — it was never sent.
     *   ** A PANEL THAT FILTERS WHAT IT RECEIVED CANNOT SHOW WHAT IT WAS NOT SENT,
     *      AND FROM THE OUTSIDE THE TWO LOOK IDENTICAL. **
     *
     * ⚠️ `hours` IS STILL SENT AND STILL MEANS SOMETHING when scoped: the server
     * bounds the scoped read to ±90 days around the appointment rather than
     * scanning an unindexed column, and says so in `scopedTo`.
     */
    opts: {
        facilityCode: string; hours?: number; take?: number; appointmentId?: string | null;
        /** ⭐ DECISION 215 — all three or none; the route treats a partial triple as
         *  absent rather than refusing it. */
        partyKind?: PartyHistoryKind | null; partyCode?: string | null; date?: string | null;
    },
): Promise<CommunicationsPageDto> {
    const params = new URLSearchParams();
    params.set('facilityCode', opts.facilityCode);
    if (opts.hours) params.set('hours', String(opts.hours));
    if (opts.take) params.set('take', String(opts.take));
    if (opts.appointmentId) params.set('appointmentId', opts.appointmentId);
    if (opts.partyKind && opts.partyCode && opts.date) {
        params.set('partyKind', opts.partyKind);
        params.set('partyCode', opts.partyCode);
        params.set('date', opts.date);
    }
    const qs = params.toString();
    return request<CommunicationsPageDto>(`/mgmt/communications${qs ? `?${qs}` : ''}`, token);
}

// ---------------------------------------------------------------------------
// The departure checklist (#317)
// ---------------------------------------------------------------------------

/**
 * ⛔ THE VOCABULARY COMES FROM THE SERVER, NOT FROM A COPY HERE.
 *
 * ⚠️ web/ and src/ are separate builds, so this file cannot import
 * `REQUIREMENT_KINDS`. A hand-typed union would go stale the day a fifth kind is
 * added — the screen would offer four and the CHECK constraint would reject a row
 * nobody could create. The type is a widened string, and `GET
 * mgmt/departure-checklists` returns the list the screen renders from.
 */
export type ChecklistKind = string;

export interface ChecklistRequirementDto {
    requirementId: string | null;
    kind: ChecklistKind;
    label: string;
    documentType: string | null;
    /** ⚠️ A print instruction. It never affects whether the requirement is met. */
    copies: number;
    isBlocking: boolean;
    sortOrder: number;
    isActive?: boolean;
}

export interface ChecklistDto {
    checklistId: string;
    name: string;
    direction: 'INBOUND' | 'OUTBOUND';
    companyCode: string | null;
    facilityCode: string | null;
    clientCode: string | null;
    carrierCode: string | null;
    priority: number;
    notes: string | null;
    isActive: boolean;
    requirements: ChecklistRequirementDto[];
}

export interface ChecklistKindDto {
    kind: ChecklistKind;
    label: string;
    help: string;
    /** ⛔ False means the kind is configurable but does nothing yet — say so. */
    wired: boolean;
}

export interface ChecklistAdminPageDto {
    lists: ChecklistDto[];
    kinds: ChecklistKindDto[];
    maxCopies: number;
    maxLabel: number;
    maxRequirements: number;
}

export type RequirementState = 'met' | 'missing' | 'awaitingDriver' | 'notWired';

export interface RequirementStatusDto {
    requirement: ChecklistRequirementDto & { requirementId: string };
    state: RequirementState;
    message: string;
    blocks: boolean;
}

export interface AppointmentChecklistDto {
    appointmentId: string;
    direction: 'INBOUND' | 'OUTBOUND';
    resolution: {
        checklist: ChecklistDto | null;
        scope: string;
        /** ⭐ Lists that also matched and were NOT used — so the rule is traceable. */
        alsoMatched: Array<{ checklistId: string; name: string; scope: string }>;
    };
    assessment: {
        items: RequirementStatusDto[];
        blocking: RequirementStatusDto[];
        outstanding: RequirementStatusDto[];
        hasBlockingGap: boolean;
    };
    stepTicks: Record<string, { completedUtc: string | null; completedBy: string | null }>;
    /**
     * ⭐ #317 STEP 5 — the library documents that apply to this load.
     *
     * ⛔ A SEPARATE LIST FROM THE REQUIREMENTS, on purpose. Either can exist
     * without the other: a `standing` requirement with no matching handout is an
     * admin asking for something the library cannot supply, and a handout with no
     * requirement is a client-wide sheet somebody may still want to hand over.
     * The panel joins them for display; merging them in the data would hide both
     * cases.
     *
     * ⚠️ Optional so an older payload still renders.
     */
    handouts?: HandoutForLoadDto[];
    /** ⛔ The library could not be READ — not the same as "there are none". */
    handoutsUnavailable?: boolean;
    /**
     * ⭐ WHAT TO PRINT, IN ORDER, WITH HOW MANY OF EACH.
     *
     * ⛔ THE SCREEN OPENS THESE ONE AT A TIME AND MUST NOT LOOP. Every popup
     * blocker allows the first `window.open` after a user gesture and silently
     * eats the rest — so a "print all" loop would open ONE document and look like
     * it worked. On a feature whose whole point is the driver leaving with the
     * right paper, silently printing one of four is the worst failure available.
     *
     * ⚠️ Optional so an older payload still renders.
     */
    printList?: PrintItemDto[];
}

export interface PrintItemDto {
    documentId: string;
    fileName: string;
    /** ⚠️ A print instruction. It has never meant "this many must be on the load". */
    copies: number;
    /** Why it is on the list — the requirement's label, or the handout's name. */
    reason: string;
}

export function getDepartureChecklists(token: string): Promise<ChecklistAdminPageDto> {
    return request('/mgmt/departure-checklists', token);
}

export function saveDepartureChecklist(
    token: string, list: Omit<ChecklistDto, 'checklistId'> & { checklistId: string | null },
): Promise<{ checklistId: string }> {
    return request('/mgmt/departure-checklists', token, {
        method: 'PUT',
        body: JSON.stringify(list),
    });
}

export function deleteDepartureChecklist(
    token: string, checklistId: string,
): Promise<{ deleted: boolean }> {
    return request(
        `/mgmt/departure-checklists?checklistId=${encodeURIComponent(checklistId)}`,
        token, { method: 'DELETE' });
}

/**
 * ⭐ DERIVED ON EVERY CALL, NEVER CACHED. A document voided a minute ago has to
 * change this answer — a checklist that remembered a green tick from before the
 * void would be reporting what somebody clicked rather than what is on the truck.
 *
 * ⚠️ `checklist: null` IS A REAL, NON-ERROR ANSWER: nothing configured for this
 * load, or the appointment is a BLOCK rather than a truck.
 */
export function getAppointmentChecklist(
    token: string, appointmentId: string,
): Promise<AppointmentChecklistDto & { reason?: string }> {
    return request(`/mgmt/appointments/${appointmentId}/checklist`, token);
}

/**
 * Tick or untick ONE step.
 *
 * ⛔ ONLY A STEP CAN BE TICKED, and the server refuses anything else with a 400 in
 * plain words. A document requirement is answered by the documents on the load;
 * there is nothing here to tick, and a checkbox that pretended otherwise would be
 * the stored second truth this feature is built to avoid.
 */
export function setChecklistStepTick(
    token: string, appointmentId: string, itemId: string, done: boolean,
): Promise<AppointmentChecklistDto> {
    return request(`/mgmt/appointments/${appointmentId}/checklist/${itemId}`, token, {
        method: 'PUT',
        body: JSON.stringify({ done }),
    });
}

// ---------------------------------------------------------------------------
// Admin status override (#306)
// ---------------------------------------------------------------------------

/**
 * ⛔ THE VOCABULARY AND THE CONSEQUENCES BOTH COME FROM THE SERVER.
 *
 * ⚠️ `planOverride` lives in src/ and this screen lives in web/ — separate
 * builds. A second copy of the consequence rules here would drift the first time
 * one of them changed, and the drift would be invisible: the dialog would explain
 * one thing while the server did another.
 */
export type OverrideStatusCode =
    'REQUESTED' | 'APPROVED' | 'ARRIVED' | 'DEPARTED' | 'NOSHOW' | 'CANCELLED';

export interface OverrideConsequenceDto {
    /** ⭐ Style on this, never by parsing the text. */
    code: string;
    level: 'warn' | 'info';
    text: string;
}

export interface OverridePlanDto {
    allowed: boolean;
    /** Set only when `allowed` is false. One sentence, written for the admin. */
    refusal?: string;
    consequences: OverrideConsequenceDto[];
    clearsArrival: boolean;
    clearsDeparture: boolean;
}

export interface OverrideSubjectDto {
    currentStatus: OverrideStatusCode;
    actualArrivalUtc: string | null;
    actualDepartureUtc: string | null;
    checkIns: number;
    dockDoorCode: string | null;
}

export interface OverridePreviewDto {
    appointmentNumber: string | null;
    subject: OverrideSubjectDto;
    plan: OverridePlanDto;
}

export interface OverrideRequestBody {
    toStatus: OverrideStatusCode;
    reason: string;
    clearArrival: boolean;
    clearDeparture: boolean;
}

/**
 * What would happen — computed from the ROW, which the browser does not have.
 *
 * ⛔ A POST DESPITE CHANGING NOTHING. Its body carries the reason an admin is
 * part-way through typing, and a GET would put that in a query string and from
 * there into every access log the request passes through.
 */
export function previewStatusOverride(
    token: string, appointmentId: string, body: OverrideRequestBody,
): Promise<OverridePreviewDto> {
    return request(`/mgmt/appointments/${appointmentId}/status-override/preview`, token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/**
 * Do it.
 *
 * ⛔ `expectedCurrentStatus` IS THE STATUS THE ADMIN WAS LOOKING AT, echoed back
 * from the preview. If the row moved in between — a scheduler marked it arrived,
 * a kiosk departed it — the server returns 409 `StatusOverrideStale` rather than
 * applying a plan nobody agreed to.
 */
export function applyStatusOverride(
    token: string, appointmentId: string,
    body: OverrideRequestBody & { expectedCurrentStatus: string },
): Promise<{ from: string; to: string; plan: OverridePlanDto }> {
    return request(`/mgmt/appointments/${appointmentId}/status-override`, token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// ---------------------------------------------------------------------------
// The handout library (#317 step 5)
// ---------------------------------------------------------------------------

export interface HandoutRevisionDto {
    handoutRevisionId: string;
    revision: number;
    fileName: string;
    contentType: string;
    bytes: number;
    changeNote: string | null;
    uploadedUtc: string | null;
    uploadedBy: string | null;
}

export interface HandoutDto {
    handoutId: string;
    name: string;
    /** ⛔ The join to a `standing` checklist requirement. Never blank. */
    documentType: string;
    companyCode: string | null;
    facilityCode: string | null;
    clientCode: string | null;
    /** ⭐ Where the load is GOING. Directions belong to the destination. */
    consigneeCode: string | null;
    copies: number;
    priority: number;
    notes: string | null;
    isActive: boolean;
    /** Null while a handout has no file uploaded — it cannot be filed. */
    currentRevision: HandoutRevisionDto | null;
    revisionCount: number;
}

export interface HandoutForLoadDto {
    handout: HandoutDto;
    revision: HandoutRevisionDto | null;
    /** ⭐ Which revisions of this handout are already on the load. */
    filedRevisions: number[];
}

export function getHandouts(token: string): Promise<{ handouts: HandoutDto[] }> {
    return request('/mgmt/handouts', token);
}

export function saveHandout(
    token: string, handout: Omit<HandoutDto, 'handoutId' | 'currentRevision' | 'revisionCount'>
        & { handoutId: string | null },
): Promise<{ handoutId: string }> {
    return request('/mgmt/handouts', token, { method: 'PUT', body: JSON.stringify(handout) });
}

/**
 * ⛔ ADDS A REVISION — it never replaces one.
 *
 * ⚠️ Editing a handout in place would make every historical load's copy
 * unexplainable: it would no longer match anything in the library and nobody
 * could say why. Revision 1 stays exactly where it is.
 */
export async function uploadHandoutRevision(
    token: string, handoutId: string, file: File, changeNote: string | null,
): Promise<{ revision: number }> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (changeNote) form.append('changeNote', changeNote);
    return request(`/mgmt/handouts/${handoutId}/revision`, token, { method: 'POST', body: form });
}

export function deleteHandout(token: string, handoutId: string): Promise<{ deleted: boolean }> {
    return request(`/mgmt/handouts?handoutId=${encodeURIComponent(handoutId)}`, token,
        { method: 'DELETE' });
}

/**
 * Put the CURRENT revision on this load.
 *
 * ⛔ THE SERVER CHOOSES THE REVISION, not this call. A screen that could name one
 * could file a superseded version — "we handed him the old directions" is the
 * exact failure the library exists to prevent.
 *
 * ⭐ `alreadyFiled` IS A SUCCESS, NOT AN ERROR. The scheduler wanted it on the
 * load and it is on the load; a double-click should not look like a failure.
 */
export function fileHandoutOnLoad(
    token: string, appointmentId: string, handoutId: string, facilityCode: string,
): Promise<{ documentId?: string; revision: number; alreadyFiled?: boolean }> {
    return request(
        `/mgmt/appointments/${appointmentId}/handouts/${handoutId}`
        + `?facilityCode=${encodeURIComponent(facilityCode)}`,
        token, { method: 'POST' });
}

// ────────────────────────────────────────────────────────────────────────────
// The consignee address book and the delivery pin (#324, migration 171)
// ────────────────────────────────────────────────────────────────────────────

export interface ConsigneeDtoFull {
    consigneeId: number;
    consigneeCode: string;
    consigneeName: string;
    address: string | null;
    arrivalInstructions: string | null;
    mapLinkOverride: string | null;
    latitude: number | null;
    longitude: number | null;
    notes: string | null;
    isActive: boolean;
    createdUtc: string | null;
    createdBy: string | null;
    modifiedUtc: string | null;
    modifiedBy: string | null;
}

/**
 * ⛔ A DISCRIMINATED RESULT, NOT A NULLABLE MESSAGE.
 *
 * `canSend: false` always carries a `reason` naming the fix — "LOWES-7741 is not
 * in the address book, add it in Settings → Consignees". ⚠️ A generic "no pin
 * available" is a sentence nobody can act on, and the four ways this can be
 * blocked have four different five-minute fixes.
 */
export type ConsigneePinPreview =
    | { canSend: false; reason: string }
    | {
        canSend: true;
        consigneeCode: string;
        consigneeName: string;
        subject: string;
        sms: string;
        /** ⚠️ SHOWN, NEVER ENFORCED — see the note on the send dialog. */
        segments: number;
        unicode: boolean;
        smsEnabled: boolean;
    };

/**
 * ⭐ #328 — the consignee codes that actually appear on appointments.
 *
 * ⛔ THE CODE IS AN EXACT JOIN — no trim, no case fold. A code retyped by hand
 * saves cleanly, looks right, and matches nothing. Offering the real ones makes
 * the common case a click and makes a typo visibly a NEW code rather than an
 * invisible near-miss of an existing one.
 *
 * ⚠️ `inAddressBook` is on every row rather than the list being filtered, so
 * editing an existing consignee does not find its own code missing.
 */
export interface KnownConsigneeCodeDto {
    consigneeCode: string;
    /** A snapshot from the most recent load — a default to save typing, not truth. */
    lastSeenName: string | null;
    loadCount: number;
    lastSeenUtc: string | null;
    inAddressBook: boolean;
}

export function getKnownConsigneeCodes(token: string): Promise<{ codes: KnownConsigneeCodeDto[] }> {
    return request('/mgmt/consignees/known-codes', token);
}

export function getConsignees(token: string): Promise<{ consignees: ConsigneeDtoFull[] }> {
    return request('/mgmt/consignees', token);
}

export function saveConsignee(
    token: string,
    consignee: Omit<ConsigneeDtoFull, 'consigneeId' | 'createdUtc' | 'createdBy' | 'modifiedUtc' | 'modifiedBy'>
        & { consigneeId: number | null },
): Promise<{ consignee: ConsigneeDtoFull }> {
    return request('/mgmt/consignees', token, { method: 'PUT', body: JSON.stringify(consignee) });
}

/**
 * ⛔ DEACTIVATE, NOT DELETE. Nothing holds a foreign key to dock.Consignee — the
 * join is by CODE from appointments that may be years old — so a delete would
 * succeed silently and take the answer to "where did that load go?" with it.
 */
export function deactivateConsignee(token: string, consigneeId: number): Promise<{ deactivated: boolean }> {
    return request(`/mgmt/consignees/${consigneeId}/deactivate`, token, { method: 'POST' });
}

/**
 * ⭐ THE PREVIEW AND THE SEND RESOLVE THE MESSAGE THE SAME WAY, server-side. What
 * the scheduler was shown is what goes out.
 */
export function previewConsigneePin(token: string, appointmentId: string): Promise<ConsigneePinPreview> {
    return request(`/mgmt/appointments/${appointmentId}/consignee-pin`, token);
}

export function sendConsigneePin(
    token: string, appointmentId: string,
    body: { channel: 'email' | 'sms' | 'both'; emails?: string[]; phones?: string[] },
): Promise<{ sent: number; results: unknown[]; smsEnabled: boolean; anyFailed: boolean; note?: string }> {
    return request(`/mgmt/appointments/${appointmentId}/consignee-pin`, token,
        { method: 'POST', body: JSON.stringify(body) });
}

// ---------------------------------------------------------------------------
// RIGHT-SIDE TOOLBAR (dock.RailPanel + dock.FacilityRailPanel, migration 172)
// ---------------------------------------------------------------------------
//
// Pete, 2026-09-08: "Can you those cards (scanned papwerork, labor,
// communications and Driver Checkin) have an active/inactive flag in config
// screen?" ... and later, looking at the shipped rail: "Scanned Paperwork is
// still Visible."
//
// ⭐⭐ ABSENCE MEANS ON. The table holds only the exceptions, so a facility with
// no rows gets every card. The SERVER applies that rule (ISNULL(p.IsActive, 1));
// what arrives here is already the effective answer and must not be
// re-interpreted.
//
// ⛔ `panels: null` IS NOT "NO PANELS". It is the server saying it could not
// resolve the configuration and is serving the shipped default — see the
// `degraded` flag and the long note on getRailPanels in src/functions. A caller
// that treats null as an empty list renders an EMPTY RAIL, which is
// indistinguishable from a correctly configured one and is the exact failure
// this whole design is trying to avoid.

export interface RailPanelDto {
    panelCode: string;
    displayName: string;
    sortOrder: number;
    /** ⛔ False for CHECKIN — a site cannot switch off the panel the screen is for. */
    isOptional: boolean;
    isActive: boolean;
}

export interface RailPanelConfigDto extends RailPanelDto {
    companyId: number;
    companyCode: string;
    facilityCode: string;
    facilityName: string | null;
    /** True only when a row exists — "on because somebody chose it", not "on by default". */
    isExplicit: boolean;
}

/**
 * What should this facility's rail draw?
 *
 * ⚠️ NO COMPANY ID IS SENT. The server resolves it from the facility, because a
 * company id chosen by the caller is exactly what (CompanyId, FacilityCode) is
 * meant to prevent.
 */
export function getRailPanels(
    token: string, facilityCode: string,
): Promise<{ facilityCode: string; panels: RailPanelDto[] | null; degraded: boolean }> {
    return request(`/rail-panels?facility=${encodeURIComponent(facilityCode)}`, token);
}

export function getRailPanelConfig(token: string): Promise<{ rows: RailPanelConfigDto[] }> {
    return request('/mgmt/rail-panels', token);
}

/**
 * ⭐ RETURNS THE WHOLE GRID, not the row that changed — the screen renders what
 * the database says rather than what it hoped the write did.
 */
export function updateRailPanel(
    token: string,
    body: { companyId: number; facilityCode: string; panelCode: string; isActive: boolean },
): Promise<{ rows: RailPanelConfigDto[] }> {
    return request('/mgmt/rail-panels/update', token, {
        method: 'PATCH', body: JSON.stringify(body),
    });
}

/**
 * ---- ⭐⭐ WHERE A DOCUMENT GOES WHEN SOMEBODY PRESSES PRINT (§1AK) ---------
 *
 * ⛔ READ ONLY, AND THERE IS NO WRITE COMPANION. Pipeline owns this config
 * (§1A.3 — *"who WRITES this? → that app owns it"*) and migration 096 grants
 * Dock `SELECT` only, so a write would fail at the database. Changing a printer
 * is an ACTION and goes through Pipeline's API.
 */
export type PrintKind = 'DIALOG' | 'ZEBRA' | 'SERVER';

export interface PrintDestinationDto {
    destinationCode: string;
    displayName: string;
    kind: PrintKind;
    /** ⭐ ZEBRA only — the device name Browser Print reports. */
    printerName: string | null;
    /** ⭐ SERVER only — a host on the SITE's network. */
    host: string | null;
    port: number | null;
    labelWidthIn: number | null;
    labelHeightIn: number | null;
    isDefault: boolean;
}

export interface PrintRouteDto {
    documentType: string;
    destinationCode: string;
    copies: number;
}

export interface PrintTemplateDto {
    templateCode: string;
    displayName: string;
    zpl: string;
    placeholderHelp: string | null;
}

export interface PrintConfigDto {
    companyId: number;
    facilityCode: string;
    destinations: PrintDestinationDto[];
    routes: PrintRouteDto[];
    templates: PrintTemplateDto[];
    /**
     * ⛔ `false` means the LOOKUP FAILED, not "nothing is configured".
     *
     * ⚠️ Empty lists with `configRead: true` are normal and mean *use the OS
     * dialog*. Empty lists with `configRead: false` mean we do not know what the
     * config says — migration 096 has not run, or its grant did not land. ⭐ The
     * screen must be able to say which, exactly as the WMS panel distinguishes
     * `shippingCodesRead`.
     */
    configRead: boolean;
}

export function getPrintConfig(token: string, facilityCode: string): Promise<PrintConfigDto> {
    const qs = new URLSearchParams({ facilityCode });
    return request<PrintConfigDto>(`mgmt/print-config?${qs.toString()}`, token);
}

export interface PrintContentDto {
    fileName: string;
    /** ⭐ The file's text, already checked server-side for `^XA` and `^XZ`. */
    zpl: string;
}

/**
 * The TEXT of a stored document, for sending to a label printer.
 *
 * ⛔ NOT `getDocumentUrl` AND NOT `fetchPreviewBlob`. The first returns a
 * storage SAS stamped `attachment`, whose CORS story for this origin nobody has
 * verified; the second goes through the render allow-list, which refuses a
 * `.zpl` and is right to — that list is about what may be drawn in an iframe.
 * ⭐ This is a third question (may these bytes go to a printer) with a third
 * answer, and the server does the reading so there is no CORS in it at all.
 */
export function getPrintContent(token: string, documentId: string): Promise<PrintContentDto> {
    return request<PrintContentDto>(
        `mgmt/documents/${encodeURIComponent(documentId)}/print-content`, token);
}

// ---- ⭐ A5 — attach a WMS order to an appointment (2026-09-28) --------------
/**
 * The order's full key. ⛔ All four parts: an order number alone is not unique,
 * and the link table and OrdersCache are both keyed on the four.
 */
export interface WmsOrderKeyDto {
    wmsSource: string;
    businessUnit: string;
    orderType: string;
    orderNumber: string;
}

/** One LINK row (never an identifier match - those cannot be removed). */
export interface WmsOrderLinkDto {
    appointmentId: number;
    appointmentNumber: string;
    facilityCode: string;
    dockDoorCode: string | null;
    scheduledStartUtc: string | null;
    schedulingMode: string | null;
    matchMethod: string;
    matchedBy: string | null;
    matchedUtc: string | null;
}

export interface WmsOrderLinksDto {
    order: WmsOrderKeyDto;
    links: WmsOrderLinkDto[];
    /** ⚠️ Links at facilities this scheduler may not see. Counted, never listed. */
    hiddenElsewhere: number;
}

export interface WmsOrderAttachDto {
    appointmentId: number;
    appointmentNumber: string;
    /** false when the link already existed - attaching twice is not an error. */
    attached: boolean;
    removed: { appointmentId: number; appointmentNumber: string }[];
}

/** GET mgmt/wms-orders/links — which appointments this order is attached to. */
export function getWmsOrderLinks(token: string, key: WmsOrderKeyDto): Promise<WmsOrderLinksDto> {
    const q = new URLSearchParams({
        wmsSource: key.wmsSource, businessUnit: key.businessUnit,
        orderType: key.orderType, orderNumber: key.orderNumber,
    });
    return request<WmsOrderLinksDto>(`/mgmt/wms-orders/links?${q.toString()}`, token);
}

/**
 * POST mgmt/appointments/{id}/wms-orders — attach, optionally removing the order
 * from other appointments in the same transaction.
 *
 * ⛔ `removeFrom` IS ONLY EVER WHAT A PERSON TICKED. The replace popup starts
 * with nothing ticked; an empty list adds and removes nothing.
 */
export function attachWmsOrder(
    token: string, appointmentId: number, key: WmsOrderKeyDto, removeFrom: number[],
): Promise<WmsOrderAttachDto> {
    return request<WmsOrderAttachDto>(`/mgmt/appointments/${encodeURIComponent(String(appointmentId))}/wms-orders`, token, {
        method: 'POST',
        body: JSON.stringify({ ...key, removeFrom }),
    });
}

// ---- ⭐ Decision 266: Possible no-shows -------------------------------------
// ⛔ Every call names the facility; the server bounds the list and both actions
// to that facility's company (§0.0) and re-checks the row before changing it.

export function listPossibleNoShows(token: string, facilityCode: string): Promise<PossibleNoShowListDto> {
    const params = new URLSearchParams({ facilityCode });
    return request<PossibleNoShowListDto>(`/mgmt/possible-no-shows?${params}`, token);
}

export function markPossibleNoShowNoShow(token: string, facilityCode: string, appointmentId: number): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`/mgmt/possible-no-shows/${appointmentId}/no-show`, token, {
        method: 'POST',
        body: JSON.stringify({ facilityCode }),
    });
}

/** ⭐ F106 — Possible no-departures (past-day trucks still marked Arrived). */
export function listPossibleNoDepartures(token: string, facilityCode: string): Promise<PossibleNoDepartureListDto> {
    const params = new URLSearchParams({ facilityCode });
    return request<PossibleNoDepartureListDto>(`/mgmt/possible-no-departures?${params}`, token);
}

/** A catch-up departure: when the truck actually left, entered after the fact. */
export function markPossibleNoDepartureDeparted(
    token: string, facilityCode: string, appointmentId: number, departureUtc: string,
): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`/mgmt/possible-no-departures/${appointmentId}/departed`, token, {
        method: 'POST',
        body: JSON.stringify({ facilityCode, departureUtc }),
    });
}

/** ⭐ F107 — past visits with no WMS order (the No WMS chip and its Match list). */
export function listNoWmsAppointments(
    token: string, facilityCode: string, range?: { from: string; to: string } | null,
): Promise<NoWmsListDto> {
    const params = new URLSearchParams({ facilityCode });
    /* ⭐ The board's days (Day = one date) — their loads with no order join the past visits. */
    if (range) { params.set('from', range.from); params.set('to', range.to); }
    return request<NoWmsListDto>(`/mgmt/no-wms-appointments?${params}`, token);
}

/** A catch-up arrival: the time the truck actually arrived, entered after the fact. */
export function markPossibleNoShowArrived(
    token: string, facilityCode: string, appointmentId: number, arrivalUtc: string,
): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`/mgmt/possible-no-shows/${appointmentId}/arrived`, token, {
        method: 'POST',
        body: JSON.stringify({ facilityCode, arrivalUtc }),
    });
}

// ---- ⭐ F183 — not conforming (Decisions 341, 355, migration 236) ------------

export type NonConformingReason = 'Spec' | 'Packaging' | 'Compliance' | 'Other';

export interface ConformingDto {
    appointmentId: number;
    direction: string;
    /** Null = conforming. */
    reason: NonConformingReason | null;
    note: string | null;
    by: string | null;
    atUtc: string | null;
    /** False until migration 236 has run: shown as conforming, and saving is refused. */
    migrated: boolean;
    /** The reasons and the note length, from the server (never typed into the screen). */
    reasons?: NonConformingReason[];
    noteMax?: number;
}

export function getAppointmentConforming(token: string, appointmentId: number): Promise<ConformingDto> {
    return request<ConformingDto>(`/mgmt/appointments/${appointmentId}/conforming`, token);
}

export function setAppointmentConforming(
    token: string, appointmentId: number, reason: NonConformingReason | null, note: string | null,
): Promise<ConformingDto> {
    return request<ConformingDto>(`/mgmt/appointments/${appointmentId}/conforming`, token, {
        method: 'PUT',
        body: JSON.stringify({ reason, note }),
    });
}

// ---- ⭐ F186 — the Reporting tab (Decisions 271-299) -------------------------
// The numbers are Pipeline's (one read API, Decision 272); the Dock route only
// says who is asking (company + facilities from the token).

export interface DockReportQuery {
    facilityCode?: string | null; startDate: string; endDate: string; dimension?: string;
    client?: string | null; carrier?: string | null; consignee?: string | null; measure?: string | null; limit?: number;
}
function dockReportQs(q: DockReportQuery): string {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== null && v !== undefined && v !== '') p.set(k, String(v));
    return p.toString();
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getDockReport(token: string, q: DockReportQuery): Promise<any> {
    return request(`/mgmt/reports/dock?${dockReportQs(q)}`, token);
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getDockReportAppointments(token: string, q: DockReportQuery): Promise<any> {
    return request(`/mgmt/reports/dock/appointments?${dockReportQs(q)}`, token);
}

// ---- ⭐ F187 — the dock-to-stock journey (item 104, Decisions 251, 358) --------
// Built in Pipeline (journey.ts), the same builder behind Pipeline's drill; the
// Dock route only says who is asking.

export type JourneyStepState = 'done' | 'now' | 'waiting' | 'missing' | 'late';
export interface JourneyStepDto {
    key: string; icon: string; label: string; atUtc: string | null; state: JourneyStepState;
    detail: string | null; sincePrevMin: number | null; extra: { label: string; atUtc: string } | null;
}
export interface JourneyTimelineDto {
    order: { WmsSource: string; BusinessUnit: string; OrderType: string; OrderNumber: string;
             currentStatus: string | null; isOwner: boolean | null; ownerAppointmentId: number | null } | null;
    steps: JourneyStepDto[];
    progress: { atUtc: string; pct: number | null; qty: number }[] | null;
    lines: { lineNumber: string; itemNumber: string | null; ordered: number | null; received: number;
             lastReceivedUtc: string | null; locations: string[];
             /** ⭐ F231: an outbound order's line (picked / shipped / short / WMS line status) */
             outbound?: { picked: number | null; shipped: number | null; short: number | null; status: string | null } }[] | null;
    verdict: { kpi: 'DTS' | 'OTS'; verdict: string; reason: string; deadlineUtc: string | null } | null;
    asOfUtc: string | null;
}
export interface JourneyDto {
    direction: 'INBOUND' | 'OUTBOUND'; facility: string | null; timezone: string | null;
    appointment: { appointmentId: number; status: string; scheduledStartUtc: string; carrierName: string | null; doorCode: string | null } | null;
    timelines: JourneyTimelineDto[]; notes: string[];
}
export function getAppointmentJourney(token: string, appointmentId: number): Promise<JourneyDto> {
    return request<JourneyDto>(`/mgmt/appointments/${appointmentId}/journey`, token);
}
